import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

type FilterKey = 'estado' | 'prioridad' | 'tipo' | 'agente' | 'grupo' | 'sla';
type SummaryKind = 'group' | 'agent';
type OperationsSort = 'load' | 'unresolved' | 'sla' | 'closed';
type OperationsKpiKey = 'total' | 'unresolved' | 'sla' | 'risk';
type SummaryState = 'open' | 'pending' | 'closed';
type SummarySla = 'within' | 'outside';
type SummaryInteraction = '1_interaction' | '2_interactions' | '3_interactions' | '4_plus';
type TrendMetric = 'received' | 'closed' | 'backlog';
type WeeklyComparisonMetric = 'received' | 'resolved' | 'unresolved';
type RiskTrendWindow = 'day' | 'weeks' | 'month' | '2m' | '3m';
type TopicWindow = 0 | 1 | 3 | 7 | 14 | 30;
type OperationsDetailPeriodWindow = '24h' | '3d' | '7d' | '14d' | 'last-month' | 'previous-month' | 'all';
type TicketSortKey = 'id' | 'estado' | 'prioridad' | 'tipo' | 'agente' | 'grupo' | 'interacciones' | 'sin_interaccion' | 'creado' | 'sla' | 'asunto';
type AppView = 'dashboard' | 'operations' | 'freshdesk' | 'schedule' | 'satisfaction' | 'documents' | 'metrics';
type ScheduleSubView = 'overview' | 'calendar' | 'planning';
type SatisfactionTrendMetric = 'promoters' | 'passives' | 'detractors' | 'average' | 'nps' | 'surveys';
type SatisfactionTrendWindow = 3 | 6 | 12 | 24;
type SatisfactionPeriodWindow = '24h' | '3d' | '7d' | '14d' | 'last-month' | 'previous-month' | 'all';

interface FilterDef {
  key: FilterKey;
  label: string;
}

interface TicketRow {
  ['ID del ticket']?: string;
  Estado?: string;
  Prioridad?: string;
  Tipo?: string;
  Agente?: string;
  Grupo?: string;
  Interacciones?: number;
  ['Sin interacción']?: string;
  ['Tiempo de creación']?: string;
  ['Antigüedad creación']?: string;
  ['Estado de la primera respuesta']?: string;
  Asunto?: string;
}

interface SummaryRow {
  label: string;
  total: number;
  unresolved: number;
  open: number;
  pending: number;
  closed: number;
  within: number;
  outside: number;
  avg_response_hours?: number | null;
  response_recent?: number;
  response_previous?: number;
  response_change?: number;
  response_change_pct?: number;
  response_direction?: 'up' | 'down' | 'flat';
  response_from?: string;
  response_to?: string;
  risk_low?: number;
  risk_medium?: number;
  risk_high?: number;
  closed_interactions?: Record<SummaryInteraction, number>;
  top_topics?: TopicRow[];
  resolved_topics?: TopicRow[];
  trend?: any[];
}

type RiskKey = 'low' | 'medium' | 'high';
type CreatedRiskKey = 'zero_one' | 'two' | 'three' | 'low' | 'medium' | 'high';

interface TopicRow {
  label: string;
  value: number;
  recent: number;
  previous: number;
  change: number;
  change_pct: number;
  direction: 'up' | 'down' | 'flat';
}

interface AreaTreemapItem {
  row: SummaryRow;
  x: number;
  y: number;
  w: number;
  h: number;
  share: number;
  color: string;
  colorSoft: string;
  compact: boolean;
  span: number;
}

interface MetricsPieSlice {
  key: string;
  label: string;
  value: number;
  pct: number;
  color: string;
  dash: string;
  offset: number;
}

interface MetricsAreaSlice {
  label: string;
  value: number;
  pct: number;
  barPct: number;
  color: string;
  resolved?: number;
  unresolved?: number;
  resolvedPct?: number;
  unresolvedPct?: number;
}

interface MetricsResolutionTrendPoint {
  key: string;
  label: string;
  total: number;
  sameDay: number;
  twoThreeDays: number;
  fourPlusDays: number;
  sameDayPct: number;
  twoThreeDaysPct: number;
  fourPlusDaysPct: number;
  withinThreeDays: number;
  withinThreeDaysPct: number;
}

interface MetricsSatisfactionTrendPoint {
  key: string;
  label: string;
  answered: number;
  detractors: number;
  detractorPct: number;
  average: number | null;
  nps: number | null;
  notePct: number;
  npsPct: number;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  readonly apiBase = (window as any).BI_API_BASE || 'http://127.0.0.1:8000';
  readonly filterDefs: FilterDef[] = [
    { key: 'estado', label: 'Estado' },
    { key: 'prioridad', label: 'Prioridad' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'agente', label: 'Área / cola' },
    { key: 'grupo', label: 'Responsable' },
    { key: 'sla', label: 'SLA 1ra respuesta' },
  ];
  readonly filterLabels: Record<FilterKey, string> = {
    estado: 'Estado',
    prioridad: 'Prioridad',
    tipo: 'Tipo',
    agente: 'Área / cola',
    grupo: 'Responsable',
    sla: 'SLA 1ra respuesta',
  };

  readonly selected: Record<FilterKey, string[]> = {
    estado: [],
    prioridad: [],
    tipo: [],
    agente: [],
    grupo: [],
    sla: [],
  };
  interactions: string[] = [];

  health: any = null;
  filtersMeta: any = null;
  summary: any = null;
  topicsSnapshot: TopicRow[] | null = null;
  recentTopicsSnapshot: TopicRow[] | null = null;
  resolvedTopicsSnapshot: TopicRow[] | null = null;
  trend: any = null;
  trendContext: any = null;
  riskTrend: any = null;
  weeklyComparison: any = null;
  periodCards: any = null;
  selectedMetricsMonthKey = '';
  selectedMetricsArea = '';
  private operationsBalanceBaseline: Record<SummaryKind, SummaryRow[]> = { group: [], agent: [] };
  tickets: { items: TicketRow[]; total: number; limit: number; offset: number } = {
    items: [],
    total: 0,
    limit: 15,
    offset: 0,
  };

  search = '';
  topicFilter = '';
  topicScope: '' | 'resolved' = '';
  activeSummaryKind: SummaryKind = 'group';
  operationsSort: OperationsSort = 'load';
  expandedSummaryRow = '';
  fromDate = '';
  toDate = '';
  dateMode: 'created' | 'resolved' | 'backlog' = 'created';
  trendSelectionMetric: TrendMetric = 'backlog';
  risk = '';
  createdRisk = '';
  unassignedOnly = false;
  ticketRisk: '' | 'medium' | 'high' = '';
  level = 'dia';
  topicWindowDays: TopicWindow = 0;
  readonly topicWindowOptions: Array<{ label: string; value: TopicWindow }> = [
    { label: 'Hoy', value: 1 },
    { label: '3 días', value: 3 },
    { label: '7 días', value: 7 },
    { label: '14 días', value: 14 },
    { label: 'Último mes', value: 30 },
    { label: 'Estado actual', value: 0 },
  ];
  readonly operationsDetailPeriodOptions: Array<{ label: string; value: OperationsDetailPeriodWindow }> = [
    { label: 'Hoy', value: '24h' },
    { label: '3 días', value: '3d' },
    { label: '7 días', value: '7d' },
    { label: '14 días', value: '14d' },
    { label: 'Último mes', value: 'last-month' },
    { label: 'Mes anterior', value: 'previous-month' },
    { label: 'Todos los periodos', value: 'all' },
  ];
  page = 0;
  pageSize = 15;
  ticketSortBy: TicketSortKey = 'creado';
  ticketSortDir: 'asc' | 'desc' = 'asc';
  theme = localStorage.getItem('bi-theme-v2') === 'dark' ? 'dark' : 'light';
  shellBackground = '#f5f6f8';
  loading = false;
  topicsLoading = false;
  areaFilterBusy = false;
  private dashboardRequestId = 0;
  private readonly dashboardCache = new Map<string, any>();
  filtersOpen = false;
  filtersClosing = false;
  private filtersCloseTimer: ReturnType<typeof setTimeout> | null = null;
  openFilter: FilterKey | null = null;
  trendHoverIndex: number | null = null;
  riskTrendSelectedIndex: number | null = null;
  riskTrendWindow: RiskTrendWindow = 'weeks';
  readonly riskTrendWindowOptions: Array<{ label: string; value: RiskTrendWindow }> = [
    { label: 'Día', value: 'day' },
    { label: 'Semanas', value: 'weeks' },
    { label: 'Mes', value: 'month' },
    { label: '2 meses', value: '2m' },
    { label: '3 meses', value: '3m' },
  ];
  satisfactionTrendHoverIndex: number | null = null;
  selectedSatisfactionTrendMetric: SatisfactionTrendMetric | '' = '';
  satisfactionTrendWindow: SatisfactionTrendWindow = 3;
  readonly satisfactionTrendWindows: Array<{ label: string; value: SatisfactionTrendWindow }> = [
    { label: 'Últimos 3 meses', value: 3 },
    { label: 'Últimos 6 meses', value: 6 },
    { label: 'Últimos 12 meses', value: 12 },
    { label: 'Últimos 2 años', value: 24 },
  ];
  private satisfactionTrendCacheKey = '';
  private satisfactionTrendCache: any = null;
  weeklyComparisonHoverIndex: number | null = null;
  weeklyComparisonMetric: WeeklyComparisonMetric = 'received';
  trendPanStart: number | null = null;
  private trendPanStartX: number | null = null;
  private trendPanOrigin: number | null = null;
  private trendPanFrame: number | null = null;
  private trendPanPendingStart: number | null = null;
  private trendDidDrag = false;
  private trendClickTimer: ReturnType<typeof setTimeout> | null = null;
  private topicsRequestId = 0;
  private readonly areaTreemapPalette = [
    ['#3f9395', '#eef8f6'],
    ['#56a2a9', '#f0f8f8'],
    ['#64aaa2', '#eff8f5'],
    ['#78b5ab', '#f1faf7'],
    ['#6ea8b8', '#f0f8fa'],
    ['#5b9788', '#eff7f4'],
    ['#84beb6', '#f2faf8'],
    ['#629e9a', '#eff8f7'],
  ];
  filterSearch: Record<FilterKey, string> = {
    estado: '',
    prioridad: '',
    tipo: '',
    agente: '',
    grupo: '',
    sla: '',
  };
  statusText = 'Conectando al backend...';
  subtitle = 'Cargando...';
  activeView: AppView = 'dashboard';
  viewMenuOpen = false;
  schedule: any = null;
  scheduleLoading = false;
  scheduleError = '';
  private scheduleAutoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private scheduleSignature = '';
  scheduleSubView: ScheduleSubView = 'overview';
  schedulePngExporting = false;
  selectedScheduleProcess = '';
  selectedScheduleMonth = '';
  selectedScheduleStatus: 'done' | 'pending' | '' = '';
  selectedScheduleActivity = '';
  selectedScheduleRemunerationMonthKey = '';
  selectedScheduleRemunerationProcess = '';
  selectedScheduleRemunerationArea = '';
  scheduleCalendarMode: 'calendar' | 'agenda' = 'calendar';
  selectedScheduleRemunerationEventKey = '';
  selectedScheduleRoadmapProcess = '';
  selectedScheduleRoadmapPoint: any = null;
  selectedScheduleRoadmapMonth = '';
  private readonly scheduleCalendarToneByProcess: Record<string, string> = {
    remuneraciones: 'tone-1',
    'gestion-documental-asistencia': 'tone-2',
    bienestar: 'tone-3',
    compensaciones: 'tone-4',
    'cumplimiento-legal': 'tone-5',
    'relaciones-laborales': 'tone-6',
    'talento-cultura': 'tone-7',
    anticipo: 'tone-1',
    sueldo: 'tone-2',
    reproceso: 'tone-3',
    pagos: 'tone-4',
    salidas: 'tone-5',
    'informacion-terceros': 'tone-6',
    'reproceso-2': 'tone-7',
  };
  private readonly scheduleCalendarProcessOrder = [
    'remuneraciones',
    'gestion-documental-asistencia',
    'bienestar',
    'compensaciones',
    'cumplimiento-legal',
    'relaciones-laborales',
    'talento-cultura',
    'anticipo',
    'sueldo',
    'reproceso',
    'reproceso-2',
    'pagos',
    'salidas',
    'informacion-terceros',
  ];
  selectedOperationsDetailRow: SummaryRow | null = null;
  documents: any = null;
  documentsLoading = false;
  activeRefreshLoading = false;
  documentsError = '';
  selectedDocument: any = null;
  docFilterEstado: string[] = [];
  docFilterTipo: string[] = [];
  docFilterGrupo: string[] = [];
  docFilterResponsable: string[] = [];
  docFilterPrioridad: string[] = [];
  docFilterFreshdeskEstado: string[] = [];
  docFilterBusqueda = '';
  docFilterAntiguedad: '' | '6' | '12' = '';
  selectedDocumentArea = '';
  selectedDuplicateArea = '';
  selectedDuplicateGroupKey = '';
  duplicateReviewSavingKey = '';
  selectedDocValidationFilter = 'all';
  docFilterFuente: 'todas' | 'oficial' | 'sugerencia' = 'todas';
  selectedDocKpi = '';
  visibleDocRows: any[] = [];
  satisfaction: any = null;
  satisfactionLoading = false;
  satisfactionError = '';
  satisfactionMonths = 13;
  satisfactionUpdatedAt = '';
  selectedSatisfactionWindow: SatisfactionPeriodWindow = 'last-month';
  selectedSatisfactionResponseSegment: '' | 'promoters' | 'passives' | 'detractors' = '';
  satisfactionResponsesPage = 0;
  readonly satisfactionResponsesPageSize = 15;
  readonly duplicateReviewOptions = [
    { value: 'vigente_unica', label: 'Única vigente', short: 'Única' },
    { value: 'duplicado_version', label: 'Duplicado / versión anterior', short: 'Duplicado' },
    { value: 'no_corresponde', label: 'No correspondía', short: 'No corr.' },
    { value: 'pendiente', label: 'Pendiente', short: 'Pend.' },
  ];

  // Cache versioning for satisfaction compute-intensive getters
  private satCacheKey = '';
  private satCachedResponseItemsKey = '';
  private satCachedResponseItems: any[] | null = null;
  private satCachedDateMetaKey = '';
  private satCachedDateMeta: { entries: Array<{ item: any; time: number }>; latestTime: number } | null = null;
  private satCachedWindowItemsKey = '';
  private satCachedWindowItems: any[] | null = null;
  private operationsRowSatisfactionCacheKey = '';
  private operationsRowSatisfactionCache = new Map<string, any | null>();
  private satCachedFilteredItems: any[] | null = null;
  private satCachedSummary: any = null;
  private satCachedAreas: any[] | null = null;
  private satCachedResponsables: any[] | null = null;
  private satCachedScoreDistribution: any[] | null = null;
  private satCachedPeriodOptions: any[] | null = null;
  private satCachedWindowPeriods: string[] | null = null;
  private satCachedWindowPeriodsKey = '';
  private satCachedRecentItems: any[] | null = null;
  private satCachedScoreOptions: number[] | null = null;
  private satCachedAreaOptions: any[] | null = null;
  private satCachedRespOptions: any[] | null = null;
  private satCachedYearOptions: string[] | null = null;
  private satCachedMonthOptions: Array<{ key: string; label: string }> | null = null;
  private satCachedQuestions: any[] | null = null;
  private satCachedAreaBars: any[] | null = null;
  private satCachedRespBars: any[] | null = null;
  private readonly apiRequestTimeoutMs = 30000;

  private satInvalidateCache(): void {
    this.satCachedResponseItemsKey = '';
    this.satCachedResponseItems = null;
    this.satCachedDateMetaKey = '';
    this.satCachedDateMeta = null;
    this.satCachedWindowItemsKey = '';
    this.satCachedWindowItems = null;
    this.operationsRowSatisfactionCacheKey = '';
    this.operationsRowSatisfactionCache.clear();
    this.satCachedFilteredItems = null;
    this.satCachedSummary = null;
    this.satCachedAreas = null;
    this.satCachedResponsables = null;
    this.satCachedScoreDistribution = null;
    this.satCachedPeriodOptions = null;
    this.satCachedWindowPeriods = null;
    this.satCachedWindowPeriodsKey = '';
    this.satCachedRecentItems = null;
    this.satCachedScoreOptions = null;
    this.satCachedAreaOptions = null;
    this.satCachedRespOptions = null;
    this.satCachedYearOptions = null;
    this.satCachedMonthOptions = null;
    this.satCachedQuestions = null;
    this.satCachedAreaBars = null;
    this.satCachedRespBars = null;
    this.satisfactionTrendCacheKey = '';
    this.satisfactionTrendCache = null;
  }

  private satInvalidateFilteredCache(): void {
    this.satCacheKey = '';
    this.satCachedResponseItemsKey = '';
    this.satCachedResponseItems = null;
    this.satCachedWindowItemsKey = '';
    this.satCachedWindowItems = null;
    this.operationsRowSatisfactionCacheKey = '';
    this.operationsRowSatisfactionCache.clear();
    this.satCachedFilteredItems = null;
    this.satCachedSummary = null;
    this.satCachedAreas = null;
    this.satCachedResponsables = null;
    this.satCachedScoreDistribution = null;
    this.satCachedRecentItems = null;
    this.satCachedQuestions = null;
    this.satCachedAreaBars = null;
    this.satCachedRespBars = null;
    this.satisfactionTrendCacheKey = '';
    this.satisfactionTrendCache = null;
  }

  private satCurrentKey(): string {
    return [
      this.satisfactionItems.length,
      this.selectedSatisfactionWindow,
      this.selectedSatisfactionArea,
      this.selectedSatisfactionResponsable,
      this.selectedSatisfactionPeriod,
      this.selectedSatisfactionYear,
      this.selectedSatisfactionMonth,
      this.selectedSatisfactionScore,
      this.selectedSatisfactionCategory,
    ].join('\x00');
  }

  private satEnsureCacheFresh(): void {
    const key = this.satCurrentKey();
    if (this.satCacheKey !== key) {
      this.satCacheKey = key;
      this.satInvalidateCache();
    }
  }

  readonly satisfactionWindowOptions: Array<{ label: string; value: SatisfactionPeriodWindow }> = [
    { label: 'Hoy', value: '24h' },
    { label: '3 días', value: '3d' },
    { label: '7 días', value: '7d' },
    { label: '14 días', value: '14d' },
    { label: 'Último mes', value: 'last-month' },
    { label: 'Mes anterior', value: 'previous-month' },
    { label: 'Todos los periodos', value: 'all' },
  ];
  selectedSatisfactionArea = '';
  selectedSatisfactionResponsable = '';
  selectedSatisfactionPeriod = '';
  selectedSatisfactionYear = '';
  selectedSatisfactionMonth = '';
  selectedSatisfactionScore = '';
  selectedSatisfactionCategory: '' | 'promoters' | 'passives' | 'detractors' = '';
  freshdesk: any = null;
  freshdeskLoading = false;
  freshdeskError = '';
  freshdeskPage = 1;
  freshdeskPerPage = 30;

  async ngOnInit(): Promise<void> {
    this.applyTheme();
    this.restoreSatisfactionCache();
    document.addEventListener('click', this.closeFiltersOnOutsideClick);
    this.startScheduleAutoRefresh();
    setTimeout(() => {
      void this.bootstrap();
    }, 0);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.closeFiltersOnOutsideClick);
    if (this.scheduleAutoRefreshTimer) clearInterval(this.scheduleAutoRefreshTimer);
    this.unbindTrendPanListeners();
    clearTimeout(this.trendClickTimer || undefined);
    if (this.filtersCloseTimer) clearTimeout(this.filtersCloseTimer);
  }

  private closeFiltersOnOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const insideViewMenu = !!target?.closest('.brand-view-toggle, .brand-view-menu');
    const insideFilters = !!target?.closest('.toolbar.card, .filter-segment');
    let changed = false;
    if (!insideViewMenu && this.viewMenuOpen) {
      this.viewMenuOpen = false;
      changed = true;
    }
    if (!insideFilters && (this.openFilter || this.filtersOpen)) {
      this.openFilter = null;
      this.closeFiltersPanel();
      changed = true;
    }
    if (changed) {
      this.cdr.detectChanges();
      return;
    }
    if (!target?.closest('.filter-box') && this.openFilter) {
      this.openFilter = null;
      this.cdr.detectChanges();
    }
  };

  private async bootstrap(): Promise<void> {
    try {
      await this.loadData();
      await this.loadSchedule();
      this.cdr.detectChanges();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo conectar';
      this.statusText = 'Backend no responde';
      this.subtitle = message;
      this.cdr.detectChanges();
    }
  }

  async loadData(forceRefresh = false): Promise<void> {
    const requestId = ++this.dashboardRequestId;
    const query = this.buildQuery();
    if (forceRefresh) {
      this.dashboardCache.clear();
    }
    if (!forceRefresh && this.dashboardCache.size) {
      await this.refreshHealthSnapshot();
    }
    const cached = forceRefresh ? null : this.dashboardCache.get(query);
    if (cached) {
      this.applyDashboardBootstrap(cached);
      void this.loadTicketsPage();
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();
    try {
      const params = new URLSearchParams(query);
      if (forceRefresh) {
        params.set('_ts', String(Date.now()));
      }
      const bootstrap = await this.fetchJson(`${this.apiBase}/api/bootstrap?${params.toString()}`);
      if (requestId !== this.dashboardRequestId) return;
      this.dashboardCache.set(query, bootstrap);
      if (this.dashboardCache.size > 24) {
        const oldestKey = this.dashboardCache.keys().next().value;
        if (oldestKey) this.dashboardCache.delete(oldestKey);
      }
      this.applyDashboardBootstrap(bootstrap);
      void this.loadTicketsPage(forceRefresh);
    } catch (error) {
      if (requestId !== this.dashboardRequestId) return;
      const message = error instanceof Error ? error.message : 'Error cargando datos';
      this.subtitle = message;
      this.statusText = 'Error al cargar';
    } finally {
      if (requestId !== this.dashboardRequestId) return;
      this.loading = false;
      this.areaFilterBusy = false;
      this.cdr.detectChanges();
    }
  }

  private async refreshHealthSnapshot(): Promise<void> {
    try {
      const previousCsv = this.health?.source_csv;
      const previousUpdated = this.health?.updated_at;
      const health = await this.fetchJson(`${this.apiBase}/api/health?_ts=${Date.now()}`);
      this.health = health;
      if (
        previousCsv &&
        (previousCsv !== health?.source_csv || previousUpdated !== health?.updated_at)
      ) {
        this.dashboardCache.clear();
      }
    } catch {
      // loadData() handles the main bootstrap error path; health is only a cache guard.
    }
  }

  private applyDashboardBootstrap(bootstrap: any): void {
    this.summary = bootstrap.summary;
    this.captureOperationsBalanceBaseline(bootstrap.summary);
    this.trend = bootstrap.trend;
    this.trendContext = bootstrap.trend_context;
    this.riskTrend = bootstrap.risk_trend;
    this.weeklyComparison = bootstrap.weekly_comparison;
    this.tickets = bootstrap.tickets || this.tickets;
    this.periodCards = bootstrap.period_cards;
    this.filtersMeta = {
      source_csv: bootstrap.source_csv,
      updated_at: bootstrap.updated_at,
      created_range: bootstrap.created_range,
      filters: bootstrap.filters,
    };
    this.health = {
      source_csv: bootstrap.source_csv,
      updated_at: bootstrap.updated_at,
      tickets: bootstrap.total_tickets || 0,
      filtered_tickets: bootstrap.summary?.total || bootstrap.tickets?.total || 0,
    };
    this.refreshSelectedOperationsDetail();
    this.subtitle = `Actualizado: ${bootstrap.updated_at || '—'}`;
    this.statusText = `Backend listo · ${this.formatNumber(this.health?.tickets ?? bootstrap.total_tickets ?? 0)} tickets históricos`;
  }

  private captureOperationsBalanceBaseline(summary: any): void {
    if (!this.selected.agente.length) {
      this.operationsBalanceBaseline.group = this.sortedSummaryRows(summary?.by_group_detail);
    }
    if (!this.selected.grupo.length) {
      this.operationsBalanceBaseline.agent = this.sortedSummaryRows(this.withAllResponsibleRows(summary?.by_agent_detail));
    }
  }

  async loadSchedule(forceRefresh = false): Promise<void> {
    this.scheduleLoading = true;
    this.scheduleError = '';
    this.cdr.detectChanges();
    try {
      const suffix = forceRefresh ? `?refresh=true&_ts=${Date.now()}` : '';
      this.schedule = await this.fetchJson(`${this.apiBase}/api/schedule${suffix}`);
      this.scheduleSignature = this.buildScheduleSignature(this.schedule);
      this.syncScheduleRemunerationMonthSelection();
    } catch (error) {
      this.scheduleError = error instanceof Error ? error.message : 'No se pudo cargar cronograma';
    } finally {
      this.scheduleLoading = false;
      this.cdr.detectChanges();
    }
  }

  async loadDocuments(forceRefresh = false): Promise<void> {
    this.documentsLoading = true;
    this.documentsError = '';
    if (forceRefresh) {
      this.documents = null;
      this.visibleDocRows = [];
      this.selectedDocument = null;
      this.selectedDuplicateArea = '';
      this.selectedDuplicateGroupKey = '';
    }
    this.cdr.detectChanges();
    try {
      const suffix = forceRefresh ? `?refresh=true&_ts=${Date.now()}` : '';
      this.documents = await this.fetchJson(`${this.apiBase}/api/documents${suffix}`);
      this.rebuildVisibleDocRows();
    } catch (error) {
      this.documentsError = error instanceof Error ? error.message : 'No se pudo cargar biblioteca documental';
    } finally {
      this.documentsLoading = false;
      this.cdr.detectChanges();
    }
  }

  get docFilteredQueue(): any[] {
    const queue = this.documents?.review_queue || this.documents?.documents || [];
    const searchNorm = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    let filtered = queue.filter((r: any) => {
      if (this.docFilterEstado.length && !this.docFilterEstado.includes(r['Estado'])) return false;
      if (this.docFilterTipo.length && !this.docFilterTipo.includes(r['Tipo estimado'])) return false;
      if (this.docFilterGrupo.length && !this.docFilterGrupo.includes(r['Grupo'])) return false;
      if (this.docFilterResponsable.length && !this.docFilterResponsable.includes(r['Responsable'])) return false;
      if (this.docFilterPrioridad.length && !this.docFilterPrioridad.includes(r['Prioridad'])) return false;
      if (this.docFilterAntiguedad) {
        const monthsRaw = r['Meses sin actualización'] || '';
        const match = monthsRaw.match(/(\d+)/);
        const months = match ? parseInt(match[1], 10) : 0;
        if (months < parseInt(this.docFilterAntiguedad, 10)) return false;
      }
      if (this.selectedDocKpi === 'sugerencias_automatizacion' && (!r['Sugerencia automatización'] || r['Sugerencia automatización'] === '(vacío)')) return false;
      if (this.selectedDocKpi === 'pendientes_revision' && !searchNorm(String(r['Estado'] || '')).includes('pendiente')) return false;
      if (this.selectedDocKpi === 'revisados_iniciales' && r['Estado'] !== 'Revisado inicial') return false;
      if (this.selectedDocKpi === 'duplicados_revisados' && !this.isDocDuplicateReviewed(r)) return false;
      if (this.selectedDocKpi === 'duplicados_confirmados' && !this.isDocDuplicateConfirmed(r)) return false;
      if (this.selectedDocKpi === 'politicas_rh' && !searchNorm(String(r['Tipo estimado'] || '')).includes('politica')) return false;
      if (this.selectedDocKpi === 'procedimientos' && !['procedimiento', 'instructivo'].some(term => searchNorm(String(r['Tipo estimado'] || '')).includes(term))) return false;
      if (this.selectedDocKpi === 'desactualizados' && !this.isDocDesactualizado(r)) return false;
      if (this.selectedDocKpi === 'listos_ia' && !['listo para ia', 'completado', 'preparado'].includes(searchNorm(String(r['Estado IA'] || ''))) && searchNorm(String(r['Estado'] || '')) !== 'listo para ia') return false;
      if (this.selectedDocKpi === 'obsoletos' && searchNorm(String(r['Estado'] || '')) !== 'obsoleto') return false;
      if (this.docFilterBusqueda) {
        const term = searchNorm(this.docFilterBusqueda);
        const fields = [r['Documento'], r['ID'], r['Responsable'], r['Grupo'], r['Tipo estimado']];
        if (!fields.some((f: any) => searchNorm(String(f || '')).includes(term))) return false;
      }
      return true;
    });
    return filtered;
  }

  get docFilteredEstado(): string[] {
    return [...new Set(this.getUnifiedOfficialRows().map((r: any) => r.Estado).filter(Boolean))].sort();
  }
  get docFilteredTipo(): string[] {
    return [...new Set(this.getUnifiedOfficialRows().map((r: any) => this.getCanonicalDocType(r)).filter(Boolean))].sort();
  }
  get docFilteredGrupo(): string[] {
    return [...new Set(this.getUnifiedOfficialRows().map((r: any) => r.Grupo).filter(Boolean))].sort();
  }
  get docFilteredResponsable(): string[] {
    return [...new Set(this.getUnifiedOfficialRows().map((r: any) => r.Responsable).filter(Boolean))].sort();
  }
  get docFilteredPrioridad(): string[] {
    return [...new Set(this.getUnifiedOfficialRows().map((r: any) => r.Prioridad).filter(Boolean))].sort();
  }

  get docFilteredFreshdeskEstado(): string[] {
    return this.documents?.filters?.freshdesk_estado || [];
  }

  get docFilterDefs(): Array<{ label: string; key: string; selected: string[]; options: string[] }> {
    return [
      { label: 'Estado', key: 'estado', selected: this.docFilterEstado, options: this.docFilteredEstado },
      { label: 'Tipo', key: 'tipo', selected: this.docFilterTipo, options: this.docFilteredTipo },
      { label: 'Grupo', key: 'grupo', selected: this.docFilterGrupo, options: this.docFilteredGrupo },
      { label: 'Responsable', key: 'responsable', selected: this.docFilterResponsable, options: this.docFilteredResponsable },
      { label: 'Prioridad', key: 'prioridad', selected: this.docFilterPrioridad, options: this.docFilteredPrioridad },
    ];
  }

  get statusBarItems(): Array<{ label: string; value: number; pct: number; color: string }> {
    const rows = this.getUnifiedOfficialRows();
    const total = rows.length || 1;
    const countStatus = (predicate: (status: string) => boolean) =>
      rows.filter((row: any) => predicate(this.docNormalizeLabel(row.Estado))).length;
    const items = [
      { label: 'Pendiente de validar', value: countStatus(status => status.includes('pendiente')), color: '#e3c628' },
      { label: 'Revisado inicial', value: countStatus(status => status === 'revisado inicial'), color: '#3fb58b' },
      { label: 'En revisión', value: countStatus(status => status === 'en revision'), color: '#56a2a9' },
      { label: 'Validado', value: countStatus(status => status === 'validado'), color: '#079767' },
      { label: 'Listo para IA', value: countStatus(status => status === 'listo para ia'), color: '#2d8c6c' },
      { label: 'Obsoleto', value: countStatus(status => status === 'obsoleto'), color: '#cf1431' },
    ];
    return items.map(item => ({ ...item, pct: Math.round((item.value / total) * 100) }));
  }

  requestDocRefresh(): void {
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  get docUnifiedKpis(): Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }> {
    const k = this.documents?.kpis || {};
    const officialRows = this.getUnifiedOfficialRows();
    const identity = this.documentIdentity;
    const norm = (value: any) => this.docNormalizeLabel(value);
    const policies = officialRows.filter((r: any) => norm(this.getDocTypeValue(r)).includes('politica')).length;
    const procedures = officialRows.filter((r: any) => ['procedimiento', 'instructivo'].some(term => norm(this.getDocTypeValue(r)).includes(term))).length;
    const pending = officialRows.filter((r: any) => r.Estado === 'Pendiente' || norm(r.Estado).includes('pendiente')).length;
    const reviewed = officialRows.filter((r: any) => r.Estado === 'Revisado inicial').length;
    const outdated = officialRows.filter((r: any) => this.isDocDesactualizado(r)).length;
    const listosIa = officialRows.filter((r: any) => ['listo para ia', 'completado', 'preparado'].includes(norm(r['Estado IA'])) || norm(r.Estado) === 'listo para ia').length;
    const obsolete = officialRows.filter((r: any) => norm(r.Estado) === 'obsoleto').length;
    const duplicated = officialRows.filter((r: any) => this.isDocDuplicado(r)).length;
    const physicalDepured = this.documentFolderUpdate.depurados || 0;
    const totalRecords = identity.total_a_revisar || identity.registros_homologados || officialRows.length;
    const baseRecords = identity.total_registros || totalRecords;
    const duplicateSummary = this.documents?.duplicate_review_summary || {};
    const duplicateAnalyzedTotal = duplicateSummary.registros || Math.max(baseRecords - pending, 0);
    const duplicateCurrent = duplicateSummary.versiones_vigentes || Math.max(duplicateAnalyzedTotal - physicalDepured, 0);
    return [
      { label: 'Total a revisar', value: totalRecords, note: `${this.formatNumber(baseRecords)} registros base`, key: 'total', tone: 'neutral', badge: '' },
      { label: 'Pendientes por revisar', value: pending, note: 'Requieren validación documental', key: 'pendientes_revision', tone: 'warning', badge: '' },
      { label: 'Revisados', value: reviewed, note: 'Primer control completo', key: 'revisados_iniciales', tone: 'ready', badge: '' },
      { label: 'Q total analizado duplicados', value: duplicateAnalyzedTotal, note: `${this.formatNumber(physicalDepured)} depurados · ${this.formatNumber(duplicateCurrent)} vigentes`, key: 'duplicados_revisados', tone: 'ready', badge: 'Mapa' },
      { label: 'Depurados', value: physicalDepured, note: 'Archivos en respaldo trazable', key: 'depurados_fisicos', tone: 'ready', badge: 'Físico' },
      { label: 'Políticas', value: policies, note: 'Tipo documental política', key: 'politicas_rh', tone: 'info', badge: '' },
      { label: 'Proc. / Inst.', value: procedures, note: 'Procedimientos e instructivos', key: 'procedimientos', tone: 'info', badge: '' },
      { label: 'Vencidos', value: outdated, note: 'Con antigüedad crítica', key: 'desactualizados', tone: 'risk', badge: 'Riesgo' },
      { label: 'Listos IA', value: listosIa, note: 'Estado IA completado/listo', key: 'listos_ia', tone: 'ready', badge: 'IA' },
      { label: 'Obsoletos', value: obsolete, note: 'Marcados como obsoletos', key: 'obsoletos', tone: 'risk', badge: 'Riesgo' },
      { label: 'Sin área', value: k.sin_clasificar || 0, note: 'Pendientes de área', key: 'sin_clasificar', tone: 'risk', badge: '' },
      { label: 'Rev. área', value: k.requieren_revision_area || 0, note: 'Marcados para revisión de área', key: 'requieren_revision', tone: 'warning', badge: '' },
      { label: 'Propuestas', value: officialRows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación').length, note: 'Incluidas en Control Documentos', key: 'sugerencias', tone: 'warning', badge: '' },
      { label: 'Alta conf.', value: officialRows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación' && norm(r.Confianza) === 'alta').length, note: 'Propuestas listas para validar', key: 'alta_confianza', tone: 'ready', badge: '' },
      { label: 'Media conf.', value: officialRows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación' && norm(r.Confianza) === 'media').length, note: 'Requieren revisión humana', key: 'media_confianza', tone: 'warning', badge: '' },
      { label: 'Pend. comparar', value: duplicated, note: 'Registros por comparar/versionar', key: 'duplicados', tone: 'warning', badge: '' },
      { label: 'Área dudosa', value: k.posibles_falsos_positivos_seguridad_ocupacional || 0, note: 'Posible corrección de área', key: 'falsos_positivos', tone: 'risk', badge: 'Riesgo' },
      { label: 'Freshdesk', value: officialRows.filter((r: any) => r['Origen levantamiento'] === 'Freshdesk').length, note: 'Incluidos en Control Documentos', key: 'freshdesk_integrado', tone: 'info', badge: '' },
      { label: 'Mejora', value: k.sugerencias_automatizacion || 0, note: 'Documentos con sugerencia de automatización', key: 'sugerencias_automatizacion', tone: 'info', badge: '' },
    ];
  }

  get docControlSummaryKpis(): Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }> {
    const summary = ['total', 'pendientes_revision', 'duplicados_revisados', 'depurados_fisicos', 'listos_ia', 'obsoletos'];
    return summary
      .map(key => this.docUnifiedKpis.find(card => card.key === key))
      .filter(Boolean) as Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }>;
  }

  isDocKpiFilterable(key: string): boolean {
    return !['duplicados_revisados', 'duplicados_confirmados', 'depurados_fisicos', 'fisico_controlado'].includes(key);
  }

  get docPrimaryKpis(): Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }> {
    const priority = ['pendientes_revision', 'requieren_revision', 'desactualizados', 'sugerencias', 'listos_ia', 'freshdesk_integrado'];
    return priority
      .map(key => this.docUnifiedKpis.find(card => card.key === key))
      .filter(Boolean) as Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }>;
  }

  get docSecondaryKpis(): Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }> {
    const secondary = ['total', 'politicas_rh', 'procedimientos', 'duplicados', 'falsos_positivos'];
    return secondary
      .map(key => this.docUnifiedKpis.find(card => card.key === key))
      .filter(Boolean) as Array<{ label: string; value: number; note: string; key: string; tone: string; badge: string }>;
  }

  get docPanelSourceLabel(): string {
    const labels = {
      todas: '',
      oficial: '',
      sugerencia: 'Sugerencias integradas',
    };
    return labels[this.docFilterFuente];
  }

  docNormalizeLabel(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  getDocTypeValue(row: any): string {
    return row?.['Tipo estimado'] || row?.['Categoría sugerida'] || row?.['Tipo archivo'] || row?.['Tipo de archivo'] || 'Sin tipo';
  }

  getCanonicalDocType(rowOrType: any): string {
    const raw = typeof rowOrType === 'string' ? rowOrType : this.getDocTypeValue(rowOrType);
    const normalized = this.docNormalizeLabel(raw);
    if (!normalized || normalized === 'sin tipo') return 'Sin tipo';
    if (normalized === 'politica') return 'Política';
    if (normalized === 'articulo operativo') return 'Artículo operativo';
    if (normalized === 'articulo de solucion / base de conocimiento') return 'Artículo de solución / base de conocimiento';
    if (normalized === 'documento candidato') return 'Documento por clasificar';
    return String(raw || 'Sin tipo').trim();
  }

  docOfficialTypeTotal(tipo: string): number {
    const target = this.docNormalizeLabel(tipo);
    return this.getUnifiedOfficialRows().filter((row: any) => this.docNormalizeLabel(this.getCanonicalDocType(row)) === target).length;
  }

  get docTypeSegments(): Array<{ tipo: string; total: number; pct: number; oficiales: number; sugerencias: number; pendientes: number; desactualizados: number }> {
    const activeTypeFilters = [...this.docFilterTipo];
    this.docFilterTipo = [];
    const rows = this.computeDocRows();
    this.docFilterTipo = activeTypeFilters;
    const totalRows = rows.length || 1;
    const map = new Map<string, { tipo: string; total: number; oficiales: number; sugerencias: number; pendientes: number; desactualizados: number }>();
    for (const row of rows) {
      const tipo = this.getCanonicalDocType(row);
      const key = this.docNormalizeLabel(tipo);
      const current = map.get(key) || { tipo, total: 0, oficiales: 0, sugerencias: 0, pendientes: 0, desactualizados: 0 };
      current.total += 1;
      if (row['Fuente registro'] === 'Excel oficial') current.oficiales += 1;
      else if (row['Fuente registro'] === 'Propuesta reclasificación') current.sugerencias += 1;
      if (String(row.Estado || '').toLowerCase().includes('pendiente')) current.pendientes += 1;
      if (this.isDocDesactualizado(row)) current.desactualizados += 1;
      map.set(key, current);
    }
    return [...map.values()]
      .map(item => ({ ...item, pct: Math.round((item.total / totalRows) * 100) }))
      .sort((a, b) => b.total - a.total || a.tipo.localeCompare(b.tipo));
  }

  get docTypeSegmentTotal(): number {
    return this.docActiveRows.length;
  }

  get docUnifiedTypeSummary(): Array<{ tipo: string; total: number; pendientes: number; revisados: number; avance_pct: number }> {
    const map = new Map<string, { tipo: string; total: number; pendientes: number; revisados: number }>();
    for (const row of this.getUnifiedOfficialRows()) {
      const tipo = this.getCanonicalDocType(row);
      const key = this.docNormalizeLabel(tipo);
      const current = map.get(key) || { tipo, total: 0, pendientes: 0, revisados: 0 };
      current.total += 1;
      if (this.docNormalizeLabel(row.Estado).includes('pendiente')) current.pendientes += 1;
      if (row.Estado === 'Revisado inicial') current.revisados += 1;
      map.set(key, current);
    }
    return [...map.values()]
      .map(item => ({ ...item, avance_pct: item.total ? Math.round((item.revisados / item.total) * 1000) / 10 : 0 }))
      .sort((a, b) => b.total - a.total || a.tipo.localeCompare(b.tipo));
  }

  applyDocTypeSegment(tipo: string): void {
    const normalized = this.docNormalizeLabel(tipo);
    const active = this.docFilterTipo.some(item => this.docNormalizeLabel(item) === normalized);
    this.docFilterTipo = active ? [] : [tipo];
    this.selectedDocKpi = '';
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  get documentTotalWorkStatus(): { total: number; official: number; suggestions: number; integrated: number } {
    const officialRows = this.getUnifiedOfficialRows();
    const integrated = officialRows.filter((r: any) => r['Origen levantamiento'] === 'Freshdesk').length;
    const suggestions = officialRows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación').length;
    return {
      total: officialRows.length,
      official: officialRows.length,
      suggestions,
      integrated,
    };
  }

  get documentIdentity(): any {
    const identity = this.documents?.document_identity || {};
    const scope = this.documents?.document_scope || {};
    const rows = this.getUnifiedOfficialRows();
    const ids = rows.map((row: any) => String(row?.ID || '').trim()).filter((id: string) => id);
    const uniqueIds = new Set(ids);
    return {
      registros_homologados: scope.total_a_revisar ?? identity.total_a_revisar ?? identity.registros_homologados ?? rows.length,
      total_registros: scope.total_registros ?? identity.total_registros ?? identity.registros_homologados ?? rows.length,
      total_a_revisar: scope.total_a_revisar ?? identity.total_a_revisar ?? identity.registros_homologados ?? rows.length,
      ids_con_valor: identity.ids_con_valor ?? ids.length,
      ids_unicos: identity.ids_unicos ?? uniqueIds.size,
      ids_repetidos: identity.ids_repetidos ?? 0,
      filas_con_id_repetido: identity.filas_con_id_repetido ?? 0,
      repeticiones_id_adicionales: identity.repeticiones_id_adicionales ?? Math.max(ids.length - uniqueIds.size, 0),
      sin_id: identity.sin_id ?? Math.max(rows.length - ids.length, 0),
    };
  }

  get docControlSubtitle(): string {
    const identity = this.documentIdentity;
    const visible = this.docActiveRows.length;
    const total = identity.total_a_revisar || identity.registros_homologados || 0;
    const baseTotal = identity.total_registros || total;
    const parts = [
      `${this.formatNumber(total)} a revisar`,
    ];
    if (baseTotal !== total) {
      parts.push(`${this.formatNumber(baseTotal)} registros base`);
    }
    if (visible !== total || this.selectedDocKpi) {
      parts.push(`${this.formatNumber(visible)} visibles`);
    }
    if (this.selectedDocKpi) {
      parts.push(`filtro activo: ${this.docKpiLabel(this.selectedDocKpi)}`);
    }
    return parts.join(' · ');
  }

  get docAiReadinessItems(): Array<{ label: string; value: number; tone: string }> {
    const ai = this.documents?.ai_readiness || {};
    return [
      { label: 'Con estado IA', value: ai.con_estado_ia || 0, tone: 'info' },
      { label: 'En preparación', value: ai.en_preparacion || 0, tone: 'warning' },
      { label: 'Pendientes IA', value: ai.pendientes_preparacion || 0, tone: 'warning' },
      { label: 'Metadatos IA completos', value: ai.metadatos_completos || 0, tone: 'ready' },
      { label: 'Listos indexación', value: ai.listos_indexacion || 0, tone: 'ready' },
    ];
  }

  get docAiReadinessEmpty(): boolean {
    const ai = this.documents?.ai_readiness || {};
    return !(ai.con_estado_ia || ai.en_preparacion || ai.pendientes_preparacion || ai.metadatos_completos || ai.listos_indexacion);
  }

  get documentAreaTracking(): any[] {
    return this.documents?.area_tracking || [];
  }

  get documentFolderUpdate(): any {
    return this.documents?.folder_update || {};
  }

  get documentFolderUpdateStatusItems(): Array<{ label: string; value: number; pct: number; tone: string }> {
    const summary = this.documentFolderUpdate;
    const total = Math.max(1, summary.total || 0);
    return [
      { label: 'Total a revisar', value: summary.total_a_revisar || summary.total || 0, pct: 100, tone: 'neutral' },
      { label: 'Registros base', value: summary.total_registros || 0, pct: ((summary.total_registros || 0) / total) * 100, tone: 'info' },
      { label: 'Por revisar activos', value: summary.por_revisar_activos || summary.por_revisar || 0, pct: ((summary.por_revisar_activos || summary.por_revisar || 0) / total) * 100, tone: 'info' },
      { label: 'Depurados físicos', value: summary.depurados || 0, pct: ((summary.depurados || 0) / total) * 100, tone: 'ready' },
      { label: 'Físico controlado', value: summary.total_fisico_controlado || 0, pct: ((summary.total_fisico_controlado || 0) / total) * 100, tone: 'ready' },
      { label: 'Diferencia', value: summary.diferencia_excel_fisico || 0, pct: Math.abs(summary.diferencia_excel_fisico || 0) / total * 100, tone: summary.diferencia_excel_fisico ? 'warning' : 'ready' },
    ];
  }

  get documentFolderUpdateAreas(): any[] {
    return this.documents?.folder_update_area_summary || [];
  }

  get metricsSourceCards(): Array<{ label: string; value: string; note: string; tone: string }> {
    const summary = this.summary || {};
    const scheduleMilestones = this.schedule?.activities?.reduce((acc: number, activity: any) => acc + ((activity.milestones || []).length || 0), 0) || 0;
    const currentMonthSat = this.metricsCurrentMonthSatisfactionSummary;
    const documentScope = this.documents?.document_scope || {};
    return [
      {
        label: 'Operación',
        value: this.formatNumber(summary.total || 0),
        note: `${this.formatNumber(summary.unresolved || 0)} sin resolver`,
        tone: 'red',
      },
      {
        label: 'Satisfacción',
        value: currentMonthSat.nps === null || currentMonthSat.nps === undefined ? 'NPS —' : `NPS ${currentMonthSat.nps}`,
        note: `Nota ${currentMonthSat.average === null || currentMonthSat.average === undefined ? '—' : Number(currentMonthSat.average).toFixed(1)} · ${this.currentSatisfactionMonthLabel} · ${this.formatNumber(currentMonthSat.answered || 0)} encuestas`,
        tone: 'teal',
      },
      {
        label: 'Cronograma',
        value: this.formatNumber(scheduleMilestones),
        note: `${this.formatNumber(this.scheduleVisiblePendingCount || 0)} hitos pendientes`,
        tone: 'amber',
      },
      {
        label: 'Documentos',
        value: this.formatNumber(documentScope.total_a_revisar || 0),
        note: `${this.formatNumber(documentScope.total_registros || 0)} registros base`,
        tone: 'ink',
      },
    ];
  }

  get metricsExecutiveCards(): Array<{ label: string; value: string; note: string; accent: string }> {
    const summary = this.summary || {};
    const sat = this.satisfactionSummary || {};
    const documentScope = this.documents?.document_scope || {};
    const folder = this.documents?.folder_update || {};
    const scheduleMilestones = this.schedule?.activities?.reduce((acc: number, activity: any) => acc + ((activity.milestones || []).length || 0), 0) || 0;
    const scheduleDone = this.schedule?.activities?.reduce((acc: number, activity: any) => acc + ((activity.milestones || []).filter((milestone: any) => milestone.status_key === 'done').length || 0), 0) || 0;
    const schedulePct = scheduleMilestones ? Math.round((scheduleDone / scheduleMilestones) * 100) : 0;
    return [
      {
        label: 'Carga activa',
        value: this.formatNumber(summary.unresolved || 0),
        note: `${this.formatNumber(summary.open || 0)} abiertos · ${this.formatNumber(summary.pending || 0)} pendientes`,
        accent: 'critical',
      },
      {
        label: 'SLA operación',
        value: this.formatPercent(summary?.sla?.within_pct),
        note: `${this.formatNumber(summary?.sla?.within || 0)} dentro · ${this.formatNumber(summary?.sla?.violated || 0)} fuera`,
        accent: 'stable',
      },
      {
        label: 'Calidad percibida',
        value: sat.average ? Number(sat.average).toFixed(1) : '—',
        note: `${this.formatNumber(sat.promoters || 0)} promotores · ${this.formatNumber(sat.detractors || 0)} detractores`,
        accent: 'teal',
      },
      {
        label: 'Avance cronograma',
        value: `${schedulePct}%`,
        note: `${this.formatNumber(scheduleDone)} cerrados de ${this.formatNumber(scheduleMilestones)}`,
        accent: 'amber',
      },
      {
        label: 'Documentos a revisar',
        value: this.formatNumber(documentScope.total_a_revisar || 0),
        note: `${this.formatNumber(folder.pendientes_integrar || 0)} por integrar · ${this.formatNumber(folder.pendientes_mover || 0)} por mover`,
        accent: 'ink',
      },
      {
        label: 'Depuración documental',
        value: this.formatNumber(folder.depurados || 0),
        note: `${this.formatNumber(documentScope.depurados_no_operativos || 0)} no operativos en base`,
        accent: 'neutral',
      },
    ];
  }

  get metricsFocusRows(): Array<{ area: string; metric: string; value: string; status: string; tone: string }> {
    const summary = this.summary || {};
    const sat = this.satisfactionSummary || {};
    const folder = this.documents?.folder_update || {};
    return [
      {
        area: 'Operación',
        metric: 'Riesgo alto',
        value: this.formatNumber(summary?.risk?.high || 0),
        status: 'Seguimiento diario',
        tone: (summary?.risk?.high || 0) ? 'risk' : 'ok',
      },
      {
        area: 'Operación',
        metric: 'Sin asignar',
        value: this.formatNumber(this.unassignedTicketsCount || 0),
        status: 'Carga sin responsable',
        tone: (this.unassignedTicketsCount || 0) ? 'warning' : 'ok',
      },
      {
        area: 'Satisfacción',
        metric: 'NPS',
        value: String(sat.nps ?? '—'),
        status: `${this.formatNumber(sat.answered || 0)} respuestas`,
        tone: Number(sat.nps || 0) < 0 ? 'risk' : 'ok',
      },
      {
        area: 'Cronograma',
        metric: 'Pendientes',
        value: this.formatNumber(this.scheduleVisiblePendingCount || 0),
        status: `${this.formatNumber(this.scheduleVisibleDoneCount || 0)} cerrados`,
        tone: (this.scheduleVisiblePendingCount || 0) ? 'warning' : 'ok',
      },
      {
        area: 'Documentos',
        metric: 'Diferencia física',
        value: this.formatNumber(folder.diferencia_excel_fisico || 0),
        status: 'Control carpetas',
        tone: (folder.diferencia_excel_fisico || 0) ? 'warning' : 'ok',
      },
    ];
  }

  get metricsResolutionTotal(): number {
    return (Number(this.metricsResolutionBuckets?.total) || 0) + this.metricsResolutionUnresolved;
  }

  get metricsResolutionBuckets(): any {
    const area = this.selectedMetricsAreaResolution;
    if (area?.resolution_time_buckets) return area.resolution_time_buckets;
    return this.selectedMetricsMonth?.resolution_time_buckets || this.summary?.resolution_time_buckets || {};
  }

  get metricsResolutionUnresolved(): number {
    const area = this.selectedMetricsAreaResolution;
    if (area) return Number(area.unresolved) || 0;
    if (this.selectedMetricsMonth) return Number(this.selectedMetricsMonth.unresolved) || 0;
    return Number(this.summary?.unresolved) || 0;
  }

  get metricsResolutionTitle(): string {
    const scope = [this.selectedMetricsMonth?.label, this.selectedMetricsArea].filter(Boolean).join(' · ');
    return scope ? `Tiempo de resolución · ${scope}` : 'Tiempo de resolución';
  }

  get metricsResolutionSlices(): MetricsPieSlice[] {
    const buckets = this.metricsResolutionBuckets;
    const circumference = 263.89;
    const total = Math.max(0, this.metricsResolutionTotal);
    const rows = [
      { key: 'same_day', label: 'Mismo día', value: Number(buckets.same_day) || 0, color: 'var(--claro-cyan)' },
      { key: 'two_three_days', label: '2-3 días', value: Number(buckets.two_three_days) || 0, color: 'var(--violet)' },
      { key: 'four_plus_days', label: '4+ días', value: Number(buckets.four_plus_days) || 0, color: 'var(--claro-red)' },
      { key: 'unresolved', label: 'No resueltos', value: this.metricsResolutionUnresolved, color: 'var(--metrics-muted)' },
    ];
    let offset = 0;
    return rows.map((row) => {
      const length = total ? (row.value / total) * circumference : 0;
      const slice = {
        ...row,
        pct: total ? Math.round((row.value / total) * 100) : 0,
        dash: `${length.toFixed(2)} ${(circumference - length).toFixed(2)}`,
        offset: Number(offset.toFixed(2)),
      };
      offset += length;
      return slice;
    });
  }

  get metricsResolutionSamePct(): number {
    return this.metricsResolutionSlices.find((slice) => slice.key === 'same_day')?.pct || 0;
  }

  get metricsResolutionTwoThreePct(): number {
    return this.metricsResolutionSlices.find((slice) => slice.key === 'two_three_days')?.pct || 0;
  }

  get metricsResolutionFourPlusPct(): number {
    return this.metricsResolutionSlices.find((slice) => slice.key === 'four_plus_days')?.pct || 0;
  }

  get metricsResolutionSixMonthTrend(): MetricsResolutionTrendPoint[] {
    return this.periodRecent.slice(0, 6).reverse().map((item: any) => {
      const key = item?.key || item?.label || '';
      const area = this.selectedMetricsArea ? this.findAreaResolution(item?.area_resolution_distribution, this.selectedMetricsArea) : null;
      const buckets = area?.resolution_time_buckets || item?.resolution_time_buckets || {};
      const sameDay = Number(buckets.same_day) || 0;
      const twoThreeDays = Number(buckets.two_three_days) || 0;
      const fourPlusDays = Number(buckets.four_plus_days) || 0;
      const total = Number(buckets.total) || sameDay + twoThreeDays + fourPlusDays;
      const withinThreeDays = sameDay + twoThreeDays;
      return {
        key,
        label: this.metricsMonthLabel(key, item?.label),
        total,
        sameDay,
        twoThreeDays,
        fourPlusDays,
        sameDayPct: total ? (sameDay / total) * 100 : 0,
        twoThreeDaysPct: total ? (twoThreeDays / total) * 100 : 0,
        fourPlusDaysPct: total ? (fourPlusDays / total) * 100 : 0,
        withinThreeDays,
        withinThreeDaysPct: total ? Math.round((withinThreeDays / total) * 100) : 0,
      };
    });
  }

  get metricsMonthlyResolutionBars(): Array<{ key: string; label: string; received: number; resolved: number; unresolved: number; resolvedPct: number; unresolvedPct: number; resolvedRatePct: number; unresolvedRatePct: number }> {
    return [...this.periodRecent].reverse().map((item: any) => {
      const key = item?.key || item?.label || '';
      const area = this.selectedMetricsArea ? this.findAreaResolution(item?.area_resolution_distribution, this.selectedMetricsArea) : null;
      const received = Number(area?.value ?? item?.received) || 0;
      const resolved = Math.max(0, Math.min(received, Number(area?.closed ?? item?.closed) || 0));
      const unresolved = Math.max(0, Math.min(received, Number(area?.unresolved ?? item?.unresolved) || 0));
      const resolvedRatePct = received ? (resolved / received) * 100 : 0;
      const unresolvedRatePct = received ? (unresolved / received) * 100 : 0;
      const rawUnresolvedPct = received ? (unresolved / received) * 100 : 0;
      const unresolvedPct = unresolved ? Math.max(rawUnresolvedPct, 4) : 0;
      return {
        key,
        label: this.metricsMonthLabel(key, item?.label),
        received,
        resolved,
        unresolved,
        resolvedPct: received ? Math.max(0, 100 - unresolvedPct) : 0,
        unresolvedPct,
        resolvedRatePct,
        unresolvedRatePct,
      };
    });
  }

  get metricsCentroServicioDerivationRows(): Array<{ key: string; label: string; resolved: number; unresolved: number; total: number; monthlyTotal: number; sharePct: number }> {
    return this.periodRecent.slice(0, 6).reverse().map((item: any) => {
      const key = item?.key || item?.label || '';
      const area = this.findAreaResolution(item?.area_resolution_distribution, 'Centro de Servicio');
      const monthlyTotal = Number(item?.received) || 0;
      const total = Number(area?.value) || 0;
      const resolved = Math.max(0, Math.min(total, Number(area?.closed) || 0));
      const unresolved = Math.max(0, Math.min(total, Number(area?.unresolved) || 0));
      return {
        key,
        label: this.metricsMonthLabel(key, item?.label),
        resolved,
        unresolved,
        total,
        monthlyTotal,
        sharePct: monthlyTotal ? Math.round((total / monthlyTotal) * 100) : 0,
      };
    });
  }

  get metricsCentroServicioDerivationTotal(): number {
    return this.metricsCentroServicioDerivationRows.reduce((sum, row) => sum + row.total, 0);
  }

  get metricsCentroServicioOwnerBreakdownRows(): Array<{ key: string; label: string; total: number; values: Array<{ label: string; value: number; pct: number; startPct: number; endPct: number; outsideLabel: boolean; color: string }> }> {
    const owners = [
      { match: 'diego astudillo [g]', label: 'Diego Astudillo', color: '#009cb1' },
      { match: 'maria joaquina [g]', label: 'Maria Joaquina', color: '#5d7cfa' },
      { match: 'alejandro muñoz [g]', label: 'Alejandro Muñoz', color: '#da291c' },
    ];
    return this.periodRecent.slice(0, 3).reverse().map((item: any) => {
      const groups = (item?.group_resolution_distribution || []) as any[];
      const area = this.findAreaResolution(item?.area_resolution_distribution, 'Centro de Servicio');
      const areaTotal = Number(area?.value) || 0;
      const areaClosed = Number(area?.closed) || 0;
      const areaUnresolved = Number(area?.unresolved) || 0;
      const rawValues = owners.map((owner) => {
        const found = groups.find((group: any) => this.normalize(group?.label || '') === this.normalize(owner.match));
        const value = Number(found?.closed) || 0;
        return { label: owner.label, value, color: owner.color };
      });
      const knownClosed = rawValues.reduce((sum, row) => sum + row.value, 0);
      const remainderClosed = Math.max(0, areaClosed - knownClosed);
      if (remainderClosed > 0) {
        rawValues.push({ label: 'Resto / sin grupo', value: remainderClosed, color: '#8a94a6' });
      }
      if (areaUnresolved > 0) {
        rawValues.push({ label: 'Pendientes', value: areaUnresolved, color: '#d89a13' });
      }
      const total = areaTotal || rawValues.reduce((sum, row) => sum + row.value, 0);
      let cursor = 0;
      return {
        key: item?.key || item?.label || '',
        label: this.metricsMonthLabel(item?.key || item?.label || '', item?.label),
        total,
        values: rawValues.map((value) => {
          const pct = total ? (value.value / total) * 100 : 0;
          const startPct = cursor;
          cursor += pct;
          return {
            ...value,
            pct,
            startPct,
            endPct: cursor,
            outsideLabel: pct > 0 && pct < 12,
          };
        }),
      };
    });
  }

  get selectedMetricsMonth(): any | null {
    if (!this.selectedMetricsMonthKey) return null;
    return this.periodRecent.find((item: any) => item?.key === this.selectedMetricsMonthKey) || null;
  }

  get selectedMetricsAreaResolution(): any | null {
    if (!this.selectedMetricsArea) return null;
    const source = this.selectedMetricsMonth?.area_resolution_distribution || this.periodCards?.area_resolution_distribution || [];
    return this.findAreaResolution(source, this.selectedMetricsArea);
  }

  get metricsSatisfactionTitle(): string {
    const scope = [this.selectedMetricsArea, this.selectedMetricsMonth ? `hasta ${this.metricsMonthLabel(this.selectedMetricsMonth.key, this.selectedMetricsMonth.label)}` : 'últimos 6 meses'].filter(Boolean).join(' · ');
    return `Satisfacción · ${scope}`;
  }

  get metricsSatisfactionTrendRows(): MetricsSatisfactionTrendPoint[] {
    const validItems = this.satisfactionItems.filter((item) => /^\d{4}-\d{2}$/.test(String(item?.period || '')));
    const periodKeys = Array.from(new Set(validItems.map((item) => String(item.period))))
      .filter((key) => !this.selectedMetricsMonthKey || key <= this.selectedMetricsMonthKey)
      .sort((a, b) => a.localeCompare(b))
      .slice(-6);
    return periodKeys.map((key) => {
      const items = validItems.filter((item) => {
        if (String(item.period) !== key) return false;
        if (this.selectedMetricsArea && item.area !== this.selectedMetricsArea) return false;
        return true;
      });
      const summary = this.calculateSatisfactionSummary(items);
      const average = summary.average === null || summary.average === undefined ? null : Number(summary.average);
      const nps = summary.nps === null || summary.nps === undefined ? null : Number(summary.nps);
      return {
        key,
        label: this.metricsMonthLabel(key),
        answered: Number(summary.answered) || 0,
        detractors: Number(summary.detractors) || 0,
        detractorPct: summary.answered ? (Number(summary.detractors) || 0) / Number(summary.answered) * 100 : 0,
        average,
        nps,
        notePct: average === null ? 0 : Math.max(0, Math.min(100, (average / 10) * 100)),
        npsPct: nps === null ? 0 : Math.max(0, Math.min(100, ((nps + 100) / 200) * 100)),
      };
    });
  }

  get metricsSatisfactionHasData(): boolean {
    return this.metricsSatisfactionTrendRows.some((row) => row.answered > 0);
  }

  private findAreaResolution(source: any, label: string): any | null {
    if (!label) return null;
    return ((source || []) as any[]).find((item) => item?.label === label) || null;
  }

  private metricsAreaSource(source: any): Array<{ label: string; value: number; closed?: number; unresolved?: number; resolution_time_buckets?: any }> {
    const rows = ((source || []) as any[])
      .map((item) => ({
        label: item.label || '(vacío)',
        value: Number(item.value) || 0,
        closed: Number(item.closed) || 0,
        unresolved: Number(item.unresolved) || 0,
        resolution_time_buckets: item.resolution_time_buckets || null,
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    return this.selectedMetricsArea ? rows.filter((item) => item.label === this.selectedMetricsArea) : rows;
  }

  get metricsResolvedPendingTitle(): string {
    const scope = [this.selectedMetricsMonth?.label || 'Total', this.selectedMetricsArea].filter(Boolean).join(' · ');
    return `Resueltos / pendientes · ${scope}`;
  }

  get metricsResolvedPendingTotal(): number {
    return this.metricsResolvedPendingSlices.reduce((sum, slice) => sum + slice.value, 0);
  }

  get metricsResolvedPendingSlices(): MetricsAreaSlice[] {
    const rows = this.metricsAreaSource(this.selectedMetricsMonth?.area_resolution_distribution || this.periodCards?.area_resolution_distribution)
      .map((item) => ({ ...item, resolved: item.closed || 0, unresolved: item.unresolved || 0 }));
    const visible = rows.slice(0, 5);
    const others = rows.slice(5).reduce((acc, item) => ({
      label: 'Otras áreas',
      value: acc.value + item.value,
      resolved: acc.resolved + item.resolved,
      unresolved: acc.unresolved + item.unresolved,
    }), { label: 'Otras áreas', value: 0, resolved: 0, unresolved: 0 });
    const compact = others.value ? [...visible, others] : visible;
    const total = Math.max(1, compact.reduce((sum, item) => sum + item.value, 0));
    const colors = ['#009cb1', '#da291c', '#5d7cfa', '#1a9a75', '#d89a13', '#8a94a6'];
    return compact.map((item, index) => ({
      ...item,
      pct: Math.round((item.value / total) * 100),
      barPct: (item.value / total) * 100,
      resolvedPct: item.value ? (item.resolved / item.value) * 100 : 0,
      unresolvedPct: item.value ? (item.unresolved / item.value) * 100 : 0,
      color: colors[index % colors.length],
    }));
  }

  get metricsResolvedPendingPieBackground(): string {
    const slices = this.metricsResolvedPendingSlices;
    if (!slices.length) return 'conic-gradient(var(--metrics-line) 0 100%)';
    const total = Math.max(1, slices.reduce((sum, slice) => sum + slice.value, 0));
    let cursor = 0;
    const parts = slices.map((slice) => {
      const start = cursor;
      cursor += (slice.value / total) * 100;
      return `${slice.color} ${start.toFixed(2)}% ${cursor.toFixed(2)}%`;
    });
    return `conic-gradient(${parts.join(', ')})`;
  }

  get metricsAreaDistributionTitle(): string {
    const scope = [this.selectedMetricsMonth?.label || 'Total', this.selectedMetricsArea].filter(Boolean).join(' · ');
    return `Distribución por área · ${scope}`;
  }

  get metricsAreaDistributionTotal(): number {
    return this.metricsAreaDistributionSlices.reduce((sum, slice) => sum + slice.value, 0);
  }

  get metricsAreaDistributionSlices(): MetricsAreaSlice[] {
    const source = this.selectedMetricsMonth?.area_distribution || this.periodCards?.area_distribution || [];
    const rows = this.metricsAreaSource(source).map((item) => ({ label: item.label, value: item.value }));
    const visible = rows.slice(0, 5);
    const others = rows.slice(5).reduce((sum, item) => sum + item.value, 0);
    const compact = others ? [...visible, { label: 'Otras áreas', value: others }] : visible;
    const total = Math.max(1, compact.reduce((sum, item) => sum + item.value, 0));
    const colors = ['#009cb1', '#da291c', '#5d7cfa', '#1a9a75', '#d89a13', '#8a94a6'];
    return compact.map((item, index) => ({
      ...item,
      pct: Math.round((item.value / total) * 100),
      barPct: (item.value / total) * 100,
      color: colors[index % colors.length],
    }));
  }

  get metricsAreaPieBackground(): string {
    const slices = this.metricsAreaDistributionSlices;
    if (!slices.length) return 'conic-gradient(var(--metrics-line) 0 100%)';
    const total = Math.max(1, slices.reduce((sum, slice) => sum + slice.value, 0));
    let cursor = 0;
    const parts = slices.map((slice) => {
      const start = cursor;
      cursor += (slice.value / total) * 100;
      return `${slice.color} ${start.toFixed(2)}% ${cursor.toFixed(2)}%`;
    });
    return `conic-gradient(${parts.join(', ')})`;
  }

  selectMetricsMonth(key: string): void {
    this.selectedMetricsMonthKey = this.selectedMetricsMonthKey === key ? '' : key;
  }

  selectMetricsArea(label: string): void {
    if (!label || label === 'Otras áreas') return;
    this.selectedMetricsArea = this.selectedMetricsArea === label ? '' : label;
  }

  get selectedDocumentAreaDetail(): any {
    if (!this.selectedDocumentArea) return null;
    return this.documents?.area_detail?.[this.selectedDocumentArea] || null;
  }

  get pendingValidationRows(): any[] {
    const rows = this.documents?.pending_validation?.rows || [];
    if (this.selectedDocValidationFilter === 'all') return rows;
    return rows.filter((row: any) => {
      if (this.selectedDocValidationFilter === 'alta') return row.Confianza === 'Alta';
      if (this.selectedDocValidationFilter === 'media') return row.Confianza === 'Media';
      if (this.selectedDocValidationFilter === 'baja') return row.Confianza === 'Baja';
      if (this.selectedDocValidationFilter === 'revision') return row['Requiere revisión humana'] === 'Sí';
      if (this.selectedDocValidationFilter === 'seguridad') return row['Área actual'] === 'Seguridad Ocupacional' && row['Área sugerida'] !== 'Seguridad Ocupacional';
      if (this.selectedDocValidationFilter === 'sin_clasificar') return row['Área actual'] === 'Sin clasificar / Por asignar' || row['Área sugerida'] === 'Sin clasificar / Por asignar';
      return true;
    });
  }

  get pendingValidationFilterDefs(): Array<{ label: string; value: string; count: number }> {
    const rows = this.documents?.pending_validation?.rows || [];
    const count = (fn: (row: any) => boolean) => rows.filter(fn).length;
    return [
      { label: 'Todas', value: 'all', count: rows.length },
      { label: 'Alta confianza', value: 'alta', count: count((r: any) => r.Confianza === 'Alta') },
      { label: 'Media confianza', value: 'media', count: count((r: any) => r.Confianza === 'Media') },
      { label: 'Baja confianza', value: 'baja', count: count((r: any) => r.Confianza === 'Baja') },
      { label: 'Requiere revisión', value: 'revision', count: count((r: any) => r['Requiere revisión humana'] === 'Sí') },
      { label: 'Riesgo Seguridad', value: 'seguridad', count: count((r: any) => r['Área actual'] === 'Seguridad Ocupacional' && r['Área sugerida'] !== 'Seguridad Ocupacional') },
      { label: 'Sin clasificar', value: 'sin_clasificar', count: count((r: any) => r['Área actual'] === 'Sin clasificar / Por asignar' || r['Área sugerida'] === 'Sin clasificar / Por asignar') },
    ];
  }

  get freshdeskRows(): any[] {
    return this.documents?.freshdesk?.rows || [];
  }

  get freshdeskPreviewRows(): any[] {
    return this.getUnifiedOfficialRows().filter((row: any) => row['Origen levantamiento'] === 'Freshdesk');
  }

  get docFilteredFreshdeskCategoria(): string[] {
    return [...new Set(this.freshdeskRows.map((r: any) => r['Categoría sugerida']).filter(Boolean))].sort();
  }

  get docFilteredFreshdeskArea(): string[] {
    return [...new Set(this.freshdeskRows.map((r: any) => r['Área sugerida']).filter(Boolean))].sort();
  }

  get docFilteredFreshdeskConfianza(): string[] {
    return [...new Set(this.freshdeskRows.map((r: any) => r.Confianza).filter(Boolean))].sort();
  }

  get classificationRiskItems(): any[] {
    return this.documents?.classification_risks?.items || [];
  }

  get duplicateMap(): any[] {
    return this.documents?.duplicate_map || [];
  }

  get duplicateAreaSummary(): any[] {
    return this.documents?.duplicate_area_summary || [];
  }

  get duplicateReviewSummary(): any {
    return this.documents?.duplicate_review_summary || {};
  }

  get activeDuplicateArea(): string {
    return this.selectedDuplicateArea || this.duplicateAreaSummary[0]?.area || '';
  }

  get filteredDuplicateMap(): any[] {
    const area = this.activeDuplicateArea;
    if (!area) return this.duplicateMap;
    return this.duplicateMap.filter((group: any) => (group.items || []).some((item: any) => item.area === area));
  }

  get selectedDuplicateGroup(): any {
    return this.filteredDuplicateMap.find((group: any) => group.clave === this.selectedDuplicateGroupKey) || this.filteredDuplicateMap[0] || null;
  }

  selectDuplicateArea(area: string): void {
    this.selectedDuplicateArea = area;
    this.selectedDuplicateGroupKey = '';
    this.cdr.detectChanges();
  }

  selectDuplicateGroup(key: string): void {
    this.selectedDuplicateGroupKey = this.selectedDuplicateGroupKey === key ? '' : key;
    this.cdr.detectChanges();
  }

  duplicateReviewOptionLabel(value: string): string {
    return this.duplicateReviewOptions.find((option) => option.value === value)?.label || 'Pendiente';
  }

  duplicateReviewClass(item: any): string {
    return item?.estado_revision_codigo || 'pendiente';
  }

  async setDuplicateReview(group: any, item: any, status: string): Promise<void> {
    const itemKey = item?.item_key;
    if (!itemKey || this.duplicateReviewSavingKey) return;
    this.duplicateReviewSavingKey = itemKey;
    this.documentsError = '';
    this.cdr.detectChanges();
    try {
      const response = await this.postJson(`${this.apiBase}/api/documents/duplicate_review`, {
        group_key: group?.clave || '',
        item_key: itemKey,
        status,
      });
      this.documents = response;
      this.rebuildVisibleDocRows();
    } catch (error) {
      this.documentsError = error instanceof Error ? error.message : 'No se pudo guardar la revisión del duplicado';
    } finally {
      this.duplicateReviewSavingKey = '';
      this.cdr.detectChanges();
    }
  }

  selectDocumentArea(area: string): void {
    this.selectedDocumentArea = this.selectedDocumentArea === area ? '' : area;
    this.cdr.detectChanges();
  }

  selectDocValidationFilter(value: string): void {
    this.selectedDocValidationFilter = value;
    this.cdr.detectChanges();
  }

  toggleDocFilter(arr: string[], value: string): void {
    const idx = arr.indexOf(value);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(value);
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  docFilterActive(key: string): boolean {
    const map: Record<string, string[]> = {
      estado: this.docFilterEstado, tipo: this.docFilterTipo,
      grupo: this.docFilterGrupo, responsable: this.docFilterResponsable,
      prioridad: this.docFilterPrioridad,
      freshdesk_estado: this.docFilterFreshdeskEstado,
    };
    return (map[key]?.length || 0) > 0;
  }

  clearDocFilters(): void {
    this.docFilterEstado = [];
    this.docFilterTipo = [];
    this.docFilterGrupo = [];
    this.docFilterResponsable = [];
    this.docFilterPrioridad = [];
    this.docFilterFreshdeskEstado = [];
    this.docFilterBusqueda = '';
    this.docFilterAntiguedad = '';
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  resetDocumentFilters(): void {
    this.docFilterFuente = 'todas';
    this.selectedDocKpi = '';
    this.selectedDocValidationFilter = 'all';
    this.selectedDocumentArea = '';
    this.selectedDuplicateArea = '';
    this.selectedDuplicateGroupKey = '';
    this.clearDocFilters();
  }

  setDocFuente(fuente: 'todas' | 'oficial' | 'sugerencia'): void {
    this.docFilterFuente = fuente;
    this.selectedDocKpi = '';
    this.clearDocFilters();
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  applyDocKpi(key: string): void {
    if (this.selectedDocKpi === key) {
      this.selectedDocKpi = '';
      this.docFilterFuente = 'todas';
      this.clearDocFilters();
      this.rebuildVisibleDocRows();
      this.cdr.detectChanges();
      return;
    }
    this.selectedDocKpi = key;
    this.clearDocFilters();
    this.selectedDocValidationFilter = 'all';
    this.docFilterFuente = 'todas';
    this.rebuildVisibleDocRows();
    this.cdr.detectChanges();
  }

  get docActiveRows(): any[] {
    if (!this.visibleDocRows.length && this.documents) {
      this.rebuildVisibleDocRows();
    }
    return this.visibleDocRows;
  }

  private rebuildVisibleDocRows(): void {
    this.visibleDocRows = this.computeDocRows();
  }

  private mapFreshdeskAsOfficial(r: any): any {
    return {
      ...r,
      'Fuente registro': 'Excel oficial',
      'Origen levantamiento': 'Freshdesk',
      ID: r['ID coincidencia Excel'] || '',
      Documento: r['Nombre archivo'],
      'Área oficial': r['Área sugerida'] || '',
      'Área sugerida': r['Área sugerida'],
      Estado: r['Estado evaluación Freshdesk'] || 'Pendiente evaluación',
      'Tipo estimado': r['Categoría sugerida'] || r['Tipo archivo'],
      Grupo: r['Área sugerida'] || 'Sin clasificar / Por asignar',
      Responsable: '',
      Prioridad: r.Confianza || '',
      'Proceso oficial': r['Acción sugerida'] || '',
      'Ruta completa': r['Ruta completa'],
    };
  }

  private mapDocumentSheetRowAsOfficial(r: any): any {
    const source = r['Fuente registro'] || 'Excel oficial';
    const isFreshdesk = this.docNormalizeLabel(source) === 'freshdesk candidato';
    const isSuggestion = this.docNormalizeLabel(source) === 'propuesta reclasificacion';
    return {
      ...r,
      'Fuente registro': 'Excel oficial',
      'Origen levantamiento': isFreshdesk ? 'Freshdesk' : (isSuggestion ? 'Propuesta reclasificación' : 'Excel'),
      ID: r.ID || r['ID coincidencia Excel'] || '',
      Documento: r.Documento || r['Nombre del archivo'] || r['Nombre archivo'] || '',
      'Área oficial': r['Área oficial'] || r['Área oficial sugerida'] || r['Área sugerida'] || r['Área oficial actual'] || '',
      'Área sugerida': r['Área sugerida'] || r['Área oficial sugerida'] || '',
      Estado: r.Estado || r['Estado validación propuesta'] || r['Estado evaluación Freshdesk'] || 'Pendiente',
      'Tipo estimado': r['Tipo estimado'] || r['Categoría sugerida'] || r['Tipo de archivo'] || r['Tipo archivo'] || '',
      Grupo: r.Grupo || r['Área oficial'] || r['Área sugerida'] || r['Area / carpeta'] || 'Sin clasificar / Por asignar',
      Responsable: r.Responsable || r['Responsable área'] || '',
      Prioridad: r.Prioridad || r.Confianza || r['Confianza sugerida'] || '',
      'Proceso oficial': r['Proceso oficial'] || r['Acción sugerida'] || r['Acción requerida'] || r['Fuente de señal usada'] || '',
      'Ruta completa': r['Ruta completa'] || '',
    };
  }

  private getUnifiedOfficialRows(): any[] {
    const sheetRows = this.documents?.document_rows_active || this.documents?.document_rows || [];
    if (sheetRows.length) {
      return sheetRows.map((r: any) => this.mapDocumentSheetRowAsOfficial(r));
    }
    return [
      ...(this.documents?.documents || []).map((r: any) => ({ ...r, 'Fuente registro': r['Fuente registro'] || 'Excel oficial', 'Origen levantamiento': r['Origen levantamiento'] || 'Excel' })),
      ...(this.documents?.freshdesk?.rows || []).map((r: any) => this.mapFreshdeskAsOfficial(r)),
    ];
  }

  private applyOfficialKpiFilter(rows: any[]): any[] {
    if (this.selectedDocKpi === 'pendientes_revision') return rows.filter((r: any) => this.docNormalizeLabel(r.Estado).includes('pendiente'));
    if (this.selectedDocKpi === 'revisados_iniciales') return rows.filter((r: any) => r.Estado === 'Revisado inicial');
    if (this.selectedDocKpi === 'politicas_rh') return rows.filter((r: any) => this.docNormalizeLabel(this.getCanonicalDocType(r)).includes('politica'));
    if (this.selectedDocKpi === 'procedimientos') return rows.filter((r: any) => ['procedimiento', 'instructivo'].some(term => this.docNormalizeLabel(this.getCanonicalDocType(r)).includes(term)));
    if (this.selectedDocKpi === 'desactualizados') return rows.filter((r: any) => this.isDocDesactualizado(r));
    if (this.selectedDocKpi === 'listos_ia') return rows.filter((r: any) => ['listo para ia', 'completado', 'preparado'].includes(this.docNormalizeLabel(r['Estado IA'])) || this.docNormalizeLabel(r.Estado) === 'listo para ia');
    if (this.selectedDocKpi === 'obsoletos') return rows.filter((r: any) => this.docNormalizeLabel(r.Estado) === 'obsoleto');
    if (this.selectedDocKpi === 'freshdesk_integrado') return rows.filter((r: any) => r['Origen levantamiento'] === 'Freshdesk');
    if (this.selectedDocKpi === 'sugerencias') return rows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación');
    if (this.selectedDocKpi === 'alta_confianza') return rows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación' && this.docNormalizeLabel(r.Confianza) === 'alta');
    if (this.selectedDocKpi === 'media_confianza') return rows.filter((r: any) => r['Origen levantamiento'] === 'Propuesta reclasificación' && this.docNormalizeLabel(r.Confianza) === 'media');
    if (this.selectedDocKpi === 'falsos_positivos') return rows.filter((r: any) => r['Área oficial actual'] === 'Seguridad Ocupacional' && r['Área oficial sugerida'] !== 'Seguridad Ocupacional');
    if (this.selectedDocKpi === 'duplicados_revisados') return rows.filter((r: any) => this.isDocDuplicateReviewed(r));
    if (this.selectedDocKpi === 'duplicados_confirmados') return rows.filter((r: any) => this.isDocDuplicateConfirmed(r));
    if (this.selectedDocKpi === 'duplicados') return rows.filter((r: any) => this.isDocDuplicado(r));
    if (this.selectedDocKpi === 'sugerencias_automatizacion') return rows.filter((r: any) => r['Sugerencia automatización'] && r['Sugerencia automatización'] !== '(vacío)');
    return rows;
  }

  private computeDocRows(): any[] {
    const searchNorm = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    if (this.docFilterFuente === 'todas') {
      let rows = this.getUnifiedOfficialRows();
      rows = this.applyOfficialKpiFilter(rows);
      if (this.docFilterEstado.length) rows = rows.filter((r: any) => this.docFilterEstado.includes(r.Estado));
      if (this.docFilterTipo.length) rows = rows.filter((r: any) => this.docFilterTipo.some(tipo => this.docNormalizeLabel(tipo) === this.docNormalizeLabel(this.getCanonicalDocType(r))));
      if (this.docFilterGrupo.length) rows = rows.filter((r: any) => this.docFilterGrupo.includes(r.Grupo));
      if (this.docFilterResponsable.length) rows = rows.filter((r: any) => this.docFilterResponsable.includes(r.Responsable));
      if (this.docFilterPrioridad.length) rows = rows.filter((r: any) => this.docFilterPrioridad.includes(r.Prioridad));
      if (this.docFilterBusqueda) {
        const term = searchNorm(this.docFilterBusqueda);
        rows = rows.filter((r: any) =>
          [r.Documento, r.ID, r['Área oficial'], r['Área sugerida'], r.Estado, r['Tipo estimado'], r.Grupo, r.Responsable, r['Acción sugerida'], r['Proceso oficial']].some((f: any) =>
            searchNorm(String(f || '')).includes(term)
          )
        );
      }
      return rows;
    }

    if (this.docFilterFuente === 'oficial') {
      let rows = this.applyOfficialKpiFilter(this.getUnifiedOfficialRows());
      if (this.docFilterEstado.length) rows = rows.filter((r: any) => this.docFilterEstado.includes(r.Estado));
      if (this.docFilterTipo.length) rows = rows.filter((r: any) => this.docFilterTipo.some(tipo => this.docNormalizeLabel(tipo) === this.docNormalizeLabel(this.getCanonicalDocType(r))));
      if (this.docFilterGrupo.length) rows = rows.filter((r: any) => this.docFilterGrupo.includes(r.Grupo));
      if (this.docFilterResponsable.length) rows = rows.filter((r: any) => this.docFilterResponsable.includes(r.Responsable));
      if (this.docFilterPrioridad.length) rows = rows.filter((r: any) => this.docFilterPrioridad.includes(r.Prioridad));
      if (this.docFilterBusqueda) {
        const term = searchNorm(this.docFilterBusqueda);
        rows = rows.filter((r: any) =>
          [r.Documento, r.ID, r['Área oficial'], r['Área sugerida'], r.Estado, r['Tipo estimado'], r.Grupo, r.Responsable, r['Acción sugerida'], r['Proceso oficial']].some((f: any) =>
            searchNorm(String(f || '')).includes(term)
          )
        );
      }
      return rows;
    }

    if (this.docFilterFuente === 'sugerencia') {
      let rows = this.documents?.pending_validation?.rows || [];
      if (this.selectedDocKpi === 'alta_confianza') {
        rows = rows.filter((r: any) => r.Confianza === 'Alta');
      } else if (this.selectedDocKpi === 'media_confianza') {
        rows = rows.filter((r: any) => r.Confianza === 'Media');
      } else if (this.selectedDocKpi === 'falsos_positivos') {
        rows = rows.filter((r: any) => r['Área actual'] === 'Seguridad Ocupacional' && r['Área sugerida'] !== 'Seguridad Ocupacional');
      } else if (this.selectedDocKpi === 'sugerencias') {
        // all suggestions
      }
      if (this.docFilterBusqueda) {
        const term = searchNorm(this.docFilterBusqueda);
        rows = rows.filter((r: any) =>
          [r['Documento'], r['ID'], r['Área actual'], r['Área sugerida']].some((f: any) =>
            searchNorm(String(f || '')).includes(term)
          )
        );
      }
      return rows;
    }

    return this.docFilteredQueue;
  }

  isDocDuplicado(row: any): boolean {
    return !!row['Duplicado / versión'];
  }

  private docDuplicateReviewCode(row: any): string {
    const values = [
      row?.['Estado'],
      row?.['Duplicado / versión'],
      row?.['Decisión usuario'],
      row?.['estado_revision'],
      row?.['estado_revision_codigo'],
    ].map(value => this.docNormalizeLabel(value));
    if (values.some(value => value.includes('no correspondia') || value.includes('no_corresponde'))) return 'no_corresponde';
    if (values.some(value => value.includes('unica vigente') || value.includes('vigente_unica'))) return 'vigente_unica';
    if (values.some(value => value.includes('duplicado_version') || value.includes('duplicado / version anterior') || (value.includes('duplicado') && value.includes('anterior')))) return 'duplicado_version';
    return '';
  }

  isDocDuplicateReviewed(row: any): boolean {
    return !!this.docDuplicateReviewCode(row);
  }

  isDocDuplicateConfirmed(row: any): boolean {
    return this.docDuplicateReviewCode(row) === 'duplicado_version';
  }

  isDocDesactualizado(row: any): boolean {
    const raw = row['Meses sin actualización'] || '';
    const match = String(raw).match(/(\d+)/);
    if (!match) return false;
    return parseInt(match[1], 10) >= 12;
  }

  docKpiLabel(key: string): string {
    return this.docUnifiedKpis.find(k => k.key === key)?.label || key;
  }

  openDocumentDetail(row: any): void {
    this.selectedDocument = row;
  }

  closeDocumentDetail(): void {
    this.selectedDocument = null;
  }

  get docCalendarLink(): any[] {
    if (!this.selectedDocument || !this.documents?.calendar_links) return [];
    return this.documents.calendar_links[this.selectedDocument['ID']] || [];
  }

  private startScheduleAutoRefresh(): void {
    if (this.scheduleAutoRefreshTimer) return;
    this.scheduleAutoRefreshTimer = setInterval(() => {
      if (this.activeView !== 'schedule' || this.scheduleLoading) return;
      void this.refreshScheduleIfChanged();
    }, 30000);
  }

  private async refreshScheduleIfChanged(): Promise<void> {
    try {
      const nextSchedule = await this.fetchJson(`${this.apiBase}/api/schedule`);
      const nextSignature = this.buildScheduleSignature(nextSchedule);
      if (!this.schedule || nextSignature !== this.scheduleSignature) {
        this.schedule = nextSchedule;
        this.scheduleSignature = nextSignature;
        this.scheduleError = '';
        this.syncScheduleRemunerationMonthSelection();
        this.cdr.detectChanges();
      }
    } catch (error) {
      this.scheduleError = error instanceof Error ? error.message : 'No se pudo actualizar cronograma';
      this.cdr.detectChanges();
    }
  }

  private buildScheduleSignature(schedule: any): string {
    return [
      schedule?.file_name || '',
      schedule?.updated_at || '',
      schedule?.total_activities || 0,
      schedule?.total_milestones || 0,
      schedule?.status_totals?.done || 0,
      schedule?.status_totals?.pending || 0,
      schedule?.status_totals?.other || 0,
    ].join('|');
  }

  async loadSatisfaction(): Promise<void> {
    this.satisfactionLoading = true;
    this.satisfactionError = '';
    this.cdr.detectChanges();
    try {
      const result = await this.fetchJson(`${this.apiBase}/api/satisfaction?mes_data=${this.satisfactionMonths}`);
      if (result && result.success === false) {
        const hasCachedItems = Array.isArray(this.satisfaction?.items) && this.satisfaction.items.length > 0;
        if (!hasCachedItems) {
          this.satisfaction = result;
        }
        this.satisfactionError = result.error || 'No se pudo cargar la encuesta';
      } else {
        this.satisfaction = result;
        this.satInvalidateCache();
        if (!this.satisfactionFilterActive) {
          this.resetSatisfactionToCurrentPeriod(false);
        }
        this.saveSatisfactionCache();
      }
    } catch (error) {
      this.satisfactionError = error instanceof Error ? error.message : 'No se pudo cargar encuesta satisfacción';
    } finally {
      this.satisfactionLoading = false;
      this.cdr.detectChanges();
    }
  }

  private restoreSatisfactionCache(): void {
    try {
      const raw = localStorage.getItem('bi-satisfaction-v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.items) {
          this.satisfaction = parsed;
          this.satInvalidateCache();
        }
      }
      const ts = localStorage.getItem('bi-satisfaction-updated-at');
      if (ts) this.satisfactionUpdatedAt = ts;
      const filters = localStorage.getItem('bi-satisfaction-filters');
      if (filters) {
        const parsed = JSON.parse(filters);
        this.selectedSatisfactionYear = parsed.year || '';
        this.selectedSatisfactionMonth = parsed.month || '';
        this.selectedSatisfactionArea = parsed.area || '';
        this.selectedSatisfactionResponsable = parsed.responsable || '';
        this.selectedSatisfactionPeriod = parsed.period || '';
        this.selectedSatisfactionScore = parsed.score || '';
        this.selectedSatisfactionCategory = parsed.category || '';
        this.selectedSatisfactionWindow = parsed.window || 'last-month';
        this.satisfactionMonths = parsed.months ?? 13;
      } else if (this.satisfaction?.items?.length) {
        this.resetSatisfactionToCurrentPeriod();
      }
    } catch {
      // ignore corrupt cache
    }
  }

  private saveSatisfactionFiltersCache(): void {
    try {
      localStorage.setItem('bi-satisfaction-filters', JSON.stringify({
        year: this.selectedSatisfactionYear,
        month: this.selectedSatisfactionMonth,
        area: this.selectedSatisfactionArea,
        responsable: this.selectedSatisfactionResponsable,
        period: this.selectedSatisfactionPeriod,
        score: this.selectedSatisfactionScore,
        category: this.selectedSatisfactionCategory,
        window: this.selectedSatisfactionWindow,
        months: this.satisfactionMonths,
      }));
    } catch {
      // ignore storage errors
    }
  }

  private saveSatisfactionCache(): void {
    try {
      if (this.satisfaction) {
        localStorage.setItem('bi-satisfaction-v1', JSON.stringify(this.satisfaction));
      }
      this.satisfactionUpdatedAt = new Date().toISOString();
      localStorage.setItem('bi-satisfaction-updated-at', this.satisfactionUpdatedAt);
      this.saveSatisfactionFiltersCache();
    } catch {
      // ignore storage errors
    }
  }

  async loadFreshdesk(): Promise<void> {
    this.freshdeskLoading = true;
    this.freshdeskError = '';
    this.cdr.detectChanges();
    try {
      const params = new URLSearchParams({
        page: String(this.freshdeskPage),
        per_page: String(this.freshdeskPerPage),
      });
      this.freshdesk = await this.fetchJson(`${this.apiBase}/api/freshdesk/tickets?${params}`);
    } catch (error) {
      this.freshdeskError = error instanceof Error ? error.message : 'No se pudo cargar Freshdesk';
    } finally {
      this.freshdeskLoading = false;
      this.cdr.detectChanges();
    }
  }

  setView(view: AppView): void {
    this.activeView = view;
    this.viewMenuOpen = false;
    this.openFilter = null;
    this.closeFiltersPanel();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (view === 'schedule') {
      this.scheduleSubView = 'overview';
      this.clearScheduleFilters();
    }
    if (view === 'schedule' && !this.schedule && !this.scheduleLoading) {
      void this.loadSchedule();
    }
    if (view === 'freshdesk' && !this.freshdesk && !this.freshdeskLoading) {
      void this.loadFreshdesk();
    }
    if (view === 'documents') {
      this.docFilterFuente = 'todas';
      this.selectedDocKpi = '';
      this.selectedDocValidationFilter = 'all';
      this.clearDocFilters();
    }
    if (view === 'documents' && !this.documents && !this.documentsLoading) {
      void this.loadDocuments();
    }
    if (view === 'metrics') {
      if (!this.documents && !this.documentsLoading) void this.loadDocuments();
      if (!this.satisfaction && !this.satisfactionLoading) void this.loadSatisfaction();
      if (!this.schedule && !this.scheduleLoading) void this.loadSchedule();
      if (!this.summary && !this.loading) void this.loadData();
    }
  }

  openDashboardFilters(): void {
    if (this.activeView !== 'dashboard') {
      this.setView('dashboard');
    }
    this.toggleFiltersPanel();
  }

  toggleViewMenu(): void {
    this.viewMenuOpen = !this.viewMenuOpen;
  }

  selectView(view: AppView): void {
    this.setView(view);
  }

  async refreshActiveView(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.activeRefreshLoading) return;
    this.activeRefreshLoading = true;
    this.cdr.detectChanges();
    try {
      if (this.activeView === 'metrics') {
        clearTimeout((this as any)._refreshTimer);
        await Promise.all([this.loadData(true), this.loadSatisfaction()]);
        return;
      }
      if (this.activeView === 'dashboard' || this.activeView === 'operations') {
        clearTimeout((this as any)._refreshTimer);
        await this.loadData(true);
        return;
      }
      if (this.activeView === 'schedule') {
        await this.loadSchedule(true);
        return;
      }
      if (this.activeView === 'satisfaction') {
        await this.loadSatisfaction();
        return;
      }
      if (this.activeView === 'freshdesk') {
        await this.loadFreshdesk();
        return;
      }
      if (this.activeView === 'documents') {
        await this.loadDocuments(true);
        this.rebuildVisibleDocRows();
      }
    } finally {
      this.activeRefreshLoading = false;
      this.cdr.detectChanges();
    }
  }

  setFreshdeskPage(delta: number): void {
    this.freshdeskPage = Math.max(1, this.freshdeskPage + delta);
    void this.loadFreshdesk();
  }

  setFreshdeskPerPage(value: number | string): void {
    const parsed = Number(value);
    this.freshdeskPerPage = Math.max(1, Math.min(100, Number.isFinite(parsed) ? parsed : 30));
    this.freshdeskPage = 1;
    void this.loadFreshdesk();
  }

  get freshdeskItems(): any[] {
    return this.freshdesk?.items || [];
  }

  get freshdeskSummary(): any {
    return this.freshdesk?.summary || {};
  }

  setSatisfactionMonths(value: number | string): void {
    const parsed = Number(value);
    this.satisfactionMonths = Math.max(1, Math.min(13, Number.isFinite(parsed) ? parsed : 13));
    this.resetSatisfactionResponsesPage();
    this.saveSatisfactionFiltersCache();
  }

  clearSatisfactionFilters(persist = true): void {
    this.selectedSatisfactionArea = '';
    this.selectedSatisfactionResponsable = '';
    this.selectedSatisfactionPeriod = '';
    this.selectedSatisfactionYear = '';
    this.selectedSatisfactionMonth = '';
    this.selectedSatisfactionScore = '';
    this.selectedSatisfactionCategory = '';
    this.selectedSatisfactionWindow = 'all';
    this.resetSatisfactionResponsesPage();
    if (persist) this.saveSatisfactionFiltersCache();
  }

  resetSatisfactionToCurrentPeriod(persist = true): void {
    this.clearSatisfactionFilters(false);
    const latestPeriod = this.latestSatisfactionPeriod;
    if (latestPeriod) {
      this.selectedSatisfactionYear = latestPeriod.slice(0, 4);
      this.selectedSatisfactionMonth = latestPeriod.slice(5, 7);
    }
    this.selectedSatisfactionWindow = 'last-month';
    this.satisfactionTrendHoverIndex = null;
    this.selectedSatisfactionTrendMetric = '';
    this.satisfactionTrendWindow = 3;
    this.satisfactionTrendCacheKey = '';
    this.satisfactionTrendCache = null;
    this.resetSatisfactionResponsesPage();
    if (persist) this.saveSatisfactionFiltersCache();
  }

  setSatisfactionWindow(value: SatisfactionPeriodWindow): void {
    if (this.selectedSatisfactionWindow === value && !this.selectedSatisfactionYear && !this.selectedSatisfactionMonth && !this.selectedSatisfactionPeriod) return;
    this.selectedSatisfactionWindow = value;
    this.selectedSatisfactionPeriod = '';
    this.selectedSatisfactionYear = '';
    this.selectedSatisfactionMonth = '';
    this.satisfactionTrendHoverIndex = null;
    this.resetSatisfactionResponsesPage();
    this.satInvalidateFilteredCache();
    this.saveSatisfactionFiltersCache();
  }

  setSatisfactionSelectFilter(kind: 'year' | 'month' | 'area' | 'responsable' | 'score', value: string): void {
    if (kind === 'year') this.selectedSatisfactionYear = value;
    if (kind === 'month') this.selectedSatisfactionMonth = value;
    if (kind === 'area') this.selectedSatisfactionArea = value;
    if (kind === 'responsable') this.selectedSatisfactionResponsable = value;
    if (kind === 'score') this.selectedSatisfactionScore = value;
    if (kind === 'year' || kind === 'month') {
      this.selectedSatisfactionWindow = 'all';
      this.selectedSatisfactionPeriod = '';
    }
    this.resetSatisfactionResponsesPage();
    this.saveSatisfactionFiltersCache();
  }

  selectSatisfactionFilter(kind: 'area' | 'responsable' | 'period', value: string): void {
    if (kind === 'area') {
      this.selectedSatisfactionArea = this.selectedSatisfactionArea === value ? '' : value;
      this.resetSatisfactionResponsesPage();
      this.saveSatisfactionFiltersCache();
      return;
    }
    if (kind === 'responsable') {
      this.selectedSatisfactionResponsable = this.selectedSatisfactionResponsable === value ? '' : value;
      this.resetSatisfactionResponsesPage();
      this.saveSatisfactionFiltersCache();
      return;
    }
    this.selectedSatisfactionPeriod = this.selectedSatisfactionPeriod === value ? '' : value;
    if (this.selectedSatisfactionPeriod) {
      this.selectedSatisfactionWindow = 'all';
    }
    this.resetSatisfactionResponsesPage();
    this.saveSatisfactionFiltersCache();
  }

  selectSatisfactionCategory(category: '' | 'promoters' | 'passives' | 'detractors'): void {
    this.selectedSatisfactionCategory = this.selectedSatisfactionCategory === category && category ? '' : category;
    this.resetSatisfactionResponsesPage();
    this.saveSatisfactionFiltersCache();
  }

  selectSatisfactionTrendMetric(metric: SatisfactionTrendMetric): void {
    this.selectedSatisfactionTrendMetric = this.selectedSatisfactionTrendMetric === metric ? '' : metric;
  }

  selectSatisfactionScore(score: number | string): void {
    const value = String(score);
    this.selectedSatisfactionScore = this.selectedSatisfactionScore === value ? '' : value;
    this.resetSatisfactionResponsesPage();
    this.saveSatisfactionFiltersCache();
  }

  selectSatisfactionResponseSegment(segment: 'promoters' | 'passives' | 'detractors'): void {
    this.selectedSatisfactionResponseSegment = this.selectedSatisfactionResponseSegment === segment ? '' : segment;
    this.resetSatisfactionResponsesPage();
  }

  private resetSatisfactionResponsesPage(): void {
    this.satisfactionResponsesPage = 0;
  }

  showSatisfactionTrendMetric(metric: SatisfactionTrendMetric): boolean {
    return !this.selectedSatisfactionTrendMetric || this.selectedSatisfactionTrendMetric === metric;
  }

  setSatisfactionTrendWindow(value: SatisfactionTrendWindow): void {
    this.satisfactionTrendWindow = value;
    this.satisfactionTrendHoverIndex = null;
    this.satisfactionTrendCacheKey = '';
    this.satisfactionTrendCache = null;
  }

  get satisfactionItems(): any[] {
    return this.satisfaction?.items || [];
  }

  get satisfactionWindowLabel(): string {
    return this.satisfactionWindowOptions.find((option) => option.value === this.selectedSatisfactionWindow)?.label || 'Todos los periodos';
  }

  get satisfactionSummary(): any {
    this.satEnsureCacheFresh();
    if (this.satCachedSummary) return this.satCachedSummary;
    this.satCachedSummary = this.calculateSatisfactionSummary(this.filteredSatisfactionItems);
    return this.satCachedSummary;
  }

  get currentSatisfactionMonthKey(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  }

  get currentSatisfactionMonthLabel(): string {
    return this.metricsMonthLabel(this.currentSatisfactionMonthKey);
  }

  get satisfactionCurrentMonthItems(): any[] {
    return this.satisfactionItems.filter((item) => String(item.period || '').slice(0, 7) === this.currentSatisfactionMonthKey);
  }

  get metricsCurrentMonthSatisfactionItems(): any[] {
    return this.satisfactionCurrentMonthItems.filter((item) => {
      if (this.selectedMetricsArea && item.area !== this.selectedMetricsArea) return false;
      return true;
    });
  }

  get metricsCurrentMonthSatisfactionSummary(): any {
    return this.calculateSatisfactionSummary(this.metricsCurrentMonthSatisfactionItems);
  }

  get filteredSatisfactionItems(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedFilteredItems) return this.satCachedFilteredItems;
    this.satCachedFilteredItems = this.satisfactionItemsInWindow().filter((item) => this.matchesSatisfactionFilters(item));
    return this.satCachedFilteredItems;
  }

  private matchesSatisfactionFilters(item: any, ignore: 'area' | 'responsable' | 'period' | '' = ''): boolean {
      if (this.selectedSatisfactionArea && item.area !== this.selectedSatisfactionArea) return false;
      if (this.selectedSatisfactionResponsable && item.responsable !== this.selectedSatisfactionResponsable) return false;
      if (this.selectedSatisfactionPeriod && item.period !== this.selectedSatisfactionPeriod) return false;
      if (this.selectedSatisfactionYear && String(item.period || '').slice(0, 4) !== this.selectedSatisfactionYear) return false;
      if (this.selectedSatisfactionMonth && String(item.period || '').slice(5, 7) !== this.selectedSatisfactionMonth) return false;
      if (this.selectedSatisfactionScore && String(Math.round(Number(item.nota))) !== this.selectedSatisfactionScore) return false;
      if (this.selectedSatisfactionCategory && this.satisfactionCategory(item.nota) !== this.selectedSatisfactionCategory) return false;
      return true;
  }

  private satisfactionItemsForFilter(ignore: 'area' | 'responsable' | 'period'): any[] {
    return this.satisfactionItemsInWindow().filter((item) => {
      if (ignore !== 'area' && this.selectedSatisfactionArea && item.area !== this.selectedSatisfactionArea) return false;
      if (ignore !== 'responsable' && this.selectedSatisfactionResponsable && item.responsable !== this.selectedSatisfactionResponsable) return false;
      if (ignore !== 'period' && this.selectedSatisfactionPeriod && item.period !== this.selectedSatisfactionPeriod) return false;
      if (ignore !== 'period' && this.selectedSatisfactionYear && String(item.period || '').slice(0, 4) !== this.selectedSatisfactionYear) return false;
      if (ignore !== 'period' && this.selectedSatisfactionMonth && String(item.period || '').slice(5, 7) !== this.selectedSatisfactionMonth) return false;
      return true;
    });
  }

  get satisfactionRecentItems(): any[] {
    return this.satisfactionResponsesPageItems;
  }

  get sortedSatisfactionResponseItems(): any[] {
    this.satEnsureCacheFresh();
    const key = [
      this.satCacheKey,
      this.selectedSatisfactionResponseSegment,
      this.filteredSatisfactionItems.length,
    ].join('|');
    if (this.satCachedResponseItemsKey === key && this.satCachedResponseItems) return this.satCachedResponseItems;
    this.satCachedResponseItemsKey = key;
    this.satCachedResponseItems = this.filteredSatisfactionItems
      .filter((item) => !this.selectedSatisfactionResponseSegment || this.satisfactionCategory(item.nota) === this.selectedSatisfactionResponseSegment)
      .slice()
      .sort((a, b) => this.satisfactionResponseDateValue(b) - this.satisfactionResponseDateValue(a));
    return this.satCachedResponseItems;
  }

  get satisfactionResponsesTotalPages(): number {
    return Math.max(1, Math.ceil(this.sortedSatisfactionResponseItems.length / this.satisfactionResponsesPageSize));
  }

  get satisfactionResponsesPageItems(): any[] {
    const safePage = Math.min(this.satisfactionResponsesPage, this.satisfactionResponsesTotalPages - 1);
    if (safePage !== this.satisfactionResponsesPage) this.satisfactionResponsesPage = safePage;
    const start = safePage * this.satisfactionResponsesPageSize;
    return this.sortedSatisfactionResponseItems.slice(start, start + this.satisfactionResponsesPageSize);
  }

  get satisfactionResponsesPageNumbers(): number[] {
    const total = this.satisfactionResponsesTotalPages;
    const windowSize = 7;
    if (total <= windowSize) {
      return Array.from({ length: total }, (_, index) => index);
    }
    const half = Math.floor(windowSize / 2);
    const start = Math.max(0, Math.min(this.satisfactionResponsesPage - half, total - windowSize));
    return Array.from({ length: windowSize }, (_, index) => start + index);
  }

  get satisfactionResponsesPageStart(): number {
    if (!this.sortedSatisfactionResponseItems.length) return 0;
    return this.satisfactionResponsesPage * this.satisfactionResponsesPageSize + 1;
  }

  get satisfactionResponsesPageEnd(): number {
    return Math.min((this.satisfactionResponsesPage + 1) * this.satisfactionResponsesPageSize, this.sortedSatisfactionResponseItems.length);
  }

  goToSatisfactionResponsesPage(page: number): void {
    this.satisfactionResponsesPage = Math.max(0, Math.min(page, this.satisfactionResponsesTotalPages - 1));
  }

  private satisfactionResponseDateValue(item: any): number {
    const candidates = [item?.fecha, item?.created_at, item?.updated_at, item?.period];
    for (const candidate of candidates) {
      const text = String(candidate || '').trim();
      if (!text || text === '(vacío)') continue;
      const normalized = /^\d{4}-\d{2}$/.test(text) ? `${text}-01` : text;
      const parsed = new Date(normalized).getTime();
      if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
  }

  get satisfactionScoreOptions(): number[] {
    this.satEnsureCacheFresh();
    if (this.satCachedScoreOptions) return this.satCachedScoreOptions;
    this.satCachedScoreOptions = Array.from(new Set(this.satisfactionItems
      .map((item) => Math.round(Number(item.nota)))
      .filter((score) => Number.isFinite(score))))
      .sort((a, b) => b - a);
    return this.satCachedScoreOptions;
  }

  get satisfactionAreas(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedAreas) return this.satCachedAreas;
    this.satCachedAreas = this.satisfactionBreakdown(this.satisfactionItemsForFilter('area'), 'area');
    return this.satCachedAreas;
  }

  get satisfactionResponsables(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedResponsables) return this.satCachedResponsables;
    this.satCachedResponsables = this.satisfactionBreakdown(this.satisfactionItemsForFilter('responsable'), 'responsable');
    return this.satCachedResponsables;
  }

  get satisfactionPeriods(): any[] {
    return this.satisfactionBreakdown(this.satisfactionItemsForFilter('period'), 'period')
      .map((row) => ({ ...row, label: this.satisfactionItems.find((item) => item.period === row.key)?.period_label || row.key }))
      .sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }

  get satisfactionPeriodOptions(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedPeriodOptions) return this.satCachedPeriodOptions;
    this.satCachedPeriodOptions = this.satisfactionBreakdown(this.satisfactionItems, 'period')
      .map((row) => ({ ...row, label: this.satisfactionItems.find((item) => item.period === row.key)?.period_label || row.key }))
      .sort((a, b) => String(a.key).localeCompare(String(b.key)));
    return this.satCachedPeriodOptions;
  }

  get latestSatisfactionPeriod(): string {
    return this.satisfactionPeriodOptions
      .map((period) => String(period.key || ''))
      .filter((key) => /^\d{4}-\d{2}$/.test(key))
      .sort((a, b) => b.localeCompare(a))[0] || '';
  }

  get satisfactionYearOptions(): string[] {
    this.satEnsureCacheFresh();
    if (this.satCachedYearOptions) return this.satCachedYearOptions;
    this.satCachedYearOptions = Array.from(new Set(this.satisfactionItems
      .map((item) => String(item.period || '').slice(0, 4))
      .filter((year) => /^\d{4}$/.test(year))))
      .sort((a, b) => b.localeCompare(a));
    return this.satCachedYearOptions;
  }

  get satisfactionMonthOptions(): Array<{ key: string; label: string }> {
    this.satEnsureCacheFresh();
    if (this.satCachedMonthOptions) return this.satCachedMonthOptions;
    const labels: Record<string, string> = {
      '01': 'Enero',
      '02': 'Febrero',
      '03': 'Marzo',
      '04': 'Abril',
      '05': 'Mayo',
      '06': 'Junio',
      '07': 'Julio',
      '08': 'Agosto',
      '09': 'Septiembre',
      '10': 'Octubre',
      '11': 'Noviembre',
      '12': 'Diciembre',
    };
    this.satCachedMonthOptions = Array.from(new Set(this.satisfactionItems
      .filter((item) => !this.selectedSatisfactionYear || String(item.period || '').slice(0, 4) === this.selectedSatisfactionYear)
      .map((item) => String(item.period || '').slice(5, 7))
      .filter((month) => /^\d{2}$/.test(month))))
      .sort()
      .map((key) => ({ key, label: labels[key] || key }));
    return this.satCachedMonthOptions;
  }

  get satisfactionAreaOptions(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedAreaOptions) return this.satCachedAreaOptions;
    this.satCachedAreaOptions = this.satisfactionBreakdown(this.satisfactionItems, 'area');
    return this.satCachedAreaOptions;
  }

  get satisfactionResponsableOptions(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedRespOptions) return this.satCachedRespOptions;
    this.satCachedRespOptions = this.satisfactionBreakdown(this.satisfactionItemsForFilter('responsable'), 'responsable');
    return this.satCachedRespOptions;
  }

  get satisfactionQuestions(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedQuestions) return this.satCachedQuestions;
    this.satCachedQuestions = this.satisfactionBreakdown(this.filteredSatisfactionItems, 'descripcion_pregunta').slice(0, 8);
    return this.satCachedQuestions;
  }

  get satisfactionAreaBars(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedAreaBars) return this.satCachedAreaBars;
    const summary = this.satisfactionSummary;
    this.satCachedAreaBars = this.satisfactionAreas.slice(0, 7).map((row) => ({
      ...row,
      pct: summary.answered ? Math.round((row.answered / summary.answered) * 100) : 0,
      dominantCategory: this.satisfactionDominantCategory(row),
    }));
    return this.satCachedAreaBars;
  }

  get satisfactionResponsableBars(): any[] {
    this.satEnsureCacheFresh();
    if (this.satCachedRespBars) return this.satCachedRespBars;
    const summary = this.satisfactionSummary;
    this.satCachedRespBars = this.satisfactionResponsables.slice(0, 7).map((row) => ({
      ...row,
      pct: summary.answered ? Math.round((row.answered / summary.answered) * 100) : 0,
      dominantCategory: this.satisfactionDominantCategory(row),
    }));
    return this.satCachedRespBars;
  }

  get satisfactionPeriodTrend(): any[] {
    const periods = this.satisfactionPeriods;
    const scores = periods.map((period) => Number(period.nps)).filter((value) => Number.isFinite(value));
    const min = scores.length ? Math.min(...scores) : 0;
    const max = scores.length ? Math.max(...scores) : 100;
    const span = Math.max(1, max - min);
    return periods.map((period) => ({
      ...period,
      y: 100 - (((Number(period.nps) || 0) - min) / span) * 72 - 12,
    }));
  }

  get satisfactionTrendRows(): any[] {
    return this.getSatisfactionTrendCache().rows;
  }

  get satisfactionTrendChartWidth(): number {
    return this.getSatisfactionTrendCache().width;
  }

  get satisfactionTrendLinePath(): string {
    return this.smoothPath(this.satisfactionTrendLinePoints);
  }

  satisfactionTrendCategoryPath(category: 'promoters' | 'passives' | 'detractors'): string {
    return this.getSatisfactionTrendCache().paths[category];
  }

  satisfactionTrendCategoryPoints(category: 'promoters' | 'passives' | 'detractors'): Array<{ x: number; y: number; value: number; label: string }> {
    return this.getSatisfactionTrendCache().points[category];
  }

  get satisfactionTrendAveragePath(): string {
    return this.getSatisfactionTrendCache().paths.average;
  }

  get satisfactionTrendNpsPath(): string {
    return this.getSatisfactionTrendCache().paths.nps;
  }

  get satisfactionTrendSurveysPath(): string {
    return this.getSatisfactionTrendCache().paths.surveys;
  }

  get satisfactionTrendNpsPoints(): Array<{ x: number; y: number; value: number; label: string }> {
    return this.getSatisfactionTrendCache().points.nps;
  }

  get satisfactionTrendSurveysPoints(): Array<{ x: number; y: number; value: number; label: string }> {
    return this.getSatisfactionTrendCache().points.surveys;
  }

  get satisfactionTrendAveragePoints(): Array<{ x: number; y: number; value: number; label: string }> {
    return this.getSatisfactionTrendCache().points.average;
  }

  get satisfactionTrendActivePoint(): any | null {
    if (this.satisfactionTrendHoverIndex === null) return null;
    return this.satisfactionTrendRows[this.satisfactionTrendHoverIndex] || null;
  }

  get satisfactionTrendHoverX(): number {
    if (this.satisfactionTrendHoverIndex === null) return 4;
    return this.satisfactionTrendAveragePoints[this.satisfactionTrendHoverIndex]?.x || 4;
  }

  get satisfactionTrendTooltipLeftPct(): number {
    return (this.satisfactionTrendHoverX / this.satisfactionTrendChartWidth) * 100;
  }

  get satisfactionTrendTooltipPositionClass(): string {
    const index = this.satisfactionTrendHoverIndex;
    const data = this.satisfactionTrendRows;
    if (index === null) return 'middle';
    if (data.length <= 2) return index === 0 ? 'left-edge' : 'right-edge';
    if (index === 0) return 'left-edge';
    if (index >= data.length - 1) return 'right-edge';
    return 'middle';
  }

  satisfactionPct(point: any, category: 'promoters' | 'passives' | 'detractors'): number {
    const answered = Number(point?.answered) || 0;
    return answered ? Number((((Number(point?.[category]) || 0) / answered) * 100).toFixed(1)) : 0;
  }

  onSatisfactionTrendPointer(event: MouseEvent): void {
    const target = event.currentTarget as SVGRectElement | null;
    const svg = target?.ownerSVGElement;
    const data = this.getSatisfactionTrendCache().rows;
    if (!svg || !data.length) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * this.satisfactionTrendChartWidth;
    const left = 4;
    const right = this.satisfactionTrendChartWidth - 4;
    const ratio = Math.max(0, Math.min(1, (x - left) / (right - left)));
    const nextIndex = Math.round(ratio * Math.max(0, data.length - 1));
    if (nextIndex !== this.satisfactionTrendHoverIndex) {
      this.satisfactionTrendHoverIndex = nextIndex;
    }
  }

  clearSatisfactionTrendHover(): void {
    this.satisfactionTrendHoverIndex = null;
  }

  get satisfactionTrendAreaPath(): string {
    const points = this.satisfactionTrendLinePoints;
    if (!points.length) return '';
    const baseline = 210;
    const line = this.smoothPath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;
  }

  private satisfactionItemsForFilterNoWindow(): any[] {
    return this.satisfactionItems.filter((item) => {
      if (this.selectedSatisfactionArea && item.area !== this.selectedSatisfactionArea) return false;
      if (this.selectedSatisfactionResponsable && item.responsable !== this.selectedSatisfactionResponsable) return false;
      if (this.selectedSatisfactionPeriod && item.period !== this.selectedSatisfactionPeriod) return false;
      if (this.selectedSatisfactionYear && String(item.period || '').slice(0, 4) !== this.selectedSatisfactionYear) return false;
      if (this.selectedSatisfactionMonth && String(item.period || '').slice(5, 7) !== this.selectedSatisfactionMonth) return false;
      if (this.selectedSatisfactionScore && String(Math.round(Number(item.nota))) !== this.selectedSatisfactionScore) return false;
      if (this.selectedSatisfactionCategory && this.satisfactionCategory(item.nota) !== this.selectedSatisfactionCategory) return false;
      return true;
    });
  }

  private satisfactionItemsForTrend(): any[] {
    return this.satisfactionItems.filter((item) => {
      if (this.selectedSatisfactionArea && item.area !== this.selectedSatisfactionArea) return false;
      if (this.selectedSatisfactionResponsable && item.responsable !== this.selectedSatisfactionResponsable) return false;
      if (this.selectedSatisfactionScore && String(Math.round(Number(item.nota))) !== this.selectedSatisfactionScore) return false;
      if (this.selectedSatisfactionCategory && this.satisfactionCategory(item.nota) !== this.selectedSatisfactionCategory) return false;
      return true;
    });
  }

  private getSatisfactionTrendCache(): any {
    const key = [
      this.satisfactionItems.length,
      this.selectedSatisfactionArea,
      this.selectedSatisfactionResponsable,
      this.selectedSatisfactionScore,
      this.selectedSatisfactionCategory,
      this.satisfactionTrendWindow,
    ].join('|');
    if (this.satisfactionTrendCacheKey === key && this.satisfactionTrendCache) {
      return this.satisfactionTrendCache;
    }
    const rows = this.satisfactionBreakdown(this.satisfactionItemsForTrend(), 'period')
      .map((row) => ({ ...row, label: this.satisfactionItems.find((item) => item.period === row.key)?.period_label || row.key }))
      .sort((a, b) => String(a.key).localeCompare(String(b.key)))
      .slice(-this.satisfactionTrendWindow);
    const visiblePeriods = this.satisfactionTrendWindow <= 6 ? this.satisfactionTrendWindow : 7;
    const width = rows.length <= visiblePeriods ? 720 : Math.max(720, rows.length * (720 / visiblePeriods));
    const left = 4;
    const right = width - 4;
    const top = 22;
    const bottom = 210;
    const span = Math.max(1, rows.length - 1);
    const pctPoints = (category: 'promoters' | 'passives' | 'detractors') => rows.map((point, index) => {
      const answered = Number(point.answered) || 0;
      const value = answered ? (Number(point[category]) || 0) / answered * 100 : 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / 100;
      return { x, y, value, label: point.label || point.key || '' };
    });
    const average = rows.map((point, index) => {
      const value = Number(point.average) || 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * Math.max(0, Math.min(10, value))) / 10;
      return { x, y, value, label: point.label || point.key || '' };
    });
    const nps = rows.map((point, index) => {
      const value = Math.max(-100, Math.min(100, Number(point.nps) || 0));
      const normalized = (value + 100) / 200;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * normalized);
      return { x, y, value, label: point.label || point.key || '' };
    });
    const maxSurveys = Math.max(1, ...rows.map((point) => Number(point.answered) || 0));
    const surveys = rows.map((point, index) => {
      const value = Number(point.answered) || 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / maxSurveys;
      return { x, y, value, label: point.label || point.key || '' };
    });
    const points = {
      promoters: pctPoints('promoters'),
      passives: pctPoints('passives'),
      detractors: pctPoints('detractors'),
      average,
      nps,
      surveys,
    };
    this.satisfactionTrendCacheKey = key;
    this.satisfactionTrendCache = {
      rows,
      width,
      points,
      paths: {
        promoters: this.smoothPath(points.promoters),
        passives: this.smoothPath(points.passives),
        detractors: this.smoothPath(points.detractors),
        average: this.smoothPath(points.average),
        nps: this.smoothPath(points.nps),
        surveys: this.smoothPath(points.surveys),
      },
    };
    return this.satisfactionTrendCache;
  }

  get satisfactionTrendLinePoints(): Array<{ x: number; y: number; value: number; label: string }> {
    const data = this.satisfactionTrendRows;
    if (!data.length) return [];
    const chartWidth = 1024;
    const left = 14;
    const right = chartWidth - 14;
    const top = 22;
    const bottom = 210;
    const values = data.map((point) => Number(point.nps)).filter((value) => Number.isFinite(value));
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 100;
    const range = Math.max(1, max - min);
    const span = Math.max(1, data.length - 1);
    return data.map((point, index) => {
      const value = Number(point.nps) || 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * (value - min)) / range;
      return { x, y, value, label: point.label || point.key || '' };
    });
  }

  get satisfactionPromoterPct(): number {
    return this.satisfactionPercent(this.satisfactionSummary.promoters || 0);
  }

  get satisfactionPassivePct(): number {
    return this.satisfactionPercent(this.satisfactionSummary.passives || 0);
  }

  get satisfactionDetractorPct(): number {
    return this.satisfactionPercent(this.satisfactionSummary.detractors || 0);
  }

  get satisfactionDonutStyle(): string {
    return `conic-gradient(#079767 0 ${this.satisfactionPromoterPct}%, #e3c628 ${this.satisfactionPromoterPct}% ${this.satisfactionPromoterPct + this.satisfactionPassivePct}%, #cf1431 ${this.satisfactionPromoterPct + this.satisfactionPassivePct}% 100%)`;
  }

  get satisfactionScoreDistribution(): Array<{ score: number; value: number; pct: number }> {
    this.satEnsureCacheFresh();
    if (this.satCachedScoreDistribution) return this.satCachedScoreDistribution;
    const counts = new Map<number, number>();
    for (let score = 1; score <= 10; score += 1) counts.set(score, 0);
    for (const item of this.filteredSatisfactionItems) {
      const score = Math.round(Number(item.nota));
      if (score >= 1 && score <= 10) counts.set(score, (counts.get(score) || 0) + 1);
    }
    const max = Math.max(1, ...Array.from(counts.values()));
    this.satCachedScoreDistribution = Array.from(counts.entries()).map(([score, value]) => ({ score, value, pct: (value / max) * 100 }));
    return this.satCachedScoreDistribution;
  }

  get satisfactionFilterActive(): boolean {
    return !!(this.selectedSatisfactionWindow !== 'all' || this.selectedSatisfactionArea || this.selectedSatisfactionResponsable || this.selectedSatisfactionPeriod || this.selectedSatisfactionYear || this.selectedSatisfactionMonth || this.selectedSatisfactionScore || this.selectedSatisfactionCategory);
  }

  private satisfactionMatchesWindow(item: any): boolean {
    if (this.selectedSatisfactionWindow === 'all') return true;
    const meta = this.satisfactionDateMeta();
    if (meta.entries.length) {
      const itemTime = this.satisfactionItemTime(item);
      if (!Number.isFinite(itemTime)) return false;
      if (this.selectedSatisfactionWindow === '24h') {
        const today = this.todayTimeBounds();
        return itemTime >= today.from && itemTime < today.to;
      }
      const latestTime = meta.latestTime;
      const latestDate = new Date(latestTime);
      const currentMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1).getTime();
      const nextMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth() + 1, 1).getTime();
      const previousMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth() - 1, 1).getTime();
      if (this.selectedSatisfactionWindow === 'last-month') {
        return itemTime >= currentMonthStart && itemTime < nextMonthStart;
      }
      if (this.selectedSatisfactionWindow === 'previous-month') {
        return itemTime >= previousMonthStart && itemTime < currentMonthStart;
      }
      const days = this.selectedSatisfactionWindow === '3d'
          ? 3
          : this.selectedSatisfactionWindow === '7d'
            ? 7
            : 14;
      return itemTime >= latestTime - (days * 86400000) && itemTime <= latestTime;
    }
    if (this.selectedSatisfactionWindow === '24h') return false;
    const windowKey = this.selectedSatisfactionWindow + '|' + this.satisfactionItems.length;
    if (this.satCachedWindowPeriodsKey !== windowKey) {
      this.satCachedWindowPeriodsKey = windowKey;
      const periods = this.satisfactionPeriodOptions
        .map((period) => String(period.key || ''))
        .filter((key) => /^\d{4}-\d{2}$/.test(key))
        .sort((a, b) => a.localeCompare(b));
      this.satCachedWindowPeriods = periods.length
        ? (() => {
            switch (this.selectedSatisfactionWindow) {
              case '3d': return periods.slice(-3);
              case '7d': return periods.slice(-7);
              case '14d': return periods.slice(-14);
              case 'previous-month': return periods.slice(-2, -1);
              case 'last-month':
              default: return periods.slice(-1);
            }
          })()
        : [];
    }
    return this.satCachedWindowPeriods !== null && (this.satCachedWindowPeriods.length === 0 || this.satCachedWindowPeriods.includes(String(item.period || '')));
  }

  private satisfactionItemsInWindow(): any[] {
    if (this.selectedSatisfactionWindow === 'all') {
      return this.satisfactionItems;
    }
    const key = `${this.selectedSatisfactionWindow}|${this.satisfactionItems.length}|${this.satisfaction?.updated_at || this.satisfactionUpdatedAt || ''}|${this.selectedSatisfactionWindow === '24h' ? this.todayDateKey() : ''}`;
    if (this.satCachedWindowItemsKey === key && this.satCachedWindowItems) return this.satCachedWindowItems;
    const meta = this.satisfactionDateMeta();
    if (meta.entries.length) {
      const latestDate = new Date(meta.latestTime);
      const currentMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1).getTime();
      const nextMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth() + 1, 1).getTime();
      const previousMonthStart = new Date(latestDate.getFullYear(), latestDate.getMonth() - 1, 1).getTime();
      const today = this.todayTimeBounds();
      this.satCachedWindowItems = meta.entries
        .filter(({ time }) => {
          if (this.selectedSatisfactionWindow === '24h') return time >= today.from && time < today.to;
          if (this.selectedSatisfactionWindow === 'last-month') return time >= currentMonthStart && time < nextMonthStart;
          if (this.selectedSatisfactionWindow === 'previous-month') return time >= previousMonthStart && time < currentMonthStart;
          const days = this.selectedSatisfactionWindow === '3d'
              ? 3
              : this.selectedSatisfactionWindow === '7d'
                ? 7
                : 14;
          return time >= meta.latestTime - (days * 86400000) && time <= meta.latestTime;
        })
        .map(({ item }) => item);
      this.satCachedWindowItemsKey = key;
      return this.satCachedWindowItems;
    }
    if (this.selectedSatisfactionWindow === '24h') {
      this.satCachedWindowItems = [];
      this.satCachedWindowItemsKey = key;
      return this.satCachedWindowItems;
    }
    this.satCachedWindowItems = this.satisfactionItems.filter((item) => this.satisfactionMatchesWindow(item));
    this.satCachedWindowItemsKey = key;
    return this.satCachedWindowItems;
  }

  private satisfactionDateMeta(): { entries: Array<{ item: any; time: number }>; latestTime: number } {
    const key = `${this.satisfactionItems.length}|${this.satisfaction?.updated_at || this.satisfactionUpdatedAt || ''}`;
    if (this.satCachedDateMetaKey === key && this.satCachedDateMeta) return this.satCachedDateMeta;
    const entries = this.satisfactionItems
      .map((item) => ({ item, time: this.satisfactionItemTime(item) }))
      .filter((entry): entry is { item: any; time: number } => Number.isFinite(entry.time));
    this.satCachedDateMeta = {
      entries,
      latestTime: entries.length ? Math.max(...entries.map((entry) => entry.time)) : Number.NaN,
    };
    this.satCachedDateMetaKey = key;
    return this.satCachedDateMeta;
  }

  private satisfactionItemTime(item: any): number {
    const candidates = [item?.fecha, item?.created_at, item?.updated_at, item?.period];
    for (const candidate of candidates) {
      const text = String(candidate || '').trim();
      if (!text || text === '(vacío)' || text === '(sin fecha)') continue;
      const normalized = /^\d{4}-\d{2}$/.test(text)
        ? `${text}-01T00:00:00`
        : /^\d{4}-\d{2}-\d{2}$/.test(text)
          ? `${text}T00:00:00`
          : text;
      const parsed = new Date(normalized).getTime();
      if (Number.isFinite(parsed)) return parsed;
    }
    return Number.NaN;
  }

  private calculateSatisfactionSummary(items: any[]): any {
    const scores = items.map((item) => Number(item.nota)).filter((score) => Number.isFinite(score));
    const promoters = scores.filter((score) => score >= 9).length;
    const passives = scores.filter((score) => score >= 7 && score < 9).length;
    const detractors = scores.filter((score) => score < 7).length;
    return {
      total: items.length,
      answered: scores.length,
      average: scores.length ? Number((scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(2)) : null,
      nps: scores.length ? Number((((promoters - detractors) / scores.length) * 100).toFixed(1)) : null,
      promoters,
      passives,
      detractors,
    };
  }

  private satisfactionPercent(value: number): number {
    const answered = this.satisfactionSummary.answered || 0;
    return answered ? Math.round((value / answered) * 100) : 0;
  }

  private satisfactionBreakdown(items: any[], field: string): any[] {
    const buckets = new Map<string, { count: number; scores: number[] }>();
    for (const item of items) {
      const key = item[field] || '(vacío)';
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { count: 0, scores: [] };
        buckets.set(key, bucket);
      }
      bucket.count++;
      const score = Number(item.nota);
      if (Number.isFinite(score)) bucket.scores.push(score);
    }
    return Array.from(buckets.entries())
      .map(([key, { count, scores }]) => {
        const promoters = scores.filter((s) => s >= 9).length;
        const passives = scores.filter((s) => s >= 7 && s < 9).length;
        const detractors = scores.filter((s) => s < 7).length;
        return {
          key, label: key,
          total: count,
          answered: scores.length,
          average: scores.length ? Number((scores.reduce((sum, s) => sum + s, 0) / scores.length).toFixed(2)) : null,
          nps: scores.length ? Number((((promoters - detractors) / scores.length) * 100).toFixed(1)) : null,
          promoters, passives, detractors,
        };
      })
      .sort((a, b) => b.answered - a.answered || a.label.localeCompare(b.label));
  }

  satisfactionScoreWidth(score: unknown): number {
    const value = Number(score);
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value * 10));
  }

  satisfactionCategory(score: unknown): 'promoters' | 'passives' | 'detractors' | '' {
    const value = Number(score);
    if (!Number.isFinite(value)) return '';
    if (value >= 9) return 'promoters';
    if (value >= 7) return 'passives';
    return 'detractors';
  }

  satisfactionCategoryLabel(score: unknown): string {
    const category = this.satisfactionCategory(score);
    if (category === 'promoters') return 'Promotor';
    if (category === 'passives') return 'Neutro';
    if (category === 'detractors') return 'Detractor';
    return 'Sin nota';
  }

  satisfactionDominantCategory(row: any): 'promoters' | 'passives' | 'detractors' {
    const averageCategory = this.satisfactionCategory(row?.average);
    if (averageCategory) return averageCategory;
    const promoters = Number(row?.promoters) || 0;
    const passives = Number(row?.passives) || 0;
    const detractors = Number(row?.detractors) || 0;
    if (detractors >= promoters && detractors >= passives) return 'detractors';
    if (passives >= promoters && passives >= detractors) return 'passives';
    return 'promoters';
  }

  satisfactionDateOnly(value: unknown): string {
    const text = String(value || '').trim();
    if (!text || text === '(vacío)') return '—';
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text.split('T')[0] || text;
    return date.toLocaleDateString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  get satisfactionLastUpdated(): string {
    if (!this.satisfactionUpdatedAt) return '';
    const date = new Date(this.satisfactionUpdatedAt);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  scheduleStatusForMonth(row: any, monthKey: string): any {
    return (row?.milestones || []).find((item: any) => item.month === monthKey);
  }

  scheduleStatusClass(statusKey: string): string {
    if (statusKey === 'done') return 'done';
    if (statusKey === 'pending') return 'pending';
    return 'other';
  }

  scheduleMonthShortLabel(monthKey: string): string {
    const label = (this.schedule?.months || []).find((month: any) => month.key === monthKey)?.label;
    return label ? label.slice(0, 3) : String(monthKey || '').slice(5, 7);
  }

  scheduleMonthFullLabel(monthKey: string): string {
    return (this.schedule?.months || []).find((m: any) => m.key === monthKey)?.label || monthKey;
  }

  get filteredScheduleMilestones(): any[] {
    const milestones: any[] = [];
    for (const activity of this.schedule?.activities || []) {
      if (this.selectedScheduleProcess && activity.process !== this.selectedScheduleProcess) continue;
      if (this.selectedScheduleActivity && activity.activity !== this.selectedScheduleActivity) continue;
      for (const milestone of activity.milestones || []) {
        if (this.selectedScheduleMonth && milestone.month !== this.selectedScheduleMonth) continue;
        if (this.selectedScheduleStatus && milestone.status_key !== this.selectedScheduleStatus) continue;
        milestones.push({
          ...milestone,
          process: activity.process,
          activity: activity.activity,
        });
      }
    }
    return milestones;
  }

  get scheduleVisibleActivitiesCount(): number {
    return this.filteredScheduleActivities.length;
  }

  get scheduleVisibleMilestonesCount(): number {
    return this.filteredScheduleMilestones.length;
  }

  get scheduleVisibleDoneCount(): number {
    return this.filteredScheduleMilestones.filter((milestone: any) => milestone.status_key === 'done').length;
  }

  get scheduleVisiblePendingCount(): number {
    return this.filteredScheduleMilestones.filter((milestone: any) => milestone.status_key === 'pending').length;
  }

  processCompletion(process: any): number {
    const total = Math.max(1, process?.milestones || 0);
    return Math.round(((process?.done || 0) / total) * 100);
  }

  get filteredScheduleActivities(): any[] {
    const activities = this.schedule?.activities || [];
    return activities
      .map((activity: any) => ({
        ...activity,
        milestones: (activity.milestones || []).filter((milestone: any) => {
          if (this.selectedScheduleMonth && milestone.month !== this.selectedScheduleMonth) return false;
          if (this.selectedScheduleStatus && milestone.status_key !== this.selectedScheduleStatus) return false;
          return true;
        }),
      }))
      .filter((activity: any) => {
        if (this.selectedScheduleProcess && activity.process !== this.selectedScheduleProcess) return false;
        if (this.selectedScheduleActivity && activity.activity !== this.selectedScheduleActivity) return false;
        return activity.milestones.length > 0 || (!this.selectedScheduleMonth && !this.selectedScheduleStatus);
      });
  }

  get filteredScheduleMonths(): any[] {
    const months = this.schedule?.months || [];
    return months.map((month: any) => {
      const items = (month.items || []).filter((item: any) => {
        if (this.selectedScheduleProcess && item.process !== this.selectedScheduleProcess) return false;
        if (this.selectedScheduleActivity && item.activity !== this.selectedScheduleActivity) return false;
        if (this.selectedScheduleStatus && item.status_key !== this.selectedScheduleStatus) return false;
        return true;
      });
      return {
        ...month,
        items,
        total: items.length,
        done: items.filter((item: any) => item.status_key === 'done').length,
        pending: items.filter((item: any) => item.status_key === 'pending').length,
      };
    });
  }

  get filteredScheduleProcesses(): any[] {
    const processMap = new Map<string, any>();
    for (const activity of this.schedule?.activities || []) {
      if (this.selectedScheduleProcess && activity.process !== this.selectedScheduleProcess) continue;
      if (this.selectedScheduleActivity && activity.activity !== this.selectedScheduleActivity) continue;
      const milestones = (activity.milestones || []).filter((milestone: any) => {
        if (this.selectedScheduleMonth && milestone.month !== this.selectedScheduleMonth) return false;
        if (this.selectedScheduleStatus && milestone.status_key !== this.selectedScheduleStatus) return false;
        return true;
      });
      if (!milestones.length && (this.selectedScheduleMonth || this.selectedScheduleStatus)) continue;
      const item = processMap.get(activity.process) || { process: activity.process, activities: 0, milestones: 0, done: 0, pending: 0 };
      item.activities += 1;
      item.milestones += milestones.length;
      item.done += milestones.filter((milestone: any) => milestone.status_key === 'done').length;
      item.pending += milestones.filter((milestone: any) => milestone.status_key === 'pending').length;
      processMap.set(activity.process, item);
    }
    return [...processMap.values()].sort((a, b) => b.milestones - a.milestones || a.process.localeCompare(b.process));
  }

  get scheduleRoadmapGroups(): any[] {
    const monthIndex = new Map((this.schedule?.months || []).map((month: any, index: number) => [month.key, index]));
    const groupMap = new Map<string, any>();
    const monthCount = Math.max(1, this.schedule?.months?.length || 12);
    for (const activity of this.filteredScheduleActivities) {
      const process = activity.process || 'Sin proceso';
      const group = groupMap.get(process) || {
        process,
        activities: 0,
        milestones: 0,
        done: 0,
        pending: 0,
        points: [],
      };
      group.activities += 1;
      for (const milestone of activity.milestones || []) {
        const index = Number(monthIndex.get(milestone.month) ?? 0);
        group.milestones += 1;
        if (milestone.status_key === 'done') group.done += 1;
        if (milestone.status_key === 'pending') group.pending += 1;
        group.points.push({
          ...milestone,
          activity: activity.activity,
          process,
          monthIndex: index,
          position: ((index + 0.5) / monthCount) * 100,
        });
      }
      groupMap.set(process, group);
    }
    return [...groupMap.values()]
      .map((group) => ({
        ...group,
        completion: Math.round((group.done / Math.max(1, group.milestones)) * 100),
        points: this.groupScheduleRoadmapPoints(group.points),
      }))
      .sort((a, b) => b.milestones - a.milestones || a.process.localeCompare(b.process));
  }

  get scheduleRoadmapSummary(): any {
    return this.scheduleRoadmapGroups.reduce((acc: any, group: any) => {
      acc.activities += group.activities || 0;
      acc.milestones += group.milestones || 0;
      acc.done += group.done || 0;
      acc.pending += group.pending || 0;
      return acc;
    }, { activities: 0, milestones: 0, done: 0, pending: 0 });
  }

  get schedulePlanningMonthLoad(): any[] {
    return this.filteredScheduleMonths
      .filter((month: any) => month.total > 0)
      .map((month: any) => ({
        ...month,
        completion: Math.round(((month.done || 0) / Math.max(1, month.total || 0)) * 100),
        loadScore: ((month.pending || 0) * 2) + (month.total || 0),
        intensity: Math.min(100, Math.round(((month.pending || 0) / Math.max(1, this.scheduleVisiblePendingCount)) * 100)),
      }));
  }

  get schedulePlanningCriticalMonths(): any[] {
    return [...this.schedulePlanningMonthLoad]
      .sort((a: any, b: any) => (b.pending || 0) - (a.pending || 0) || (b.total || 0) - (a.total || 0))
      .slice(0, 4);
  }

  get schedulePlanningProcessLoad(): any[] {
    const monthIndex = new Map((this.schedule?.months || []).map((month: any, index: number) => [month.key, index]));
    const processMap = new Map<string, any>();
    for (const activity of this.filteredScheduleActivities) {
      const processName = activity.process || 'Sin proceso';
      const process = processMap.get(processName) || {
        process: processName,
        activities: 0,
        milestones: 0,
        done: 0,
        pending: 0,
        months: new Set<string>(),
        items: [],
      };
      process.activities += 1;
      for (const milestone of activity.milestones || []) {
        const monthOrder = Number(monthIndex.get(milestone.month) ?? 99);
        process.milestones += 1;
        if (milestone.status_key === 'done') process.done += 1;
        if (milestone.status_key === 'pending') process.pending += 1;
        process.months.add(milestone.month);
        process.items.push({
          ...milestone,
          activity: activity.activity,
          monthOrder,
          monthLabel: this.scheduleMonthShortLabel(milestone.month),
        });
      }
      processMap.set(processName, process);
    }
    return [...processMap.values()]
      .map((process: any) => {
        const sortedItems = [...process.items].sort((a: any, b: any) => a.monthOrder - b.monthOrder || String(a.activity).localeCompare(String(b.activity)));
        const nextPending = sortedItems.find((item: any) => item.status_key === 'pending') || sortedItems[0] || null;
        return {
          ...process,
          monthsCount: process.months.size,
          completion: Math.round((process.done / Math.max(1, process.milestones)) * 100),
          nextPending,
        };
      })
      .sort((a: any, b: any) => b.pending - a.pending || b.milestones - a.milestones || a.process.localeCompare(b.process));
  }

  get schedulePlanningTopProcesses(): any[] {
    return this.schedulePlanningProcessLoad.slice(0, 6);
  }

  get schedulePlanningMatrix(): any[] {
    const months = this.schedule?.months || [];
    return this.schedulePlanningProcessLoad.slice(0, 8).map((process: any) => ({
      ...process,
      monthCells: months.map((month: any) => {
        const items = (process.items || []).filter((item: any) => item.month === month.key);
        const pending = items.filter((item: any) => item.status_key === 'pending').length;
        const done = items.filter((item: any) => item.status_key === 'done').length;
        return {
          key: month.key,
          label: month.label,
          total: items.length,
          pending,
          done,
          status_key: pending ? 'pending' : done ? 'done' : '',
        };
      }),
    }));
  }

  get schedulePlanningNextActivities(): any[] {
    const monthIndex = new Map((this.schedule?.months || []).map((month: any, index: number) => [month.key, index]));
    return this.filteredScheduleMilestones
      .filter((milestone: any) => milestone.status_key === 'pending')
      .map((milestone: any) => ({
        ...milestone,
        monthIndex: Number(monthIndex.get(milestone.month_key || milestone.month) ?? monthIndex.get(milestone.month) ?? 99),
        monthLabel: this.scheduleMonthShortLabel(milestone.month_key || milestone.month),
      }))
      .sort((a: any, b: any) => a.monthIndex - b.monthIndex || String(a.process).localeCompare(String(b.process)) || String(a.activity).localeCompare(String(b.activity)))
      .slice(0, 8);
  }

  get selectedScheduleRoadmapGroup(): any | null {
    if (!this.selectedScheduleRoadmapProcess) return null;
    return this.scheduleRoadmapGroups.find((group: any) => group.process === this.selectedScheduleRoadmapProcess) || null;
  }

  get selectedScheduleRoadmapActivities(): any[] {
    if (!this.selectedScheduleRoadmapProcess) return [];
    const monthIndex = new Map((this.schedule?.months || []).map((month: any, index: number) => [month.key, index]));
    return this.filteredScheduleActivities
      .filter((activity: any) => activity.process === this.selectedScheduleRoadmapProcess)
      .map((activity: any) => {
        const milestones = [...(activity.milestones || [])]
          .map((milestone: any) => ({
            ...milestone,
            monthIndex: Number(monthIndex.get(milestone.month) ?? 0),
            monthLabel: this.scheduleMonthShortLabel(milestone.month),
          }))
          .sort((a: any, b: any) => a.monthIndex - b.monthIndex || String(a.status).localeCompare(String(b.status)));
        return {
          ...activity,
          milestones,
          firstMonthIndex: milestones[0]?.monthIndex ?? 99,
          done: milestones.filter((milestone: any) => milestone.status_key === 'done').length,
          pending: milestones.filter((milestone: any) => milestone.status_key === 'pending').length,
        };
      })
      .sort((a: any, b: any) => a.firstMonthIndex - b.firstMonthIndex || String(a.activity).localeCompare(String(b.activity)));
  }

  selectedScheduleRoadmapPointsForMonth(monthIndex: number): any[] {
    return (this.selectedScheduleRoadmapGroup?.points || [])
      .filter((point: any) => Number(point.monthIndex) === monthIndex)
      .sort((a: any, b: any) => String(a.activity).localeCompare(String(b.activity)));
  }

  get selectedScheduleMonthActivities(): any[] {
    if (!this.selectedScheduleRoadmapMonth || !this.schedule?.months) return [];
    const monthIndexes: Map<string, number> = new Map((this.schedule.months || []).map((m: any, i: number) => [m.key, i]));
    const targetIndex = monthIndexes.get(this.selectedScheduleRoadmapMonth) ?? -1;
    if (targetIndex < 0) return [];
    const shortLabel = this.scheduleMonthShortLabel(this.selectedScheduleRoadmapMonth);
    return this.filteredScheduleActivities
      .map((activity: any) => {
        const milestones = (activity.milestones || [])
          .filter((m: any) => monthIndexes.get(m.month) === targetIndex)
          .map((m: any) => ({ ...m, monthLabel: shortLabel }));
        if (!milestones.length) return null;
        return {
          ...activity,
          milestones,
          firstMonthIndex: targetIndex,
          done: milestones.filter((m: any) => m.status_key === 'done').length,
          pending: milestones.filter((m: any) => m.status_key === 'pending').length,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => String(a.process).localeCompare(String(b.process)) || String(a.activity).localeCompare(String(b.activity)));
  }

  get selectedScheduleRoadmapMonthLabel(): string {
    if (!this.selectedScheduleRoadmapMonth || !this.schedule?.months) return '';
    return this.schedule.months.find((m: any) => m.key === this.selectedScheduleRoadmapMonth)?.label || this.selectedScheduleRoadmapMonth;
  }

  openScheduleRoadmapGroup(group: any): void {
    this.selectedScheduleRoadmapProcess = group?.process || '';
    this.selectedScheduleRoadmapPoint = null;
    this.cdr.detectChanges();
  }

  openScheduleRoadmapProcess(process: string): void {
    this.selectedScheduleRoadmapProcess = process || '';
    this.cdr.detectChanges();
  }

  closeScheduleRoadmapGroup(): void {
    this.selectedScheduleRoadmapProcess = '';
    this.selectedScheduleRoadmapPoint = null;
    this.selectedScheduleRoadmapMonth = '';
    this.cdr.detectChanges();
  }

  private groupScheduleRoadmapPoints(points: any[]): any[] {
    const sorted = [...points].sort((a: any, b: any) => a.monthIndex - b.monthIndex || String(a.activity).localeCompare(String(b.activity)));
    const byMonth = new Map<number, any[]>();
    for (const point of sorted) {
      const items = byMonth.get(point.monthIndex) || [];
      items.push(point);
      byMonth.set(point.monthIndex, items);
    }
    return [...byMonth.values()].map((items: any[]) => {
      const first = items[0];
      const statuses = new Set(items.map((item: any) => item.status_key));
      const statusKey = statuses.size === 1 ? first.status_key : 'other';
      return {
        ...first,
        status_key: statusKey,
        status: items.length === 1 ? first.status : `${items.length} hitos`,
        activity: items.length === 1 ? first.activity : `${items.length} hitos agrupados`,
        count: items.length,
        items,
        detail: items.map((item: any) => `${item.activity} - ${item.status}`).join(' | '),
      };
    }).sort((a: any, b: any) => a.monthIndex - b.monthIndex);
  }

  get filteredScheduleNextMilestones(): any[] {
    const monthIndex = new Map((this.schedule?.months || []).map((month: any, index: number) => [month.key, index]));
    const monthLabel = new Map((this.schedule?.months || []).map((month: any) => [month.key, month.label]));
    const targetMonth = this.scheduleHighlightsMonthKey;
    return this.filteredScheduleMilestones
      .filter((milestone: any) => !targetMonth || milestone.month === targetMonth)
      .map((milestone: any) => ({
        ...milestone,
        month_key: milestone.month,
        month: monthLabel.get(milestone.month) || milestone.month,
        monthIndex: Number(monthIndex.get(milestone.month) ?? 0),
      }))
      .sort((a: any, b: any) => a.monthIndex - b.monthIndex || String(a.process).localeCompare(String(b.process)) || String(a.activity).localeCompare(String(b.activity)));
  }

  get currentScheduleMonthKey(): string {
    const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
    return (this.schedule?.months || []).find((month: any) => String(month.number).padStart(2, '0') === currentMonth)?.key || '';
  }

  get scheduleHighlightsMonthKey(): string {
    return this.selectedScheduleMonth || this.currentScheduleMonthKey;
  }

  get visibleScheduleNextMilestones(): any[] {
    return this.filteredScheduleNextMilestones.slice(0, 7);
  }

  get hiddenScheduleNextMilestonesCount(): number {
    return Math.max(0, this.filteredScheduleNextMilestones.length - this.visibleScheduleNextMilestones.length);
  }

  get scheduleHighlightsTitle(): string {
    if (this.selectedScheduleStatus === 'done') return 'Hitos completados';
    if (this.selectedScheduleStatus === 'pending') return 'Hitos pendientes';
    return 'Próximos hitos';
  }

  get scheduleHighlightsSubtitle(): string {
    if (this.selectedScheduleStatus === 'done') return 'Actividades ejecutadas dentro de la selección.';
    if (this.selectedScheduleStatus === 'pending') return 'Actividades por ejecutar dentro de la selección.';
    const label = (this.schedule?.months || []).find((month: any) => month.key === this.scheduleHighlightsMonthKey)?.label;
    return label ? `Hitos del periodo ${label} según los filtros activos.` : 'Hitos según los filtros activos.';
  }

  get scheduleHighlightsEmptyText(): string {
    if (this.selectedScheduleStatus === 'done') return 'Sin hitos completados para la selección actual.';
    if (this.selectedScheduleStatus === 'pending') return 'Sin hitos pendientes para la selección actual.';
    return 'Sin hitos para la selección actual.';
  }

  get scheduleRemunerationCalendar(): any | null {
    return this.schedule?.remuneration_calendar || null;
  }

  get scheduleCalendarIsMonthly(): boolean {
    return this.scheduleRemunerationCalendar?.granularity === 'month';
  }

  private scheduleRemunerationMonthKey(value: string): string {
    return /^\d{4}-\d{2}$/.test(String(value || '')) ? String(value) : '';
  }

  private scheduleRemunerationMonthLabel(value: string): string {
    const key = this.scheduleRemunerationMonthKey(value);
    if (!key) return '';
    const [yearText, monthText] = key.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    if (!year || !month) return key;
    const label = new Intl.DateTimeFormat('es-CL', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  get scheduleRemunerationCalendarMonthOptions(): Array<{ key: string; label: string; events: any[]; groups: any[]; total: number }> {
    const calendar = this.scheduleRemunerationCalendar;
    if (!calendar) return [];
    const monthMap = new Map<string, { key: string; label: string; events: any[] }>();
    for (const event of calendar.events || []) {
      const date = new Date(`${event?.date || ''}T12:00:00`);
      if (Number.isNaN(date.getTime())) continue;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const entry = monthMap.get(key) || {
        key,
        label: this.scheduleRemunerationMonthLabel(key),
        events: [],
      };
      entry.events.push(event);
      monthMap.set(key, entry);
    }
    const fallbackKey = this.scheduleRemunerationMonthKey(calendar.month_key) || this.scheduleRemunerationMonthKey(
      calendar.year && calendar.month_number ? `${calendar.year}-${String(calendar.month_number).padStart(2, '0')}` : '',
    );
    if (!monthMap.size && fallbackKey) {
      monthMap.set(fallbackKey, {
        key: fallbackKey,
        label: this.scheduleRemunerationMonthLabel(fallbackKey) || `${calendar.month_label || ''} ${calendar.year || ''}`.trim(),
        events: calendar.events || [],
      });
    }
    return [...monthMap.values()]
      .map((month) => ({
        key: month.key,
        label: month.label,
        events: month.events,
        groups: (calendar.groups || [])
          .map((group: any) => ({
            title: group.title,
            events: (group.events || []).filter((event: any) => this.scheduleRemunerationMonthKey(event?.date?.slice(0, 7)) === month.key),
          }))
          .filter((group: any) => group.events.length)
          .sort((a: any, b: any) => this.scheduleCalendarProcessOrderIndex(a.title) - this.scheduleCalendarProcessOrderIndex(b.title) || String(a.title).localeCompare(String(b.title))),
        total: month.events.length,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  get selectedScheduleRemunerationMonth(): { key: string; label: string; events: any[]; groups: any[]; total: number } | null {
    const options = this.scheduleRemunerationCalendarMonthOptions;
    if (!options.length) return null;
    const selected = options.find((month) => month.key === this.selectedScheduleRemunerationMonthKey);
    if (selected) return selected;
    const preferred = this.scheduleRemunerationMonthKey(this.currentScheduleRemunerationMonthKey)
      || this.scheduleRemunerationMonthKey(this.scheduleRemunerationCalendar?.month_key)
      || this.scheduleRemunerationMonthKey(this.scheduleRemunerationCalendar?.events?.[0]?.date?.slice(0, 7))
      || options[0].key;
    return options.find((month) => month.key === preferred) || options[0];
  }

  get selectedScheduleRemunerationMonthIndex(): number {
    return this.scheduleRemunerationCalendarMonthOptions.findIndex((month) => month.key === this.selectedScheduleRemunerationMonth?.key);
  }

  get canGoPreviousScheduleRemunerationMonth(): boolean {
    return this.selectedScheduleRemunerationMonthIndex > 0;
  }

  get canGoNextScheduleRemunerationMonth(): boolean {
    const index = this.selectedScheduleRemunerationMonthIndex;
    return index >= 0 && index < this.scheduleRemunerationCalendarMonthOptions.length - 1;
  }

  get scheduleRemunerationCalendarMonthLabel(): string {
    const month = this.selectedScheduleRemunerationMonth;
    if (month) return month.label;
    const calendar = this.scheduleRemunerationCalendar;
    if (!calendar) return '';
    return this.scheduleRemunerationMonthLabel(calendar.month_key) || `${calendar.month_label || ''} ${calendar.year || ''}`.trim();
  }

  get scheduleRemunerationCalendarMonthEvents(): any[] {
    const month = this.selectedScheduleRemunerationMonth;
    const events = month?.events || [];
    const areaKey = this.selectedScheduleRemunerationArea;
    if (!areaKey) return events;
    return events.filter((event: any) => this.scheduleCalendarEventAreaKey(event) === areaKey);
  }

  get scheduleRemunerationCalendarMonthGroups(): any[] {
    const month = this.selectedScheduleRemunerationMonth;
    if (!month) return [];
    const areaKey = this.selectedScheduleRemunerationArea;
    return (month.groups || [])
      .map((group: any) => ({
        ...group,
        events: (group.events || []).filter((event: any) => !areaKey || this.scheduleCalendarEventAreaKey(event) === areaKey),
      }))
      .filter((group: any) => group.events.length);
  }

  get scheduleRemunerationAreaOptions(): Array<{ key: string; label: string; total: number }> {
    const month = this.selectedScheduleRemunerationMonth;
    const areaMap = new Map<string, { key: string; label: string; total: number }>();
    for (const event of month?.events || []) {
      const label = this.scheduleCalendarEventAreaLabel(event);
      if (!label || label === '(vacío)') continue;
      const key = this.scheduleCalendarEventAreaKey(event);
      const current = areaMap.get(key);
      if (current) {
        current.total += 1;
        continue;
      }
      areaMap.set(key, { key, label, total: 1 });
    }
    return [...areaMap.values()].sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  }

  get scheduleRemunerationAreaTotal(): number {
    return this.selectedScheduleRemunerationMonth?.events?.length || 0;
  }

  get scheduleRemunerationProcessOptions(): Array<{ key: string; label: string; total: number }> {
    const processEntries = new Map<string, { key: string; label: string; total: number }>();
    const monthGroups = this.scheduleRemunerationCalendarMonthGroups || [];
    for (const group of monthGroups) {
      const title = String(group?.title || '').trim();
      if (!title) continue;
      const normalized = this.normalize(title);
      let key = '';
      let label = '';
      if (normalized.includes('remuneracion')) {
        key = 'remuneraciones';
        label = 'Remuneraciones';
      } else if (normalized.includes('gestion') && normalized.includes('documental')) {
        key = 'gestion-documental-asistencia';
        label = 'Gestión documental y asistencia';
      } else if (normalized.includes('bienestar')) {
        key = 'bienestar';
        label = 'Bienestar';
      } else if (normalized.includes('compensacion')) {
        key = 'compensaciones';
        label = 'Compensaciones';
      } else if (normalized.includes('cumplimiento') || normalized.includes('legal')) {
        key = 'cumplimiento-legal';
        label = 'Cumplimiento/Legal';
      } else if (normalized.includes('relaciones') && normalized.includes('laborales')) {
        key = 'relaciones-laborales';
        label = 'Relaciones Laborales';
      } else if (normalized.includes('talento') && normalized.includes('cultura')) {
        key = 'talento-cultura';
        label = 'Talento y Cultura';
      } else if (normalized.includes('anticipo')) {
        key = 'anticipo';
        label = 'Proceso anticipos';
      } else if (normalized.includes('sueldo')) {
        key = 'sueldo';
        label = 'Proceso sueldos';
      } else if (normalized.includes('reproceso') && normalized.includes('2')) {
        key = 'reproceso-2';
        label = 'Reproceso 2';
      } else if (normalized.includes('reproceso')) {
        key = 'reproceso';
        label = 'Reproceso';
      } else if (normalized.includes('pago')) {
        key = 'pagos';
        label = 'Pagos asociados';
      } else if (normalized.includes('salida')) {
        key = 'salidas';
        label = 'Salidas';
      } else if (normalized.includes('informacion') && normalized.includes('tercero')) {
        key = 'informacion-terceros';
        label = 'Información terceros';
      } else {
        key = normalized;
        label = title;
      }
      const current = processEntries.get(key);
      if (current) {
        current.total += (group?.events || []).length;
        continue;
      }
      processEntries.set(key, {
        key,
        label,
        total: (group?.events || []).length,
      });
    }
    return [...processEntries.values()].sort((a, b) => {
      const ai = this.scheduleCalendarProcessOrder.indexOf(a.key);
      const bi = this.scheduleCalendarProcessOrder.indexOf(b.key);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return a.label.localeCompare(b.label);
    });
  }

  get selectedScheduleRemunerationProcessKey(): string {
    return this.selectedScheduleRemunerationProcess;
  }

  get selectedScheduleRemunerationAreaKey(): string {
    return this.selectedScheduleRemunerationArea;
  }

  get scheduleRemunerationCalendarMonthFilteredGroups(): any[] {
    const processKey = this.selectedScheduleRemunerationProcessKey;
    const groups = this.scheduleRemunerationCalendarMonthGroups || [];
    if (!processKey) return groups;
    return groups.filter((group: any) => this.scheduleRemunerationProcessKeyForGroup(group?.title) === processKey);
  }

  get scheduleRemunerationCalendarMonthFilteredEvents(): any[] {
    const processKey = this.selectedScheduleRemunerationProcessKey;
    const events = this.scheduleRemunerationCalendarMonthEvents || [];
    if (!processKey) return events;
    return events.filter((event: any) => this.scheduleRemunerationProcessKeyForGroup(event?.group) === processKey);
  }

  get scheduleRemunerationCalendarMonthFilteredEventCount(): number {
    return this.scheduleRemunerationCalendarMonthFilteredEvents.length;
  }

  private reconcileScheduleCalendarFiltersForMonth(monthKey: string): void {
    const month = this.scheduleRemunerationCalendarMonthOptions.find((item) => item.key === monthKey);
    const events = month?.events || [];
    if (this.selectedScheduleRemunerationArea) {
      const areaStillAvailable = events.some((event: any) => this.scheduleCalendarEventAreaKey(event) === this.selectedScheduleRemunerationArea);
      if (!areaStillAvailable) {
        this.selectedScheduleRemunerationArea = '';
      }
    }
    if (this.selectedScheduleRemunerationProcess) {
      const processStillAvailable = events
        .filter((event: any) => !this.selectedScheduleRemunerationArea || this.scheduleCalendarEventAreaKey(event) === this.selectedScheduleRemunerationArea)
        .some((event: any) => this.scheduleRemunerationProcessKeyForGroup(event?.group) === this.selectedScheduleRemunerationProcess);
      if (!processStillAvailable) {
        this.selectedScheduleRemunerationProcess = '';
      }
    }
    this.selectedScheduleRemunerationEventKey = '';
  }

  get scheduleCalendarAgendaDays(): Array<{ date: string; label: string; events: any[] }> {
    const dayMap = new Map<string, any[]>();
    for (const event of this.scheduleRemunerationCalendarMonthFilteredEvents || []) {
      const date = String(event?.date || '');
      if (!date) continue;
      const events = dayMap.get(date) || [];
      events.push(event);
      dayMap.set(date, events);
    }
    return [...dayMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, events]) => ({
        date,
        label: this.scheduleCalendarDayLabel(date),
        events: events.sort((a: any, b: any) => String(a.group).localeCompare(String(b.group)) || String(a.title).localeCompare(String(b.title))),
      }));
  }

  get showScheduleRemunerationCalendar(): boolean {
    const calendar = this.scheduleRemunerationCalendar;
    if (!calendar || !this.selectedScheduleProcess || !this.selectedScheduleMonth) return false;
    const processMatch = this.normalize(this.selectedScheduleProcess).includes('remuneraciones');
    const selectedMonth = String(this.selectedScheduleMonth).slice(5, 7);
    return processMatch && selectedMonth === String(calendar.month_number || '').padStart(2, '0');
  }

  get showScheduleRemunerationCalendarHint(): boolean {
    const calendar = this.scheduleRemunerationCalendar;
    if (!calendar || !this.selectedScheduleProcess) return false;
    return this.normalize(this.selectedScheduleProcess).includes('remuneraciones') && !this.showScheduleRemunerationCalendar;
  }

  hasScheduleRemunerationCalendar(process: string): boolean {
    return Boolean(this.scheduleRemunerationCalendar) && this.normalize(process).includes('remuneraciones');
  }

  openScheduleRemunerationCalendar(): void {
    const calendar = this.scheduleRemunerationCalendar;
    if (!calendar) return;
    this.syncScheduleRemunerationMonthSelection();
    this.selectedScheduleProcess = 'Remuneraciones';
    const monthKey = this.selectedScheduleRemunerationMonth?.key || this.selectedScheduleRemunerationMonthKey || this.scheduleRemunerationCalendar?.month_key || '';
    const month = (this.schedule?.months || []).find((item: any) => item.key === monthKey || String(item.key || '').slice(5, 7) === String(monthKey || '').slice(5, 7));
    this.selectedScheduleMonth = month?.key || monthKey;
    this.selectedScheduleActivity = '';
    this.selectedScheduleStatus = '';
    this.selectedScheduleRemunerationProcess = '';
    this.selectedScheduleRemunerationEventKey = '';
    this.scheduleSubView = 'calendar';
  }

  get scheduleRemunerationCalendarWeeks(): any[][] {
    const month = this.selectedScheduleRemunerationMonth;
    if (!month?.key) return [];
    const [year, monthNumber] = String(month.key).split('-').map((part: string) => Number(part));
    if (!year || !monthNumber) return [];
    const first = new Date(year, monthNumber - 1, 1);
    const daysInMonth = new Date(year, monthNumber, 0).getDate();
    const mondayFirstOffset = (first.getDay() + 6) % 7;
    const cells: any[] = [];
    for (let index = 0; index < mondayFirstOffset; index += 1) {
      cells.push({ blank: true });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = `${year}-${String(monthNumber).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const events = this.scheduleRemunerationCalendarMonthFilteredEvents.filter((event: any) => event.date === date);
      cells.push({ day, date, events });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ blank: true });
    }
    const weeks = [];
    for (let index = 0; index < cells.length; index += 7) {
      weeks.push(cells.slice(index, index + 7));
    }
    return weeks;
  }

  scheduleCalendarEventClass(event: any): string {
    return this.scheduleCalendarGroupClass(event?.group);
  }

  scheduleCalendarGroupClass(group: string): string {
    const processKey = this.scheduleRemunerationProcessKeyForGroup(group);
    return this.scheduleCalendarToneByProcess[processKey] || 'tone-4';
  }

  private scheduleCalendarProcessOrderIndex(group: string): number {
    const index = this.scheduleCalendarProcessOrder.indexOf(this.scheduleRemunerationProcessKeyForGroup(group));
    return index === -1 ? 99 : index;
  }

  scheduleCalendarEventKey(event: any): string {
    return [event?.date || '', event?.group || '', event?.title || ''].join('|');
  }

  isScheduleRemunerationEventActive(event: any): boolean {
    return this.scheduleCalendarEventKey(event) === this.selectedScheduleRemunerationEventKey;
  }

  get selectedScheduleRemunerationEvent(): any | null {
    if (!this.selectedScheduleRemunerationEventKey) return null;
    return this.scheduleRemunerationCalendarMonthFilteredEvents.find((event: any) => this.isScheduleRemunerationEventActive(event)) || null;
  }

  selectScheduleRemunerationEvent(event: any): void {
    const key = this.scheduleCalendarEventKey(event);
    this.selectedScheduleRemunerationEventKey = this.selectedScheduleRemunerationEventKey === key ? '' : key;
    this.cdr.detectChanges();
  }

  closeScheduleRemunerationEvent(): void {
    this.selectedScheduleRemunerationEventKey = '';
    this.cdr.detectChanges();
  }

  setScheduleCalendarMode(mode: 'calendar' | 'agenda'): void {
    this.scheduleCalendarMode = mode;
    this.selectedScheduleRemunerationEventKey = '';
    this.cdr.detectChanges();
  }

  setScheduleRemunerationProcessFilter(processKey: string): void {
    this.selectedScheduleRemunerationProcess = this.selectedScheduleRemunerationProcessKey === processKey ? '' : processKey;
    this.selectedScheduleRemunerationEventKey = '';
    this.cdr.detectChanges();
  }

  setScheduleRemunerationAreaFilter(areaKey: string): void {
    this.selectedScheduleRemunerationArea = this.selectedScheduleRemunerationAreaKey === areaKey ? '' : areaKey;
    this.selectedScheduleRemunerationProcess = '';
    this.selectedScheduleRemunerationEventKey = '';
    this.cdr.detectChanges();
  }

  private scheduleCalendarEventAreaLabel(event: any): string {
    return String(event?.area || event?.type || 'Sin área').trim();
  }

  private scheduleCalendarEventAreaKey(event: any): string {
    return this.normalize(this.scheduleCalendarEventAreaLabel(event));
  }

  scheduleRemunerationProcessKeyForGroup(value: string): string {
    const normalized = this.normalize(value);
    if (normalized.includes('remuneracion')) return 'remuneraciones';
    if (normalized.includes('gestion') && normalized.includes('documental')) return 'gestion-documental-asistencia';
    if (normalized.includes('bienestar')) return 'bienestar';
    if (normalized.includes('compensacion')) return 'compensaciones';
    if (normalized.includes('cumplimiento') || normalized.includes('legal')) return 'cumplimiento-legal';
    if (normalized.includes('relaciones') && normalized.includes('laborales')) return 'relaciones-laborales';
    if (normalized.includes('talento') && normalized.includes('cultura')) return 'talento-cultura';
    if (normalized.includes('anticipo')) return 'anticipo';
    if (normalized.includes('sueldo')) return 'sueldo';
    if (normalized.includes('reproceso') && normalized.includes('2')) return 'reproceso-2';
    if (normalized.includes('reproceso')) return 'reproceso';
    if (normalized.includes('pago')) return 'pagos';
    if (normalized.includes('salida')) return 'salidas';
    if (normalized.includes('informacion') && normalized.includes('tercero')) return 'informacion-terceros';
    return normalized;
  }

  scheduleCalendarDayLabel(value: string): string {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-CL', { weekday: 'long', day: '2-digit', month: 'long' });
  }

  scheduleCalendarEventPeriodLabel(event: any): string {
    if (this.scheduleCalendarIsMonthly) {
      return event?.month_label || this.scheduleRemunerationCalendarMonthLabel;
    }
    return this.scheduleCalendarDayLabel(event?.date || '');
  }

  get scheduleFilterSummary(): string {
    const parts = [];
    if (this.selectedScheduleProcess) parts.push(this.selectedScheduleProcess);
    if (this.selectedScheduleActivity) parts.push(this.selectedScheduleActivity);
    if (this.selectedScheduleMonth) parts.push((this.schedule?.months || []).find((month: any) => month.key === this.selectedScheduleMonth)?.label || this.selectedScheduleMonth);
    if (this.selectedScheduleStatus) parts.push(this.selectedScheduleStatus === 'done' ? 'Completados' : 'Pendientes');
    return parts.length ? parts.join(' · ') : 'Vista completa';
  }

  setScheduleSubView(view: ScheduleSubView): void {
    this.scheduleSubView = view;
    if (view === 'calendar') {
      this.syncScheduleRemunerationMonthSelection();
    }
    this.cdr.detectChanges();
  }

  private syncScheduleRemunerationMonthSelection(): void {
    const options = this.scheduleRemunerationCalendarMonthOptions;
    if (!options.length) {
      this.selectedScheduleRemunerationMonthKey = '';
      this.selectedScheduleRemunerationProcess = '';
      this.selectedScheduleRemunerationEventKey = '';
      return;
    }
    if (options.some((month) => month.key === this.selectedScheduleRemunerationMonthKey)) return;
    const preferred = this.scheduleRemunerationMonthKey(this.currentScheduleRemunerationMonthKey)
      || this.scheduleRemunerationMonthKey(this.scheduleRemunerationCalendar?.month_key)
      || this.scheduleRemunerationMonthKey(this.scheduleRemunerationCalendar?.events?.[0]?.date?.slice(0, 7))
      || options[0].key;
    this.selectedScheduleRemunerationMonthKey = options.some((month) => month.key === preferred) ? preferred : options[0].key;
    this.reconcileScheduleCalendarFiltersForMonth(this.selectedScheduleRemunerationMonthKey);
  }

  selectScheduleRemunerationMonth(monthKey: string): void {
    if (!monthKey || this.selectedScheduleRemunerationMonthKey === monthKey) return;
    this.selectedScheduleRemunerationMonthKey = monthKey;
    this.reconcileScheduleCalendarFiltersForMonth(monthKey);
    this.cdr.detectChanges();
  }

  navigateScheduleRemunerationMonth(direction: -1 | 1): void {
    const options = this.scheduleRemunerationCalendarMonthOptions;
    if (!options.length) return;
    const currentIndex = Math.max(0, this.selectedScheduleRemunerationMonthIndex);
    const nextIndex = Math.min(options.length - 1, Math.max(0, currentIndex + direction));
    if (nextIndex === currentIndex) return;
    this.selectedScheduleRemunerationMonthKey = options[nextIndex].key;
    this.reconcileScheduleCalendarFiltersForMonth(this.selectedScheduleRemunerationMonthKey);
    this.cdr.detectChanges();
  }

  private get currentScheduleRemunerationMonthKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  async exportCurrentScheduleViewPng(): Promise<void> {
    const element = document.getElementById(`schedule-${this.scheduleSubView}-export`) as HTMLElement | null;
    if (!element || this.schedulePngExporting) return;

    this.schedulePngExporting = true;
    this.scheduleError = '';
    this.cdr.detectChanges();

    try {
      await this.exportElementAsPng(element, this.schedulePngFileName());
    } catch (error) {
      console.error('Error exportando PNG de cronograma', error);
      this.scheduleError = 'No se pudo exportar la imagen PNG del cronograma.';
    } finally {
      this.schedulePngExporting = false;
      this.cdr.detectChanges();
    }
  }

  private schedulePngFileName(): string {
    const labels: Record<ScheduleSubView, string> = {
      overview: 'Vista general',
      calendar: 'Calendario operación personas',
      planning: 'Planificación equipo',
    };
    const date = new Date().toISOString().slice(0, 10);
    return `Cronograma_${this.slug(labels[this.scheduleSubView])}_${date}.png`;
  }

  private async exportElementAsPng(element: HTMLElement, fileName: string): Promise<void> {
    const rect = element.getBoundingClientRect();
    const shouldCenterVisibleWidth = this.scheduleSubView === 'overview' || this.scheduleSubView === 'calendar';
    const contentWidth = Math.max(1, Math.ceil(shouldCenterVisibleWidth ? rect.width : Math.max(rect.width, element.scrollWidth)));
    const contentHeight = Math.max(1, Math.ceil(Math.max(rect.height, element.scrollHeight)));
    const padding = shouldCenterVisibleWidth ? 28 : 0;
    const width = contentWidth + padding * 2;
    const height = contentHeight + padding * 2;
    const clone = this.cloneElementForPngExport(element, contentWidth, contentHeight);
    const exportRoot = this.wrapElementForPngExport(clone, width, height, padding);
    const serialized = new XMLSerializer().serializeToString(exportRoot);
    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
      `<foreignObject x="0" y="0" width="100%" height="100%">${serialized}</foreignObject>`,
      '</svg>',
    ].join('');
    const svgUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));

    try {
      const image = await this.loadImage(svgUrl);
      const scale = this.pngExportScale(width, height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(width * scale);
      canvas.height = Math.ceil(height * scale);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No se pudo crear canvas');
      context.scale(scale, scale);
      context.fillStyle = this.scheduleExportBackgroundColor();
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Canvas sin contenido')), 'image/png');
      });
      this.downloadBlob(blob, fileName);
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  private cloneElementForPngExport(element: HTMLElement, width: number, height: number): HTMLElement {
    const clone = element.cloneNode(true) as HTMLElement;
    const sourceElements = [element, ...Array.from(element.querySelectorAll<HTMLElement>('*'))];
    const cloneElements = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];

    sourceElements.forEach((source, index) => {
      const target = cloneElements[index];
      if (!target) return;
      const computed = window.getComputedStyle(source);
      const style = Array.from(computed)
        .map((property) => `${property}:${computed.getPropertyValue(property)};`)
        .join('');
      target.setAttribute('style', `${target.getAttribute('style') || ''};${style}`);
      if (source instanceof HTMLInputElement && target instanceof HTMLInputElement) {
        target.value = source.value;
      }
      if (source instanceof HTMLTextAreaElement && target instanceof HTMLTextAreaElement) {
        target.value = source.value;
      }
      if (source instanceof HTMLSelectElement && target instanceof HTMLSelectElement) {
        target.value = source.value;
      }
    });

    clone.querySelectorAll('[data-export-exclude="true"]').forEach((excluded) => excluded.remove());
    clone.style.width = `${width}px`;
    clone.style.minHeight = `${height}px`;
    clone.style.boxSizing = 'border-box';
    clone.style.background = this.scheduleExportBackgroundColor();
    clone.style.padding = '0';
    clone.style.margin = '0 auto';
    clone.style.transform = 'none';
    clone.style.transformOrigin = 'center top';
    return clone;
  }

  private wrapElementForPngExport(element: HTMLElement, width: number, height: number, padding: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    wrapper.style.width = `${width}px`;
    wrapper.style.minHeight = `${height}px`;
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.padding = `${padding}px`;
    wrapper.style.background = this.scheduleExportBackgroundColor();
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'flex-start';
    wrapper.style.justifyContent = 'center';
    wrapper.style.overflow = 'hidden';
    wrapper.appendChild(element);
    return wrapper;
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('No se pudo renderizar SVG'));
      image.src = url;
    });
  }

  private pngExportScale(width: number, height: number): number {
    const maxDimension = 14000;
    const baseScale = window.devicePixelRatio > 1 ? 2 : 1.5;
    return Math.max(1, Math.min(baseScale, maxDimension / Math.max(width, height)));
  }

  private scheduleExportBackgroundColor(): string {
    return this.theme === 'dark' ? '#0c111a' : '#f7f8fb';
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  toggleScheduleProcess(process: string): void {
    this.selectedScheduleProcess = this.selectedScheduleProcess === process ? '' : process;
    this.selectedScheduleActivity = '';
  }

  toggleScheduleMonth(monthKey: string): void {
    this.selectedScheduleMonth = this.selectedScheduleMonth === monthKey ? '' : monthKey;
    this.selectedScheduleActivity = '';
  }

  toggleScheduleStatus(status: 'done' | 'pending'): void {
    this.selectedScheduleStatus = this.selectedScheduleStatus === status ? '' : status;
    this.selectedScheduleActivity = '';
  }

  focusScheduleActivities(): void {
    this.selectedScheduleMonth = '';
    this.selectedScheduleActivity = '';
  }

  focusScheduleMilestones(): void {
    this.selectedScheduleStatus = '';
    this.selectedScheduleActivity = '';
  }

  selectScheduleMilestone(monthKey: string, status: 'done' | 'pending' | 'other', activity = ''): void {
    const statusFilter = status === 'done' || status === 'pending' ? status : '';
    const selectingSame = this.selectedScheduleMonth === monthKey && this.selectedScheduleActivity === activity && this.selectedScheduleStatus === statusFilter;
    if (selectingSame) {
      this.selectedScheduleMonth = '';
      this.selectedScheduleStatus = '';
      this.selectedScheduleActivity = '';
      return;
    }
    this.selectedScheduleMonth = monthKey;
    this.selectedScheduleStatus = statusFilter;
    this.selectedScheduleActivity = activity;
  }

  selectScheduleRoadmapPoint(point: any): void {
    this.selectedScheduleRoadmapPoint = point;
    this.selectedScheduleRoadmapProcess = point.process || '';
    this.selectedScheduleRoadmapMonth = '';
    this.cdr.detectChanges();
  }

  selectScheduleRoadmapMonth(monthKey: string): void {
    this.selectedScheduleRoadmapMonth = this.selectedScheduleRoadmapMonth === monthKey ? '' : monthKey;
    this.selectedScheduleRoadmapPoint = null;
    this.selectedScheduleRoadmapProcess = '';
    this.cdr.detectChanges();
  }

  isSchedulePointActive(point: any): boolean {
    if (this.selectedScheduleActivity) {
      return this.selectedScheduleActivity === point.activity && this.selectedScheduleMonth === point.month;
    }
    return this.selectedScheduleMonth === point.month || this.selectedScheduleStatus === point.status_key;
  }

  clearScheduleFilters(): void {
    this.selectedScheduleProcess = '';
    this.selectedScheduleMonth = '';
    this.selectedScheduleStatus = '';
    this.selectedScheduleActivity = '';
    this.selectedScheduleRemunerationArea = '';
    this.selectedScheduleRemunerationProcess = '';
    this.selectedScheduleRemunerationEventKey = '';
    this.selectedScheduleRoadmapProcess = '';
    this.selectedScheduleRoadmapPoint = null;
    this.selectedScheduleRoadmapMonth = '';
  }

  resetScheduleCalendarFilters(): void {
    this.selectedScheduleRemunerationArea = '';
    this.selectedScheduleRemunerationProcess = '';
    this.selectedScheduleRemunerationEventKey = '';
    this.cdr.detectChanges();
  }

  private buildQuery(includePagination = false): string {
    const params = new URLSearchParams();
    if (this.search.trim()) params.set('search', this.search.trim());
    if (this.topicFilter.trim()) params.set('topic', this.topicFilter.trim());
    if (this.topicScope) params.set('topic_scope', this.topicScope);
    if (this.fromDate) params.set('from_date', this.fromDate);
    if (this.toDate) params.set('to_date', this.toDate);
    if (this.dateMode !== 'created') params.set('date_mode', this.dateMode);
    if (this.level) params.set('level', this.level);
    params.set('risk_window', this.riskTrendWindow);
    params.set('topic_window_days', String(this.topicWindowDays || 30));
    params.set('sort_by', this.ticketSortBy);
    params.set('sort_dir', this.ticketSortDir);
    if (this.risk) params.set('risk', this.risk);
    if (this.createdRisk) params.set('created_risk', this.createdRisk);
    if (this.unassignedOnly) params.set('unassigned', 'true');
    for (const [key, values] of Object.entries(this.selected)) {
      values.forEach((value) => params.append(key, value));
    }
    this.interactions.forEach((value) => params.append('interactions', value));
    if (includePagination) {
      params.set('limit', String(this.pageSize));
      params.set('offset', String(this.page * this.pageSize));
    }
    return params.toString();
  }

  private buildTicketsQuery(): string {
    const params = new URLSearchParams(this.buildQuery(true));
    if (this.ticketRisk) {
      params.delete('risk');
      params.delete('estado');
      params.set('risk', this.ticketRisk);
      this.unresolvedStateValues().forEach((value) => params.append('estado', value));
      params.delete('interactions');
    }
    return params.toString();
  }

  private async fetchJson(url: string, timeoutMs = this.apiRequestTimeoutMs): Promise<any> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} en ${url}`);
      }
      return response.json();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error(`La carga superó ${Math.round(timeoutMs / 1000)} segundos. Intenta nuevamente o revisa el backend.`);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private async postJson(url: string, payload: any, timeoutMs = this.apiRequestTimeoutMs): Promise<any> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} en ${url}`);
      }
      return response.json();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error(`La carga superó ${Math.round(timeoutMs / 1000)} segundos. Intenta nuevamente o revisa el backend.`);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private monthBounds(dateString: string): { from: string; to: string } {
    const dt = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return { from: '', to: '' };
    const pad = (value: number) => String(value).padStart(2, '0');
    const from = new Date(dt.getFullYear(), dt.getMonth(), 1);
    const to = new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
    return {
      from: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`,
      to: `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`,
    };
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private slug(value: string): string {
    return this.normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  displayValue(key: FilterKey | 'risk' | 'created_risk' | 'interactions' | 'trend', value: unknown): string {
    const text = String(value ?? '').trim();
    const normalized = this.normalize(text);
    if (!text) return '';

    if (key === 'estado') {
      if (normalized === 'open' || normalized === 'abierto') return 'Abierto';
      if (normalized === 'pending' || normalized === 'pendiente') return 'Pendiente';
      if (normalized === 'new' || normalized === 'nuevo') return 'Nuevo';
      if (normalized === 'closed' || normalized === 'cerrado') return 'Cerrado';
      if (normalized === 'resolved' || normalized === 'resuelto') return 'Resuelto';
    }

    if (key === 'prioridad') {
      if (normalized === 'low' || normalized === 'baja') return 'Baja';
      if (normalized === 'medium' || normalized === 'media') return 'Media';
      if (normalized === 'high' || normalized === 'alta') return 'Alta';
      if (normalized === 'urgent' || normalized === 'urgente') return 'Urgente';
    }

    if (key === 'tipo') {
      if (normalized === 'question' || normalized === 'pregunta') return 'Pregunta';
      if (normalized === 'incident' || normalized === 'incidente') return 'Incidente';
      if (normalized === 'problem' || normalized === 'problema') return 'Problema';
      if (normalized === 'task' || normalized === 'tarea') return 'Tarea';
    }

    if (key === 'sla') {
      if (normalized === 'within sla' || normalized === 'dentro de sla') return 'Dentro de SLA';
      if (normalized === 'sla violated' || normalized === 'fuera de sla') return 'Fuera de SLA';
    }

    if (key === 'risk') {
      if (normalized.includes('riesgo bajo') || normalized.includes('low')) return 'Riesgo bajo';
      if (normalized.includes('riesgo medio') || normalized.includes('medium')) return 'Riesgo medio';
      if (normalized.includes('riesgo alto') || normalized.includes('high')) return 'Riesgo alto';
      if (normalized === 'low') return 'Bajo';
      if (normalized === 'medium') return 'Medio';
      if (normalized === 'high') return 'Alto';
    }

    if (key === 'created_risk') {
      if (normalized === 'zero-one' || normalized === 'zero_one' || normalized === '0-1' || normalized === '0_1') return '0 - 1 días';
      if (normalized === 'two' || normalized === '2') return '2 días';
      if (normalized === 'three' || normalized === '3') return '3 días';
      if (normalized === 'low') return '0 - 3 días';
      if (normalized === 'medium') return '4 - 7 días';
      if (normalized === 'high') return '8+ días';
    }

    if (key === 'interactions') {
      if (normalized === '1_interaction') return '1 interacción';
      if (normalized === '2_interactions') return '2 interacciones';
      if (normalized === '3_interactions') return '3 interacciones';
      if (normalized === '4_plus') return '4 o más interacciones';
    }

    if (key === 'trend' && normalized === 'backlog') return 'Pendientes';

    return text;
  }

  ticketValue(key: FilterKey, value: unknown): string {
    return this.displayValue(key, value) || '—';
  }

  formatNumber(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    return new Intl.NumberFormat('es-CL').format(Number(value));
  }

  metricsMonthLabel(key: unknown, fallback: unknown = ''): string {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const keyText = String(key || '');
    const keyMatch = keyText.match(/^(\d{4})-(\d{2})$/);
    if (keyMatch) {
      const monthIndex = Number(keyMatch[2]) - 1;
      if (monthIndex >= 0 && monthIndex < months.length) return `${months[monthIndex]} ${keyMatch[1]}`;
    }
    const fallbackText = String(fallback || keyText);
    const fallbackMatch = fallbackText.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Ene|Abr|Ago|Dic)\s+(\d{4})$/i);
    if (fallbackMatch) {
      const monthMap: Record<string, string> = {
        jan: 'Ene', feb: 'Feb', mar: 'Mar', apr: 'Abr', may: 'May', jun: 'Jun',
        jul: 'Jul', aug: 'Ago', sep: 'Sep', oct: 'Oct', nov: 'Nov', dec: 'Dic',
        ene: 'Ene', abr: 'Abr', ago: 'Ago', dic: 'Dic',
      };
      return `${monthMap[fallbackMatch[1].toLowerCase()] || fallbackMatch[1]} ${fallbackMatch[2]}`;
    }
    return fallbackText;
  }

  trendDateLabel(point: any): string {
    const label = String(point?.label || point?.key || '');
    return label.split('|')[0]?.trim() || label;
  }

  trendDayLabel(point: any): string {
    const label = String(point?.label || '');
    return label.split('|')[1]?.trim() || '';
  }

  formatPercent(value: unknown): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(1)}%`;
  }

  formatHours(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return `${num.toFixed(num >= 10 ? 0 : 1)} h`;
  }

  private todayDateKey(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
  }

  private todayBounds(): { from: string; to: string } {
    const today = this.todayDateKey();
    return { from: today, to: today };
  }

  private isTodayTicketWindowActive(): boolean {
    const today = this.todayDateKey();
    return this.fromDate === today && this.toDate === today && this.dateMode === 'created';
  }

  private todayTimeBounds(): { from: number; to: number } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    return { from: start, to: end };
  }

  get topicWindowLabel(): string {
    return this.topicWindowOptions.find((option) => option.value === this.topicWindowDays)?.label || `${this.topicWindowDays} días`;
  }

  private mapTopics(items: any[] = []): TopicRow[] {
    return items.map((item: any) => ({
      label: item.label,
      value: Number(item.value) || 0,
      recent: Number(item.recent) || 0,
      previous: Number(item.previous) || 0,
      change: Number(item.change) || 0,
      change_pct: Number(item.change_pct) || 0,
      direction: item.direction === 'up' || item.direction === 'down' ? item.direction : 'flat',
    }));
  }

  displayDate(value: string | undefined): string {
    if (!value) return '—';
    const dt = new Date(`${value}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return value;
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }).format(dt);
  }

  applyTheme(): void {
    const lightBg = '#ffffff';
    const darkBg = '#0b1118';
    const themeBg = this.theme === 'light' ? lightBg : darkBg;
    document.documentElement.dataset['theme'] = this.theme;
    document.body.dataset['theme'] = this.theme;
    this.shellBackground = themeBg;
    document.documentElement.style.setProperty('--page-bg', themeBg);
    document.body.style.setProperty('--page-bg', themeBg);
    document.documentElement.style.background = themeBg;
    document.body.style.background = themeBg;
    document.documentElement.style.backgroundColor = themeBg;
    document.body.style.backgroundColor = themeBg;
    document.documentElement.style.backgroundImage = 'none';
    document.body.style.backgroundImage = 'none';
    const shell = document.querySelector('.app-shell') as HTMLElement | null;
    if (shell) {
      shell.style.setProperty('--page-bg', themeBg);
      shell.style.background = themeBg;
      shell.style.backgroundColor = themeBg;
      shell.style.backgroundImage = 'none';
    }
    localStorage.setItem('bi-theme-v2', this.theme);
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    this.applyTheme();
  }

  toggleFilter(key: FilterKey): void {
    this.openFilter = this.openFilter === key ? null : key;
  }

  toggleFiltersPanel(): void {
    if (this.filtersOpen) {
      this.closeFiltersPanel();
      return;
    }
    if (this.filtersCloseTimer) {
      clearTimeout(this.filtersCloseTimer);
      this.filtersCloseTimer = null;
    }
    this.filtersClosing = false;
    this.filtersOpen = true;
    this.cdr.detectChanges();
  }

  private closeFiltersPanel(): void {
    if (!this.filtersOpen && !this.filtersClosing) return;
    if (this.filtersCloseTimer) clearTimeout(this.filtersCloseTimer);
    this.openFilter = null;
    this.filtersOpen = false;
    this.filtersClosing = true;
    this.cdr.detectChanges();
    this.filtersCloseTimer = setTimeout(() => {
      this.filtersClosing = false;
      this.filtersCloseTimer = null;
      this.cdr.detectChanges();
    }, 220);
  }

  isSelected(key: FilterKey, value: string): boolean {
    return this.selected[key].includes(value);
  }

  selectValue(key: FilterKey, value: string, checked: boolean): void {
    const current = new Set(this.selected[key]);
    if (checked) current.add(value);
    else current.delete(value);
    this.selected[key] = [...current];
    this.page = 0;
    this.cdr.detectChanges();
    this.requestRefresh();
  }

  private sameValues(current: string[], next: string[]): boolean {
    if (current.length !== next.length) return false;
    const currentSet = new Set(current.map((value) => this.normalize(value)));
    return next.every((value) => currentSet.has(this.normalize(value)));
  }

  isFilterSelectionActive(key: FilterKey, values: string[]): boolean {
    return this.sameValues(this.selected[key], values);
  }

  private toggleSelection(key: FilterKey, values: string[]): boolean {
    if (this.sameValues(this.selected[key], values)) {
      this.selected[key] = [];
      this.page = 0;
      return false;
    }
    this.selected[key] = values;
    this.page = 0;
    return true;
  }

  private setSelection(key: FilterKey, values: string[]): void {
    this.selected[key] = values;
    this.page = 0;
  }

  private matchOptionsOrNeedles(key: FilterKey, needles: string[]): string[] {
    const matches = this.matchOptions(key, needles);
    return matches.length ? matches : needles;
  }

  applyPriority(label: string): void {
    this.toggleSelection('prioridad', [label]);
    this.requestRefresh();
  }

  applyStateLabel(label: string): void {
    this.toggleSelection('estado', [label]);
    this.requestRefresh();
  }

  selectedCount(key: FilterKey): string {
    const values = this.selected[key];
    if (!values.length) return '';
    if (values.length === 1) return this.displayValue(key, values[0]);
    return `${values.length} seleccionados`;
  }

  filterOptions(key: FilterKey): string[] {
    const raw = this.filtersMeta?.filters?.[key] || [];
    const term = this.normalize(this.filterSearch[key]);
    if (!term) return raw;
    return raw.filter((value: string) => this.normalize(value).includes(term) || this.normalize(this.displayValue(key, value)).includes(term));
  }

  clearFilters(): void {
    clearTimeout(this.trendClickTimer || undefined);
    this.trendClickTimer = null;
    this.trendPanStart = null;
    this.search = '';
    this.topicFilter = '';
    this.topicScope = '';
    this.topicsSnapshot = null;
    this.recentTopicsSnapshot = null;
    this.resolvedTopicsSnapshot = null;
    this.fromDate = '';
    this.toDate = '';
    this.dateMode = 'created';
    this.trendSelectionMetric = 'backlog';
    this.risk = '';
    this.createdRisk = '';
    this.unassignedOnly = false;
    this.ticketRisk = '';
    this.interactions = [];
    for (const key of Object.keys(this.selected) as FilterKey[]) {
      this.selected[key] = [];
    }
    this.page = 0;
    this.requestRefresh();
  }

  resetActiveViewFilters(): void {
    this.viewMenuOpen = false;
    if (this.activeView === 'schedule') {
      if (this.scheduleSubView === 'calendar') {
        this.resetScheduleCalendarFilters();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.scheduleSubView = 'overview';
      this.clearScheduleFilters();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (this.activeView === 'satisfaction') {
      this.resetSatisfactionToCurrentPeriod();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (this.activeView === 'documents') {
      this.resetDocumentFilters();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    this.clearFilters();
  }

  resetAllFilters(): void {
    this.viewMenuOpen = false;
    this.closeFiltersPanel();
    this.selectedMetricsMonthKey = '';
    this.selectedMetricsArea = '';
    this.scheduleSubView = 'overview';
    this.clearScheduleFilters();
    this.clearSatisfactionFilters();
    this.selectedSatisfactionTrendMetric = '';
    this.satisfactionTrendHoverIndex = null;
    this.satisfactionTrendWindow = 3;
    this.satisfactionTrendCacheKey = '';
    this.satisfactionTrendCache = null;
    this.resetDocumentFilters();
    this.clearFilters();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  requestRefresh(): void {
    clearTimeout((this as any)._refreshTimer);
    (this as any)._refreshTimer = setTimeout(() => this.loadData(), 90);
  }

  setLevel(level: string): void {
    this.level = level;
    this.requestRefresh();
  }

  setRiskTrendWindow(window: RiskTrendWindow): void {
    if (this.riskTrendWindow === window) return;
    this.riskTrendWindow = window;
    this.riskTrendSelectedIndex = null;
    this.requestRefresh();
  }

  scrollToSegment(id: 'estado' | 'temas' | 'tendencias' | 'resumen' | 'tickets'): void {
    const target = document.getElementById(`segment-${id}`);
    if (!target) {
      return;
    }

    const topbar = document.querySelector('.app-shell .topbar') as HTMLElement | null;
    const topbarOffset = topbar?.getBoundingClientRect().height ?? 104;
    const targetTop = window.scrollY + target.getBoundingClientRect().top;
    const offset = topbarOffset + 18;

    window.scrollTo({
      top: Math.max(0, targetTop - offset),
      behavior: 'smooth',
    });
  }

  applyKpi(kind: 'total' | 'unresolved' | 'unassigned' | 'sla_within' | 'sla_violated' | 'risk_high' | 'avg_first_response'): void {
    if (kind === 'total' || kind === 'avg_first_response') {
      return;
    }
    if (kind === 'unresolved') {
      const values = this.unresolvedStateValues();
      if (this.sameValues(this.selected.estado, values) && !this.risk) {
        this.selected.estado = [];
      } else {
        this.setSelection('estado', values);
      }
      this.interactions = [];
    }
    if (kind === 'unassigned') {
      this.unassignedOnly = !this.unassignedOnly;
      if (this.unassignedOnly) {
        this.selected.estado = this.unresolvedStateValues();
        this.selected.agente = this.unassignedAgentValues();
        this.risk = '';
        this.interactions = [];
      } else if (this.sameValues(this.selected.agente, this.unassignedAgentValues())) {
        this.selected.agente = [];
      }
    }
    if (kind === 'sla_within') {
      this.toggleSelection('sla', this.slaValues('within'));
    }
    if (kind === 'sla_violated') {
      this.toggleSelection('sla', this.slaValues('outside'));
    }
    if (kind === 'risk_high') {
      if (this.isKpiActive('risk_high')) {
        this.risk = '';
      } else {
        this.risk = 'high';
        this.selected.estado = this.unresolvedStateValues();
        this.clearUnassignedFilter();
      }
      this.interactions = [];
    }
    this.page = 0;
    this.requestRefresh();
  }

  isKpiActive(kind: 'total' | 'unresolved' | 'unassigned' | 'sla_within' | 'sla_violated' | 'risk_high' | 'avg_first_response'): boolean {
    if (kind === 'unresolved') return this.sameValues(this.selected.estado, this.unresolvedStateValues());
    if (kind === 'unassigned') return this.unassignedOnly || (this.sameValues(this.selected.estado, this.unresolvedStateValues()) && this.sameValues(this.selected.agente, this.unassignedAgentValues()));
    if (kind === 'sla_within') return this.sameValues(this.selected.sla, this.slaValues('within'));
    if (kind === 'sla_violated') return this.sameValues(this.selected.sla, this.slaValues('outside'));
    if (kind === 'risk_high') return this.risk === 'high' && this.sameValues(this.selected.estado, this.unresolvedStateValues());
    return false;
  }

  isUnresolvedRiskActive(risk: 'medium' | 'high'): boolean {
    return this.ticketRisk === risk;
  }

  applyTicketRisk(risk: 'medium' | 'high'): void {
    this.ticketRisk = this.ticketRisk === risk ? '' : risk;
    if (this.ticketRisk) {
      this.clearUnassignedFilter();
    }
    this.page = 0;
    void this.loadTicketsPage();
  }

  private unresolvedStateValues(): string[] {
    return this.matchOptionsOrNeedles('estado', ['open', 'pending', 'nuevo']);
  }

  private unassignedAgentValues(): string[] {
    return this.matchOptionsOrNeedles('agente', ['No Agent', 'Sin agente', 'Unassigned']);
  }

  private clearUnassignedFilter(): void {
    this.unassignedOnly = false;
    if (this.sameValues(this.selected.agente, this.unassignedAgentValues())) {
      this.selected.agente = [];
    }
  }

  private stateValues(value: SummaryState): string[] {
    if (value === 'open') return this.matchOptionsOrNeedles('estado', ['open', 'nuevo']);
    if (value === 'pending') return this.matchOptionsOrNeedles('estado', ['pending']);
    return this.matchOptionsOrNeedles('estado', ['closed', 'resolved', 'cerrado', 'resuelto']);
  }

  private slaValues(value: SummarySla): string[] {
    return value === 'within'
      ? this.matchOptionsOrNeedles('sla', ['within sla'])
      : this.matchOptionsOrNeedles('sla', ['sla violated']);
  }

  private matchOptions(key: FilterKey, needles: string[]): string[] {
    const raw = this.filtersMeta?.filters?.[key] || [];
    return raw.filter((value: string) => needles.some((needle) => this.normalize(value) === this.normalize(needle)));
  }

  applyMonth(monthKey: string): void {
    const bounds = this.monthBounds(monthKey);
    if (this.fromDate === bounds.from && this.toDate === bounds.to) {
      this.fromDate = '';
      this.toDate = '';
      this.dateMode = 'created';
      this.page = 0;
      this.requestRefresh();
      return;
    }
    this.fromDate = bounds.from;
    this.toDate = bounds.to;
    this.dateMode = 'created';
    this.page = 0;
    this.requestRefresh();
  }

  applyMonthGroup(monthKey: string, group: string): void {
    const bounds = this.monthBounds(monthKey);
    const groupValues = this.matchByLabel('grupo', group);
    if (this.fromDate === bounds.from && this.toDate === bounds.to && this.sameValues(this.selected.grupo, groupValues)) {
      this.fromDate = '';
      this.toDate = '';
      this.dateMode = 'created';
      this.selected.grupo = [];
      this.page = 0;
      this.requestRefresh();
      return;
    }
    this.fromDate = bounds.from;
    this.toDate = bounds.to;
    this.dateMode = 'created';
    this.selected.grupo = groupValues;
    this.page = 0;
    this.requestRefresh();
  }

  applyPeriodRisk(risk: RiskKey): void {
    this.ticketRisk = '';
    this.clearUnassignedFilter();
    if (this.risk === risk && this.sameValues(this.selected.estado, this.unresolvedStateValues())) {
      this.risk = '';
    } else {
      this.risk = risk;
      this.selected.estado = this.unresolvedStateValues();
    }
    this.interactions = [];
    this.page = 0;
    this.requestRefresh();
  }

  applyCreatedPeriodRisk(risk: CreatedRiskKey): void {
    this.ticketRisk = '';
    this.clearUnassignedFilter();
    if (this.createdRisk === risk && this.sameValues(this.selected.estado, this.unresolvedStateValues())) {
      this.createdRisk = '';
    } else {
      this.createdRisk = risk;
      this.selected.estado = this.unresolvedStateValues();
    }
    this.interactions = [];
    this.page = 0;
    this.requestRefresh();
  }

  applySummaryState(value: SummaryState): void {
    this.toggleSelection('estado', this.stateValues(value));
    if (value === 'closed') {
      this.risk = '';
      this.createdRisk = '';
      this.clearUnassignedFilter();
    } else {
      this.interactions = [];
    }
    this.page = 0;
    this.requestRefresh();
  }

  applySummarySla(value: SummarySla): void {
    this.toggleSelection('sla', this.slaValues(value));
    this.page = 0;
    this.requestRefresh();
  }

  applySummaryInteractions(value: SummaryInteraction): void {
    this.interactions = this.interactions.includes(value) ? [] : [value];
    if (this.interactions.length) {
      this.risk = '';
      this.createdRisk = '';
      this.clearUnassignedFilter();
      this.selected.estado = this.stateValues('closed');
    }
    this.page = 0;
    this.requestRefresh();
  }

  isMonthActive(monthKey: string): boolean {
    const bounds = this.monthBounds(monthKey);
    return this.fromDate === bounds.from && this.toDate === bounds.to;
  }

  isRiskActive(value: RiskKey): boolean {
    return this.risk === value;
  }

  isCreatedRiskActive(value: CreatedRiskKey): boolean {
    return this.createdRisk === value;
  }

  isSummaryLabelActive(kind: SummaryKind, label: string): boolean {
    const key = this.summaryFilterKey(kind);
    return this.selected[key].some((value) => this.normalize(value) === this.normalize(label));
  }

  applySummaryRow(row: SummaryRow, kind: SummaryKind, immediate = false): void {
    const key = this.summaryFilterKey(kind);
    const values = this.matchByLabel(key, row.label);
    if (this.sameValues(this.selected[key], values)) {
      this.selected[key] = [];
      this.page = 0;
    } else {
      this.setSummaryScopeValues(kind, values);
    }
    if (immediate) {
      this.areaFilterBusy = true;
      clearTimeout((this as any)._refreshTimer);
      this.cdr.detectChanges();
      void this.loadData();
      return;
    }
    this.requestRefresh();
  }

  applyAreaQuadrant(row: SummaryRow): void {
    if (this.areaFilterBusy || row.label === 'Otras áreas') return;
    this.applySummaryRow(row, 'group');
  }

  applySummaryRowState(row: SummaryRow, kind: SummaryKind, value: SummaryState): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const stateValues = this.stateValues(value);
    if (this.sameValues(this.selected[scopeKey], scopeValues) && this.sameValues(this.selected.estado, stateValues)) {
      this.selected[scopeKey] = [];
      this.selected.estado = [];
      this.page = 0;
    } else {
      this.setSummaryScope(row, kind);
      this.selected.estado = stateValues;
    }
    if (value === 'closed') {
      this.risk = '';
      this.createdRisk = '';
      this.clearUnassignedFilter();
    } else {
      this.interactions = [];
    }
    this.requestRefresh();
  }

  applySummaryRowUnresolved(row: SummaryRow, kind: SummaryKind): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const stateValues = this.unresolvedStateValues();
    if (this.sameValues(this.selected[scopeKey], scopeValues) && this.sameValues(this.selected.estado, stateValues)) {
      this.selected.estado = [];
      this.page = 0;
    } else {
      this.setSummaryScope(row, kind);
      this.selected.estado = stateValues;
      this.interactions = [];
      this.risk = '';
      this.clearUnassignedFilter();
    }
    this.requestRefresh();
  }

  applySummaryRowSla(row: SummaryRow, kind: SummaryKind, value: SummarySla): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const slaValues = this.slaValues(value);
    if (this.sameValues(this.selected[scopeKey], scopeValues) && this.sameValues(this.selected.sla, slaValues)) {
      this.selected[scopeKey] = [];
      this.selected.sla = [];
      this.page = 0;
    } else {
      this.setSummaryScope(row, kind);
      this.selected.sla = slaValues;
    }
    this.requestRefresh();
  }

  applySummaryRowInteractions(row: SummaryRow, kind: SummaryKind, value: SummaryInteraction): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    if (this.sameValues(this.selected[scopeKey], scopeValues) && this.interactions.includes(value)) {
      this.selected[scopeKey] = [];
      this.interactions = [];
      this.page = 0;
    } else {
      this.setSummaryScope(row, kind);
      this.interactions = [value];
      this.risk = '';
      this.createdRisk = '';
      this.clearUnassignedFilter();
      this.selected.estado = this.stateValues('closed');
    }
    this.requestRefresh();
  }

  private setSummaryScope(row: SummaryRow, kind: SummaryKind): void {
    this.setSummaryScopeValues(kind, this.matchByLabel(this.summaryFilterKey(kind), row.label));
  }

  private setSummaryScopeValues(kind: SummaryKind, values: string[]): void {
    const key = this.summaryFilterKey(kind);
    const oppositeKey = kind === 'group' ? 'grupo' : 'agente';
    this.selected[key] = values;
    this.selected[oppositeKey] = [];
    this.page = 0;
  }

  private summaryFilterKey(kind: SummaryKind): FilterKey {
    return kind === 'group' ? 'agente' : 'grupo';
  }

  private matchByLabel(key: FilterKey, label: string): string[] {
    const raw = this.filtersMeta?.filters?.[key] || [];
    return raw.filter((value: string) => this.normalize(value) === this.normalize(label));
  }

  removeChip(kind: string, key?: FilterKey, value?: string): void {
    if (kind === 'search') this.search = '';
    if (kind === 'topic') {
      this.topicFilter = '';
      this.topicsSnapshot = null;
      this.recentTopicsSnapshot = null;
      this.resolvedTopicsSnapshot = null;
    }
    if (kind === 'range') {
      clearTimeout(this.trendClickTimer || undefined);
      this.trendClickTimer = null;
      this.trendPanStart = null;
      this.fromDate = '';
      this.toDate = '';
      this.dateMode = 'created';
    }
    if (kind === 'risk') this.risk = '';
    if (kind === 'created_risk') this.createdRisk = '';
    if (kind === 'interactions') this.interactions = [];
    if (kind === 'filter' && key && value !== undefined) {
      this.selected[key] = this.selected[key].filter((item) => item !== value);
    }
    this.requestRefresh();
  }

  resetPeriodFilter(): void {
    clearTimeout(this.trendClickTimer || undefined);
    this.trendClickTimer = null;
    this.trendPanStart = null;
    this.fromDate = '';
    this.toDate = '';
    this.dateMode = 'created';
    this.trendSelectionMetric = 'backlog';
    this.page = 0;
    this.requestRefresh();
  }

  get activeChips(): Array<{ kind: string; label: string; value: string; rawValue?: string; key?: FilterKey }> {
    const chips: Array<{ kind: string; label: string; value: string; rawValue?: string; key?: FilterKey }> = [];
    if (this.search) chips.push({ kind: 'search', label: 'Búsqueda', value: this.search });
    if (this.topicFilter) chips.push({ kind: 'topic', label: 'Tema', value: `${this.topicFilter} · ${this.topicWindowLabel}` });
    if (this.fromDate || this.toDate) chips.push({ kind: 'range', label: 'Período', value: `${this.fromDate || 'inicio'} -> ${this.toDate || 'fin'}` });
    if (this.risk) chips.push({ kind: 'risk', label: 'Riesgo', value: this.displayValue('risk', this.risk) });
    if (this.createdRisk) chips.push({ kind: 'created_risk', label: 'Riesgo creado', value: this.displayValue('created_risk', this.createdRisk) });
    if (this.interactions.length) chips.push({ kind: 'interactions', label: 'Interacciones', value: this.interactions.map((value) => this.displayValue('interactions', value)).join(', ') });
    for (const [key, values] of Object.entries(this.selected) as Array<[FilterKey, string[]]>) {
      values.forEach((value) => chips.push({ kind: 'filter', label: this.filterLabels[key] || key, value: this.displayValue(key, value), rawValue: value, key }));
    }
    return chips;
  }

  get filterValues(): Array<{ key: FilterKey; label: string; options: string[] }> {
    return this.filterDefs.map((def) => ({ key: def.key, label: def.label, options: this.filterOptions(def.key) }));
  }

  get summaryRowsGroup(): SummaryRow[] {
    return this.sortedSummaryRows(this.summary?.by_group_detail);
  }

  get summaryRowsAgent(): SummaryRow[] {
    const rows = (this.summary?.by_agent_detail || []) as SummaryRow[];
    return this.sortedSummaryRows(this.selected.agente.length ? rows : this.withAllResponsibleRows(rows));
  }

  get summaryWindowRowsGroup(): SummaryRow[] {
    return this.sortedSummaryRows(this.summary?.by_group_window_detail || this.summary?.by_group_detail);
  }

  get summaryWindowRowsAgent(): SummaryRow[] {
    const rows = (this.summary?.by_agent_window_detail || this.summary?.by_agent_detail || []) as SummaryRow[];
    return this.sortedSummaryRows(this.selected.agente.length ? rows : this.withAllResponsibleRows(rows));
  }

  get activeSummaryRows(): SummaryRow[] {
    const allRows = this.activeSummaryKind === 'group' ? this.summaryRowsGroup : this.summaryRowsAgent;
    const rows = allRows.map((row) => this.withOperationsVisiblePeriod(row));
    return this.filterActiveSummaryRows(this.sortOperationsRows(this.withVisibleSummaryData(rows)));
  }

  private withOperationsVisiblePeriod(row: SummaryRow): SummaryRow {
    const series = this.operationsDetailTrendSeries(row);
    if (!series.length) {
      if (this.topicWindowDays === 0 && !this.fromDate && !this.toDate) return row;
      return { ...row, total: 0, unresolved: 0, open: 0, pending: 0, closed: 0 };
    }
    const total = series.reduce((sum, point: any) => sum + (Number(point.received) || 0), 0);
    const closed = series.reduce((sum, point: any) => sum + (Number(point.closed) || 0), 0);
    const unresolved = Number(series[series.length - 1]?.backlog) || 0;
    const currentUnresolved = Math.max(1, (row.open || 0) + (row.pending || 0));
    const openShare = Math.max(0, Math.min(1, (row.open || 0) / currentUnresolved));
    const open = unresolved ? Math.round(unresolved * openShare) : 0;
    return {
      ...row,
      total,
      closed,
      unresolved,
      open,
      pending: Math.max(0, unresolved - open),
    };
  }

  private refreshSelectedOperationsDetail(): void {
    if (!this.selectedOperationsDetailRow) return;
    const label = this.selectedOperationsDetailRow.label;
    this.selectedOperationsDetailRow = this.activeSummaryRows.find((row) => row.label === label) || null;
  }

  private withVisibleSummaryData(rows: SummaryRow[]): SummaryRow[] {
    if (this.activeSummaryKind !== 'agent') return rows;
    return rows.filter((row) => this.summaryHasAnyData(row));
  }

  private summaryHasAnyData(row: SummaryRow): boolean {
    return [
      row.total,
      row.unresolved,
      row.open,
      row.pending,
      row.closed,
      row.within,
      row.outside,
      row.risk_low,
      row.risk_medium,
      row.risk_high,
      row.response_recent,
      row.response_previous,
    ].some((value) => Number(value) > 0)
      || Object.values(row.closed_interactions || {}).some((value) => Number(value) > 0)
      || (row.top_topics || []).some((topic) => Number(topic.value || topic.recent) > 0)
      || (row.resolved_topics || []).some((topic) => Number(topic.value || topic.recent) > 0)
      || (row.trend || []).some((point: any) => Number(point.received || point.closed || point.backlog) > 0);
  }

  private filterActiveSummaryRows(rows: SummaryRow[]): SummaryRow[] {
    const key = this.summaryFilterKey(this.activeSummaryKind);
    const selected = this.selected[key];
    if (!selected.length) return rows;
    const selectedLabels = new Set(selected.map((value) => this.normalize(value)));
    return rows.filter((row) => selectedLabels.has(this.normalize(row.label)));
  }

  private withAllResponsibleRows(rows: SummaryRow[] | undefined): SummaryRow[] {
    const output = [...(rows || [])];
    const known = new Set(output.map((row) => row.label));
    for (const label of this.allResponsibleLabels) {
      if (!known.has(label)) {
        output.push(this.emptySummaryRow(label));
      }
    }
    return output;
  }

  private get allResponsibleLabels(): string[] {
    return (this.filtersMeta?.filters?.grupo || []).filter((label: string) => !!label);
  }

  private emptySummaryRow(label: string): SummaryRow {
    return {
      label,
      total: 0,
      unresolved: 0,
      open: 0,
      pending: 0,
      closed: 0,
      within: 0,
      outside: 0,
      risk_low: 0,
      risk_medium: 0,
      risk_high: 0,
      closed_interactions: {
        '1_interaction': 0,
        '2_interactions': 0,
        '3_interactions': 0,
        '4_plus': 0,
      },
      top_topics: [],
      resolved_topics: [],
      trend: [],
    };
  }

  get activeSummaryTitle(): string {
    return this.activeSummaryKind === 'group' ? 'Resumen por área' : 'Resumen por responsable';
  }

  get activeSummaryUnresolvedTotal(): number {
    return this.activeSummaryRows.reduce((sum, row) => sum + (row.unresolved || 0), 0);
  }

  get activeSummaryReceivedTotal(): number {
    return this.activeSummaryRows.reduce((sum, row) => sum + (row.total || 0), 0);
  }

  get activeSummaryClosedTotal(): number {
    return this.activeSummaryRows.reduce((sum, row) => sum + (row.closed || 0), 0);
  }

  get activeSummarySlaAverage(): number {
    const totals = this.activeSummaryRows.reduce((acc, row) => {
      acc.within += row.within || 0;
      acc.total += (row.within || 0) + (row.outside || 0);
      return acc;
    }, { within: 0, total: 0 });
    return totals.total ? (totals.within / totals.total) * 100 : 0;
  }

  get activeSummaryMaxLoad(): SummaryRow | null {
    return this.activeSummaryRows.reduce<SummaryRow | null>((top, row) => (!top || (row.total || 0) > (top.total || 0) ? row : top), null);
  }

  get operationsKpis(): Array<{ label: string; value: string; note: string }> {
    const maxLoad = this.activeSummaryMaxLoad;
    return [
      { label: 'Total recibidos', value: this.formatNumber(this.activeSummaryReceivedTotal), note: this.topicWindowLabel },
      { label: 'Total sin resolver', value: this.formatNumber(this.activeSummaryUnresolvedTotal), note: 'Abiertos + pendientes' },
      { label: 'SLA promedio', value: this.formatPercent(this.activeSummarySlaAverage), note: 'Primera respuesta' },
      { label: 'Total cerrados', value: this.formatNumber(this.activeSummaryClosedTotal), note: 'Closed + Resolved' },
      { label: 'Mayor carga', value: maxLoad ? this.formatNumber(maxLoad.total) : '—', note: maxLoad?.label || 'Sin datos' },
    ];
  }

  get operationsExecutiveKpis(): Array<{ key: OperationsKpiKey; icon: string; label: string; value: string; note: string; delta: string; tone: string }> {
    return [
      { key: 'total', icon: '↗', label: 'Total recibidos', value: this.formatNumber(this.activeSummaryReceivedTotal), note: this.topicWindowLabel, delta: this.operationsDeltaText('total'), tone: 'cyan' },
      { key: 'unresolved', icon: '!', label: 'Sin resolver', value: this.formatNumber(this.activeSummaryUnresolvedTotal), note: 'Abiertos + pendientes', delta: this.operationsDeltaText('unresolved'), tone: 'orange' },
      { key: 'sla', icon: '✓', label: 'SLA promedio', value: this.formatPercent(this.activeSummarySlaAverage), note: 'Primera respuesta', delta: `${this.formatNumber(this.summary?.sla?.within || 0)} OK`, tone: 'green' },
      { key: 'risk', icon: '!', label: 'Riesgo - Sin interacción', value: '', note: 'Selecciona un tramo para filtrar', delta: '', tone: 'orange' },
    ];
  }

  get operationsChartRows(): SummaryRow[] {
    return this.activeSummaryKind === 'agent' ? this.activeSummaryRows : this.activeSummaryRows.slice(0, 8);
  }

  get operationsTrendLabels(): any[] {
    const data = this.trendVisible;
    if (data.length <= 4) return data;
    return [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
  }

  get operationsSatisfactionSummary(): any {
    return this.calculateSatisfactionSummary(this.operationsSatisfactionItems);
  }

  get operationsSatisfactionItems(): any[] {
    return this.satisfactionItemsInWindow();
  }

  get operationsNpsValue(): number {
    const value = Number(this.operationsSatisfactionSummary?.nps);
    return Number.isFinite(value) ? value : 0;
  }

  get operationsNpsDonutStyle(): Record<string, string> {
    const nps = Math.max(-100, Math.min(100, this.operationsNpsValue));
    const pct = ((nps + 100) / 200) * 100;
    return { background: `conic-gradient(var(--op-cyan) ${pct}%, color-mix(in srgb, var(--line-soft) 72%, transparent) 0)` };
  }

  operationsSatisfactionPct(category: 'promoters' | 'passives' | 'detractors'): number {
    const summary = this.operationsSatisfactionSummary || {};
    const total = Number(summary.answered) || 0;
    return total ? Math.round(((Number(summary[category]) || 0) / total) * 100) : 0;
  }

  operationsRowSatisfaction(row: SummaryRow): any | null {
    const field = this.activeSummaryKind === 'group' ? 'area' : 'responsable';
    const cacheKey = `${this.selectedSatisfactionWindow}|${this.activeSummaryKind}|${this.satisfactionItems.length}|${this.satisfaction?.updated_at || this.satisfactionUpdatedAt || ''}`;
    if (this.operationsRowSatisfactionCacheKey !== cacheKey) {
      this.operationsRowSatisfactionCacheKey = cacheKey;
      this.operationsRowSatisfactionCache.clear();
    }
    if (!this.operationsRowSatisfactionCache.size) {
      for (const summary of this.satisfactionBreakdown(this.operationsSatisfactionItems, field)) {
        this.operationsRowSatisfactionCache.set(summary.label, summary);
      }
    }
    return this.operationsRowSatisfactionCache.get(row.label) || null;
  }

  operationsDeltaText(metric: 'total' | 'unresolved' | 'closed'): string {
    const rows = this.activeSummaryRows;
    if (metric === 'closed') {
      const previous = rows.reduce((sum, row) => sum + (row.response_previous || 0), 0);
      const current = rows.reduce((sum, row) => sum + (row.closed || 0), 0);
      return previous ? `${current >= previous ? '+' : ''}${this.formatPercent(((current - previous) / previous) * 100)}` : 'Período actual';
    }
    return 'Filtro activo';
  }

  operationsLoadPct(row: SummaryRow): number {
    const key = this.isOperationsUnresolvedActive ? 'unresolved' : 'total';
    const max = Math.max(1, ...this.activeSummaryRows.map((item) => Number(item[key]) || 0));
    return ((Number(row[key]) || 0) / max) * 100;
  }

  operationsLoadValue(row: SummaryRow): number {
    return this.isOperationsUnresolvedActive ? (row.unresolved || 0) : (row.total || 0);
  }

  operationsPendingShare(row: SummaryRow): number {
    const baseline = this.operationsBalanceBaseline[this.activeSummaryKind];
    const rows = baseline.length ? baseline : (this.activeSummaryKind === 'group' ? this.summaryRowsGroup : this.summaryRowsAgent);
    const totalPending = Math.max(1, rows.reduce((sum, item) => sum + (item.unresolved || 0), 0));
    return ((row.unresolved || 0) / totalPending) * 100;
  }

  operationsRiskPct(row: SummaryRow, key: 'ok' | 'risk' | 'critical'): number {
    const total = Math.max(1, row.unresolved || this.summaryRiskTotal(row));
    if (key === 'ok') return ((row.risk_low || 0) / total) * 100;
    if (key === 'risk') return ((row.risk_medium || 0) / total) * 100;
    return ((row.risk_high || 0) / total) * 100;
  }

  operationsRiskValue(row: SummaryRow): string {
    return this.isOperationsUnresolvedActive ? this.formatNumber(this.summaryRiskTotal(row)) : this.summarySlaRate(row);
  }

  get isOperationsUnresolvedActive(): boolean {
    return this.sameValues(this.selected.estado, this.unresolvedStateValues());
  }

  operationsMiniTrendPath(row: SummaryRow): string {
    const previous = Number(row.response_previous) || 0;
    const current = Number(row.closed) || 0;
    const max = Math.max(1, previous, current);
    const y1 = 32 - (previous / max) * 24;
    const y2 = 32 - (current / max) * 24;
    const mid = (previous + current) / 2;
    const yMid = 32 - (mid / max) * 24;
    return `M 4 ${y1.toFixed(1)} C 26 ${y1.toFixed(1)}, 36 ${yMid.toFixed(1)}, 56 ${yMid.toFixed(1)} S 88 ${y2.toFixed(1)}, 112 ${y2.toFixed(1)}`;
  }

  get activeSummaryEmptyText(): string {
    return this.activeSummaryKind === 'group' ? 'Sin datos por área.' : 'Sin datos por responsable.';
  }

  setSummaryKind(kind: SummaryKind): void {
    this.activeSummaryKind = kind;
    this.expandedSummaryRow = '';
    this.closeOperationsDetail();
  }

  applyOperationsKpi(key: OperationsKpiKey): void {
    if (key === 'total') {
      this.risk = '';
      this.interactions = [];
      this.selected.estado = [];
      this.selected.sla = [];
      this.selected.agente = [];
      this.selected.grupo = [];
      this.page = 0;
      this.requestRefresh();
      return;
    }
    if (key === 'unresolved') {
      if (this.sameValues(this.selected.estado, this.unresolvedStateValues())) {
        this.selected.estado = [];
      } else {
        this.selected.estado = this.unresolvedStateValues();
        this.interactions = [];
      }
      this.page = 0;
      this.requestRefresh();
      return;
    }
    if (key === 'sla') {
      this.applySummarySla('within');
      return;
    }
    if (key === 'risk') {
      if (this.risk) {
        this.risk = '';
        this.selected.estado = [];
      } else {
        this.risk = 'high';
        this.selected.estado = this.unresolvedStateValues();
        this.interactions = [];
      }
      this.createdRisk = '';
      this.page = 0;
      this.requestRefresh();
    }
  }

  isOperationsKpiActive(key: OperationsKpiKey): boolean {
    if (key === 'total') {
      return !this.selected.estado.length && !this.selected.sla.length && !this.selected.agente.length && !this.selected.grupo.length && !this.risk && !this.createdRisk && !this.interactions.length;
    }
    if (key === 'unresolved') {
      return this.sameValues(this.selected.estado, this.unresolvedStateValues());
    }
    if (key === 'sla') {
      return this.sameValues(this.selected.sla, this.slaValues('within'));
    }
    return key === 'risk' && Boolean(this.risk);
  }

  applyOperationsScope(row: SummaryRow): void {
    this.applySummaryRow(row, this.activeSummaryKind);
  }

  setOperationsSort(sort: OperationsSort): void {
    this.operationsSort = sort;
  }

  toggleSummaryDetails(row: SummaryRow): void {
    const key = `${this.activeSummaryKind}:${row.label}`;
    this.expandedSummaryRow = this.expandedSummaryRow === key ? '' : key;
  }

  openOperationsDetail(row: SummaryRow): void {
    this.selectedOperationsDetailRow = row;
    this.cdr.detectChanges();
  }

  closeOperationsDetail(): void {
    this.selectedOperationsDetailRow = null;
    this.cdr.detectChanges();
  }

  isSummaryDetailsOpen(row: SummaryRow): boolean {
    return this.expandedSummaryRow === `${this.activeSummaryKind}:${row.label}`;
  }

  summarySlaValue(row: SummaryRow): number {
    const total = (row.within || 0) + (row.outside || 0);
    return total ? ((row.within || 0) / total) * 100 : 0;
  }

  visibleSummaryResolvedTopics(row: SummaryRow): TopicRow[] {
    return this.summaryResolvedTopics(row).slice(0, 3);
  }

  visibleSummaryReceivedTopics(row: SummaryRow): TopicRow[] {
    const rowTopics = this.summaryReceivedTopics(row);
    return (rowTopics.length ? rowTopics : this.summaryTopics).slice(0, 3);
  }

  receivedTopicsTitle(row: SummaryRow): string {
    return this.summaryReceivedTopics(row).length ? 'Top 3 recibidos' : 'Top 3 recibidos globales';
  }

  hiddenSummaryResolvedTopicsCount(row: SummaryRow): number {
    return Math.max(0, this.summaryResolvedTopics(row).length - 3);
  }

  private sortedSummaryRows(rows: SummaryRow[] | undefined): SummaryRow[] {
    return [...(rows || [])]
      .sort((a, b) => (b.unresolved || 0) - (a.unresolved || 0) || (b.total || 0) - (a.total || 0) || a.label.localeCompare(b.label));
  }

  private sortOperationsRows(rows: SummaryRow[]): SummaryRow[] {
    const sorted = [...rows];
    const tieBreak = (a: SummaryRow, b: SummaryRow) => (b.total || 0) - (a.total || 0) || a.label.localeCompare(b.label);
    if (this.operationsSort === 'unresolved') {
      return sorted.sort((a, b) => (b.unresolved || 0) - (a.unresolved || 0) || tieBreak(a, b));
    }
    if (this.operationsSort === 'sla') {
      return sorted.sort((a, b) => this.summarySlaValue(a) - this.summarySlaValue(b) || tieBreak(a, b));
    }
    if (this.operationsSort === 'closed') {
      return sorted.sort((a, b) => (b.closed || 0) - (a.closed || 0) || tieBreak(a, b));
    }
    return sorted.sort(tieBreak);
  }

  private fallbackResponseWindowBounds(): { from: string; to: string } | null {
    const to = this.filtersMeta?.created_range?.to || this.health?.created_range?.to;
    if (!to) return null;
    const toDate = new Date(`${to}T00:00:00`);
    if (Number.isNaN(toDate.getTime())) return null;
    if (this.topicWindowDays === 0) {
      const from = this.filtersMeta?.created_range?.from || this.health?.created_range?.from || to;
      return { from, to };
    }
    const fromDate = new Date(toDate);
    fromDate.setDate(fromDate.getDate() - this.topicWindowDays + 1);
    const format = (date: Date) => date.toISOString().slice(0, 10);
    return { from: format(fromDate), to: format(toDate) };
  }

  applySummaryResolved(row: SummaryRow, kind: SummaryKind): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const bounds = row.response_from && row.response_to
      ? { from: row.response_from, to: row.response_to }
      : this.fallbackResponseWindowBounds();
    if (!bounds) {
      this.applySummaryRowState(row, kind, 'closed');
      return;
    }
    const stateValues = this.stateValues('closed');
    const alreadyActive =
      this.sameValues(this.selected[scopeKey], scopeValues)
      && this.sameValues(this.selected.estado, stateValues)
      && this.fromDate === bounds.from
      && this.toDate === bounds.to
      && this.dateMode === 'resolved';
    if (alreadyActive) {
      this.selected[scopeKey] = [];
      this.selected.estado = [];
      this.fromDate = '';
      this.toDate = '';
      this.dateMode = 'created';
    } else {
      this.setSummaryScope(row, kind);
      this.selected.estado = stateValues;
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
      this.dateMode = 'resolved';
      this.risk = '';
      this.clearUnassignedFilter();
    }
    this.page = 0;
    this.requestRefresh();
  }

  applySummaryResolvedTopic(row: SummaryRow, kind: SummaryKind, topic: TopicRow): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const bounds = row.response_from && row.response_to
      ? { from: row.response_from, to: row.response_to }
      : this.fallbackResponseWindowBounds();
    const stateValues = this.stateValues('closed');
    const alreadyActive =
      this.sameValues(this.selected[scopeKey], scopeValues)
      && this.sameValues(this.selected.estado, stateValues)
      && this.normalize(this.topicFilter) === this.normalize(topic.label)
      && this.topicScope === 'resolved'
      && (!bounds || (this.fromDate === bounds.from && this.toDate === bounds.to && this.dateMode === 'resolved'));
    if (alreadyActive) {
      this.selected[scopeKey] = [];
      this.selected.estado = [];
      this.topicFilter = '';
      this.topicScope = '';
      this.fromDate = '';
      this.toDate = '';
      this.dateMode = 'created';
    } else {
      this.setSummaryScope(row, kind);
      this.selected.estado = stateValues;
      this.topicFilter = topic.label;
      this.topicScope = 'resolved';
      this.risk = '';
      this.clearUnassignedFilter();
      if (bounds) {
        this.fromDate = bounds.from;
        this.toDate = bounds.to;
        this.dateMode = 'resolved';
      }
    }
    this.page = 0;
    this.requestRefresh();
  }

  applySummaryTopic(row: SummaryRow, kind: SummaryKind, topic: TopicRow): void {
    const scopeKey = this.summaryFilterKey(kind);
    const scopeValues = this.matchByLabel(scopeKey, row.label);
    const alreadyActive =
      this.sameValues(this.selected[scopeKey], scopeValues)
      && this.normalize(this.topicFilter) === this.normalize(topic.label)
      && this.topicScope !== 'resolved';
    if (alreadyActive) {
      this.selected[scopeKey] = [];
      this.topicFilter = '';
      this.topicScope = '';
    } else {
      this.setSummaryScope(row, kind);
      this.topicFilter = topic.label;
      this.topicScope = '';
      this.risk = '';
      this.clearUnassignedFilter();
    }
    this.page = 0;
    this.requestRefresh();
  }

  summaryUnresolvedPct(row: SummaryRow, kind: SummaryKind): number {
    const rows = this.activeSummaryKind === kind ? this.activeSummaryRows : (kind === 'group' ? this.summaryRowsGroup : this.summaryRowsAgent);
    const max = Math.max(1, ...rows.map((item) => item.unresolved || 0));
    return Math.min(100, ((row.unresolved || 0) / max) * 100);
  }

  summaryStatePct(row: SummaryRow, value: SummaryState): number {
    const total = Math.max(1, row.total || 0);
    const amount = value === 'open' ? row.open : value === 'pending' ? row.pending : row.closed;
    return Math.max(6, Math.min(100, ((amount || 0) / total) * 100));
  }

  summarySlaPct(row: SummaryRow, value: SummarySla): number {
    const total = Math.max(1, (row.within || 0) + (row.outside || 0));
    const amount = value === 'within' ? row.within : row.outside;
    return Math.max(6, Math.min(100, ((amount || 0) / total) * 100));
  }

  summarySlaRate(row: SummaryRow): string {
    const total = Math.max(1, (row.within || 0) + (row.outside || 0));
    return this.formatPercent(((row.within || 0) / total) * 100);
  }

  summaryInteractionPct(row: SummaryRow, bucket: SummaryInteraction): number {
    const values = this.interactionBuckets.map((key) => row.closed_interactions?.[key] || 0);
    const total = Math.max(1, values.reduce((sum, value) => sum + value, 0));
    return Math.max(6, Math.min(100, ((row.closed_interactions?.[bucket] || 0) / total) * 100));
  }

  summaryRiskTotal(row: SummaryRow): number {
    return (row.risk_low || 0) + (row.risk_medium || 0) + (row.risk_high || 0);
  }

  summaryRiskyTickets(row: SummaryRow): number {
    return row.risk_high || 0;
  }

  summaryResponsePct(row: SummaryRow): number {
    const previous = Number(row.response_previous) || 0;
    const recent = Number(row.response_recent) || 0;
    if (previous > 0) return ((recent - previous) / previous) * 100;
    return recent > 0 ? 100 : 0;
  }

  summaryResponseDirection(row: SummaryRow): 'up' | 'down' | 'flat' {
    const direction = row.response_direction;
    if (direction === 'up' || direction === 'down') return direction;
    return 'flat';
  }

  interactionLabel(value: SummaryInteraction): string {
    return {
      '1_interaction': '1',
      '2_interactions': '2',
      '3_interactions': '3',
      '4_plus': '4+',
    }[value];
  }

  readonly interactionBuckets: SummaryInteraction[] = ['1_interaction', '2_interactions', '3_interactions', '4_plus'];

  isSummaryInteractionActive(value: SummaryInteraction): boolean {
    return this.interactions.includes(value);
  }

  isSummarySlaActive(value: SummarySla): boolean {
    return this.sameValues(this.selected.sla, this.slaValues(value));
  }

  priorityTone(label: string): string {
    const value = this.normalize(label);
    if (value.includes('urgent') || value.includes('alta') || value.includes('high')) return 'high';
    if (value.includes('medium') || value.includes('media')) return 'medium';
    if (value.includes('low') || value.includes('baja')) return 'low';
    return 'neutral';
  }

  priorityDescription(label: string): string {
    const tone = this.priorityTone(label);
    if (tone === 'high') return 'Atención prioritaria';
    if (tone === 'medium') return 'Seguimiento normal';
    if (tone === 'low') return 'Baja urgencia';
    return 'Sin clasificación crítica';
  }

  stateTone(label: string): string {
    const value = this.normalize(label);
    if (value.includes('open') || value.includes('nuevo') || value.includes('abierto')) return 'open';
    if (value.includes('pending') || value.includes('pendiente')) return 'pending';
    if (value.includes('resolved') || value.includes('resuelto')) return 'resolved';
    if (value.includes('closed') || value.includes('cerrado')) return 'closed';
    return 'neutral';
  }

  stateDescription(label: string): string {
    const tone = this.stateTone(label);
    if (tone === 'open') return 'Requiere gestión';
    if (tone === 'pending') return 'A la espera';
    if (tone === 'resolved') return 'Resuelto recientemente';
    if (tone === 'closed') return 'Cierre administrativo';
    return 'Estado operativo';
  }

  get trendSeries(): any[] {
    return this.trendWindowSeries;
  }

  get rawTrendSeries(): any[] {
    if (Array.isArray(this.trend)) return this.trend;
    return this.trend?.series || [];
  }

  get trendWindowSeries(): any[] {
    const source = this.trendSeriesInActiveRange(this.rawTrendSeries);
    if (this.topicWindowDays === 0) return source;
    const size = Math.max(1, Math.min(source.length, this.topicWindowDays));
    return source.slice(-size);
  }

  private trendSeriesInActiveRange(source: any[]): any[] {
    if (!this.fromDate && !this.toDate) return source;
    return source.filter((point: any) => {
      const key = String(point?.key || '');
      if (!/^\d{4}-\d{2}-\d{2}/.test(key)) return true;
      const day = key.slice(0, 10);
      if (this.fromDate && day < this.fromDate) return false;
      if (this.toDate && day > this.toDate) return false;
      return true;
    });
  }

  get trendContextSeries(): any[] {
    if (Array.isArray(this.trendContext)) return this.trendContext;
    return this.trendContext?.series || [];
  }

  get trendVisible(): any[] {
    const source = this.trendWindowSeries;
    const size = Math.min(36, source.length);
    const maxStart = Math.max(0, source.length - size);
    const start = Math.max(0, Math.min(maxStart, this.trendPanStart ?? maxStart));
    return source.slice(start, start + size);
  }

  get trendHoverPoint(): any | null {
    if (this.trendHoverIndex === null) return null;
    return this.trendVisible[this.trendHoverIndex] || null;
  }

  get trendActivePoint(): any | null {
    return this.trendHoverPoint;
  }

  get trendHoverX(): number {
    if (this.trendHoverIndex === null) return 4;
    const points = this.trendLinePoints('backlog');
    return points[this.trendHoverIndex]?.x || 4;
  }

  get trendTooltipPositionClass(): string {
    if (this.trendHoverIndex === null) return 'middle';
    const data = this.trendVisible;
    if (data.length <= 2) {
      return this.trendHoverIndex === 0 ? 'left-edge' : 'right-edge';
    }
    if (this.trendHoverIndex <= 3) return 'left-edge';
    if (this.trendHoverIndex >= data.length - 4) return 'right-edge';
    return 'middle';
  }

  get trendTooltipLeftPct(): number {
    return (this.trendHoverX / 720) * 100;
  }

  get trendAxisLabels(): any[] {
    const data = this.trendVisible;
    if (data.length <= 3) return data;
    return [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
  }

  get riskTrendSeries(): any[] {
    return this.riskTrend?.series || [];
  }

  get riskTrendActivePoint(): any | null {
    if (this.riskTrendSelectedIndex === null) return null;
    return this.riskTrendSeries[this.riskTrendSelectedIndex] || null;
  }

  get riskTrendLatestPoint(): any | null {
    const series = this.riskTrendSeries;
    return series.length ? series[series.length - 1] : null;
  }

  get riskTrendDetailPoint(): any | null {
    return this.riskTrendActivePoint || this.riskTrendLatestPoint;
  }

  get riskTrendMax(): number {
    const data = this.riskTrendSeries;
    if (!data.length) return 1;
    return Math.max(
      this.riskTrendLineMax,
      this.riskTrendFlowMax,
    );
  }

  get riskTrendYLabels(): Array<{ y: number; label: string }> {
    const max = this.riskTrendMax;
    if (max <= 0) return [];
    return [
      { y: 22, label: this.formatNumber(max) },
      { y: 84, label: this.formatNumber(Math.round((max * 2) / 3)) },
      { y: 147, label: this.formatNumber(Math.round(max / 3)) },
      { y: 210, label: '0' }
    ];
  }

  get riskTrendLineMax(): number {
    const data = this.riskTrendSeries;
    if (!data.length) return 1;
    return Math.max(
      1,
      ...data.map((point: any) => Number(point?.stock_critical) || 0),
    );
  }

  get riskTrendFlowMax(): number {
    const data = this.riskTrendSeries;
    if (!data.length) return 1;
    return Math.max(
      1,
      ...data.map((point: any) => Math.max(
        Number(point?.flow?.inflow) || 0,
        Number(point?.flow?.outflow) || 0,
      )),
    );
  }

  get riskTrendFlowPoints(): any[] {
    const data = this.riskTrendSeries;
    if (!data.length) return [];
    const chartWidth = 1024;
    const left = 18;
    const right = chartWidth - 18;
    const top = 24;
    const base = 130;
    const bottom = 210;
    const span = Math.max(1, data.length - 1);
    const maxVal = this.riskTrendFlowMax;
    const upRange = base - top;
    const downRange = bottom - base;

    return data.map((point: any, index: number) => {
      const x = left + ((right - left) * index) / span;
      const inflow = Number(point?.flow?.inflow) || 0;
      const outflow = Number(point?.flow?.outflow) || 0;
      
      const hInflow = (upRange * inflow) / maxVal;
      const hOutflow = (downRange * outflow) / maxVal;
      const visualHInflow = inflow > 0 ? Math.max(6, hInflow) : 0;
      const visualHOutflow = outflow > 0 ? Math.max(6, hOutflow) : 0;

      return {
        x,
        base,
        inflow: {
          x: Math.max(0, Math.min(chartWidth - 10, x - 11)),
          labelX: Math.max(18, Math.min(chartWidth - 18, x - 6)),
          labelY: Math.max(16, base - visualHInflow - 10),
          h: visualHInflow,
          y: base - visualHInflow,
          val: inflow
        },
        outflow: {
          x: Math.max(0, Math.min(chartWidth - 10, x + 1)),
          labelX: Math.max(18, Math.min(chartWidth - 18, x + 6)),
          labelY: Math.min(224, base + visualHOutflow + 16),
          h: visualHOutflow,
          y: base,
          val: outflow
        }
      };
    });
  }

  get riskTrendHoverX(): number {
    const index = this.riskTrendSelectedIndex ?? Math.max(0, this.riskTrendSeries.length - 1);
    return this.riskTrendStockPoints[index]?.x || 0;
  }

  get riskTrendTooltipLeftPct(): number {
    return (this.riskTrendHoverX / 1024) * 100;
  }

  get riskTrendTooltipPositionClass(): string {
    if (this.riskTrendSelectedIndex === null) return 'middle';
    const count = this.riskTrendSeries.length;
    if (count <= 2) return this.riskTrendSelectedIndex === 0 ? 'left-edge' : 'right-edge';
    if (this.riskTrendSelectedIndex <= 2) return 'left-edge';
    if (this.riskTrendSelectedIndex >= count - 3) return 'right-edge';
    return 'middle';
  }

  get riskTrendAxisLabels(): any[] {
    const data = this.riskTrendSeries;
    if (data.length <= 3) return data;
    return [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
  }

  get riskTrendStockPath(): string {
    return this.smoothPath(this.riskTrendStockPoints);
  }

  get riskTrendStockAreaPath(): string {
    const points = this.riskTrendStockPoints;
    if (!points.length) return '';
    const baseline = 210;
    const line = this.smoothPath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;
  }

  get riskTrendStockPoints(): Array<{ x: number; y: number; value: number; label: string; labelX: number; labelY: number }> {
    const data = this.riskTrendSeries;
    if (!data.length) return [];
    const chartWidth = 1024;
    const left = 18;
    const right = chartWidth - 18;
    const top = 34;
    const bottom = 198;
    const span = Math.max(1, data.length - 1);
    const max = this.riskTrendLineMax;
    return data.map((point: any, index: number) => {
      const value = Number(point?.stock_critical) || 0;
      const inflow = Number(point?.flow?.inflow) || 0;
      const outflow = Number(point?.flow?.outflow) || 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / max;
      const edgeOffset = index === 0 ? 20 : index === data.length - 1 ? -20 : 0;
      const sideOffset = inflow > 0 || outflow > 0 ? (index % 2 === 0 ? 12 : -12) : 0;
      const labelX = Math.max(28, Math.min(chartWidth - 28, x + edgeOffset + sideOffset));
      const labelAboveLine = y > 82 || inflow === 0;
      const labelY = Math.max(18, Math.min(218, y + (labelAboveLine ? -15 : 22)));
      return { x, y, value, label: point.label || point.key || '', labelX, labelY };
    });
  }

  riskTrendRangeLabel(point: any): string {
    const start = String(point?.start || '');
    const end = String(point?.end || '');
    if (!start || !end || start === end) return point?.label || start || '';
    return `${point?.label || start} · ${start} a ${end}`;
  }

  riskTrendAxisLabel(point: any): string {
    const label = String(point?.label || '').trim();
    const rangeMatch = label.match(/(\d{1,2})\/(\d{1,2})\s*[-–]\s*(\d{1,2})\/(\d{1,2})/);
    if (rangeMatch) {
      return `${rangeMatch[1].padStart(2, '0')}/${rangeMatch[2].padStart(2, '0')}-${rangeMatch[3].padStart(2, '0')}/${rangeMatch[4].padStart(2, '0')}`;
    }

    const start = String(point?.start || point?.key || '');
    const end = String(point?.end || '');
    const startDate = this.parseDateSafe(start);
    const endDate = this.parseDateSafe(end);
    if (startDate && endDate) {
      if (endDate <= startDate) return this.compactDayMonth(startDate);
      return `${this.compactDayMonth(startDate)}-${this.compactDayMonth(endDate)}`;
    }
    if (startDate) return this.compactDayMonth(startDate);

    return label.replace(/\s+/g, ' ').replace(/\s*[-–]\s*/g, '-');
  }

  private parseDateSafe(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private compactDayMonth(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  selectRiskTrendPoint(event: MouseEvent): void {
    const target = event.currentTarget as SVGRectElement | null;
    const svg = target?.ownerSVGElement;
    const data = this.riskTrendSeries;
    if (!svg || !data.length) return;
    const rect = svg.getBoundingClientRect();
    const chartWidth = 1024;
    const x = ((event.clientX - rect.left) / rect.width) * chartWidth;
    const left = 0;
    const right = chartWidth;
    const ratio = Math.max(0, Math.min(1, (x - left) / (right - left)));
    this.riskTrendSelectedIndex = Math.round(ratio * Math.max(0, data.length - 1));
  }

  selectRiskTrendIndex(index: number): void {
    if (index < 0 || index >= this.riskTrendSeries.length) return;
    this.riskTrendSelectedIndex = index;
  }

  isRiskTrendSelectedIndex(index: number): boolean {
    const selected = this.riskTrendSelectedIndex ?? Math.max(0, this.riskTrendSeries.length - 1);
    return selected === index;
  }

  get weeklyComparisonSeries(): any[] {
    return this.weeklyComparison?.series || [];
  }

  get weeklyComparisonAxis(): any[] {
    return this.weeklyComparison?.axis || [];
  }

  readonly weeklyComparisonMetricOptions: Array<{ key: WeeklyComparisonMetric; label: string }> = [
    { key: 'received', label: 'Recibido' },
    { key: 'resolved', label: 'Resuelto' },
    { key: 'unresolved', label: 'Sin resolver' },
  ];

  weeklyComparisonValues(serie: any): number[] {
    const metrics = serie?.metrics;
    if (metrics?.[this.weeklyComparisonMetric]) return metrics[this.weeklyComparisonMetric];
    return serie?.values || [];
  }

  get weeklyComparisonMax(): number {
    const values = this.weeklyComparisonSeries.flatMap((serie: any) => this.weeklyComparisonValues(serie));
    return Math.max(1, ...values.map((value: any) => Number(value) || 0));
  }

  get weeklyComparisonActiveAxis(): any | null {
    if (this.weeklyComparisonHoverIndex === null) return null;
    return this.weeklyComparisonAxis[this.weeklyComparisonHoverIndex] || null;
  }

  get weeklyComparisonHoverX(): number {
    if (this.weeklyComparisonHoverIndex === null) return 4;
    const count = Math.max(1, this.weeklyComparisonAxis.length - 1);
    return 4 + ((716 - 4) * this.weeklyComparisonHoverIndex) / count;
  }

  get weeklyComparisonTooltipPositionClass(): string {
    if (this.weeklyComparisonHoverIndex === null) return 'middle';
    if (this.weeklyComparisonHoverIndex <= 2) return 'left-edge';
    if (this.weeklyComparisonHoverIndex >= this.weeklyComparisonAxis.length - 3) return 'right-edge';
    return 'middle';
  }

  get weeklyComparisonTooltipLeftPct(): number {
    return (this.weeklyComparisonHoverX / 720) * 100;
  }

  get weeklyComparisonTooltipRows(): Array<{ label: string; value: number; color: string }> {
    if (this.weeklyComparisonHoverIndex === null) return [];
    const hoverIndex = this.weeklyComparisonHoverIndex;
    return this.weeklyComparisonSeries.map((serie: any, index: number) => ({
      label: serie.label,
      value: Number(this.weeklyComparisonValues(serie)?.[hoverIndex] || 0),
      color: ['red', 'teal', 'violet'][index] || 'teal',
    }));
  }

  weeklyComparisonPath(serie: any): string {
    return this.smoothPath(this.weeklyComparisonPoints(serie));
  }

  weeklyComparisonAreaPath(serie: any): string {
    const points = this.weeklyComparisonPoints(serie);
    if (!points.length) return '';
    const baseline = 210;
    const line = this.smoothPath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;
  }

  weeklyComparisonPoints(serie: any): Array<{ x: number; y: number }> {
    const values = this.weeklyComparisonValues(serie);
    const left = 4;
    const right = 716;
    const top = 22;
    const bottom = 210;
    const span = Math.max(1, values.length - 1);
    return values.map((raw: any, index: number) => {
      const value = Number(raw) || 0;
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / this.weeklyComparisonMax;
      return { x, y };
    });
  }

  onWeeklyComparisonPointer(event: MouseEvent): void {
    const target = event.currentTarget as SVGRectElement | null;
    const svg = target?.ownerSVGElement;
    const count = this.weeklyComparisonAxis.length;
    if (!svg || !count) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 720;
    const left = 4;
    const right = 716;
    const ratio = Math.max(0, Math.min(1, (x - left) / (right - left)));
    this.weeklyComparisonHoverIndex = Math.round(ratio * Math.max(0, count - 1));
  }

  clearWeeklyComparisonHover(): void {
    this.weeklyComparisonHoverIndex = null;
  }

  get trendLineViewBox(): string {
    return '0 0 720 240';
  }

  trendLinePath(metric: TrendMetric): string {
    const points = this.trendLinePoints(metric);
    return this.smoothPath(points);
  }

  trendAreaPath(metric: TrendMetric): string {
    const points = this.trendLinePoints(metric);
    if (!points.length) return '';
    const baseline = 210;
    const line = this.smoothPath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;
  }

  operationsDetailTrendSeries(row: SummaryRow): any[] {
    const source = this.trendSeriesInActiveRange(Array.isArray(row.trend) ? row.trend : []);
    if (this.topicWindowDays === 0) return source;
    const size = Math.max(1, Math.min(source.length, this.topicWindowDays));
    return source.slice(-size);
  }

  operationsDetailTrendPath(row: SummaryRow, metric: TrendMetric): string {
    return this.smoothPath(this.operationsDetailTrendPoints(row, metric));
  }

  operationsDetailTrendAreaPath(row: SummaryRow, metric: TrendMetric): string {
    const points = this.operationsDetailTrendPoints(row, metric);
    if (!points.length) return '';
    const baseline = 164;
    const line = this.smoothPath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`;
  }

  operationsDetailTrendLabels(row: SummaryRow): any[] {
    const data = this.operationsDetailTrendSeries(row);
    if (data.length <= 3) return data;
    return [data[0], data[Math.floor(data.length / 2)], data[data.length - 1]];
  }

  private operationsDetailTrendPoints(row: SummaryRow, metric: TrendMetric): Array<{ x: number; y: number; value: number; label: string }> {
    const data = this.operationsDetailTrendSeries(row);
    if (!data.length) return [];
    const left = 10;
    const right = 710;
    const top = 16;
    const bottom = 164;
    const max = Math.max(1, ...data.map((point: any) => Math.max(Number(point.received) || 0, Number(point.closed) || 0, Number(point.backlog) || 0)));
    const span = Math.max(1, data.length - 1);
    return data.map((point: any, index: number) => {
      const value = Number(point?.[metric] || 0);
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / max;
      return { x, y, value, label: point.label || point.key || '' };
    });
  }

  private smoothPath(points: Array<{ x: number; y: number }>): string {
    if (!points.length) return '';
    if (points.length === 1) {
      const x = points[0].x.toFixed(1);
      const y = points[0].y.toFixed(1);
      return `M ${(+x - 0.5).toFixed(1)} ${y} L ${(+x + 0.5).toFixed(1)} ${y}`;
    }
    const commands = [`M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`];
    for (let i = 1; i < points.length; i += 1) {
      const prev = points[i - 1];
      const current = points[i];
      const midX = (prev.x + current.x) / 2;
      commands.push(`C ${midX.toFixed(1)} ${prev.y.toFixed(1)}, ${midX.toFixed(1)} ${current.y.toFixed(1)}, ${current.x.toFixed(1)} ${current.y.toFixed(1)}`);
    }
    return commands.join(' ');
  }

  trendLinePoints(metric: TrendMetric): Array<{ x: number; y: number; value: number; label: string }> {
    const data = this.trendVisible;
    if (!data.length) return [];
    const left = 4;
    const right = 716;
    const top = 22;
    const bottom = 210;
    const max = this.trendMax;
    const span = Math.max(1, data.length - 1);
    return data.map((point: any, index: number) => {
      const value = Number(point?.[metric] || 0);
      const x = left + ((right - left) * index) / span;
      const y = bottom - ((bottom - top) * value) / max;
      return { x, y, value, label: point.label || point.key || '' };
    });
  }

  onTrendPointer(event: MouseEvent): void {
    const target = event.currentTarget as SVGRectElement | null;
    const svg = target?.ownerSVGElement;
    const data = this.trendVisible;
    if (!svg || !data.length) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 720;
    const left = 4;
    const right = 716;
    const ratio = Math.max(0, Math.min(1, (x - left) / (right - left)));
    this.trendHoverIndex = Math.round(ratio * Math.max(0, data.length - 1));
  }

  clearTrendHover(): void {
    this.trendHoverIndex = null;
  }

  startTrendPan(event: PointerEvent): void {
    event.preventDefault();
    this.trendPanStartX = event.clientX;
    const source = this.trendWindowSeries;
    const size = Math.min(36, source.length);
    const maxStart = Math.max(0, source.length - size);
    this.trendPanOrigin = Math.max(0, Math.min(maxStart, this.trendPanStart ?? maxStart));
    this.trendDidDrag = false;
    window.addEventListener('pointermove', this.onWindowTrendPanMove);
    window.addEventListener('pointerup', this.onWindowTrendPanEnd);
  }

  moveTrendPan(event: PointerEvent): void {
    if (this.trendPanStartX === null || this.trendPanOrigin === null) return;
    event.preventDefault();
    const source = this.trendWindowSeries;
    const size = Math.min(36, source.length);
    const maxStart = Math.max(0, source.length - size);
    const dx = event.clientX - this.trendPanStartX;
    if (Math.abs(dx) > 2) this.trendDidDrag = true;
    const pointsDelta = Math.trunc(-dx / 5);
    const nextStart = Math.max(0, Math.min(maxStart, this.trendPanOrigin + pointsDelta));
    if (nextStart === this.trendPanStart || nextStart === this.trendPanPendingStart) return;
    this.trendPanPendingStart = nextStart;
    if (this.trendPanFrame !== null) return;
    this.trendPanFrame = requestAnimationFrame(() => {
      this.trendPanStart = this.trendPanPendingStart;
      this.trendPanPendingStart = null;
      this.trendPanFrame = null;
      this.trendHoverIndex = null;
      this.cdr.detectChanges();
    });
  }

  endTrendPan(): void {
    this.unbindTrendPanListeners();
    if (this.trendPanFrame !== null) {
      cancelAnimationFrame(this.trendPanFrame);
      this.trendPanFrame = null;
    }
    if (this.trendPanPendingStart !== null) {
      this.trendPanStart = this.trendPanPendingStart;
      this.trendPanPendingStart = null;
    }
    this.trendPanStartX = null;
    this.trendPanOrigin = null;
  }

  private readonly onWindowTrendPanMove = (event: PointerEvent) => this.moveTrendPan(event);
  private readonly onWindowTrendPanEnd = () => this.endTrendPan();

  private unbindTrendPanListeners(): void {
    window.removeEventListener('pointermove', this.onWindowTrendPanMove);
    window.removeEventListener('pointerup', this.onWindowTrendPanEnd);
  }

  applyTrendPoint(event?: MouseEvent): void {
    if (this.trendDidDrag) {
      this.trendDidDrag = false;
      return;
    }
    if (event) this.onTrendPointer(event);
    if (event?.detail && event.detail > 1) return;
    const point = this.trendActivePoint;
    if (!point) return;
    clearTimeout(this.trendClickTimer || undefined);
    this.trendClickTimer = setTimeout(() => {
      this.trendClickTimer = null;
      this.applyTrendSelection(point);
    }, 220);
  }

  applyTrendMetric(point: any, metric: TrendMetric): void {
    const key = String(point?.key || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
    this.trendSelectionMetric = metric;
    const nextMode: 'created' | 'resolved' | 'backlog' = metric === 'closed'
      ? 'resolved'
      : metric === 'backlog'
        ? 'backlog'
        : 'created';
    if (this.fromDate === key && this.toDate === key && this.dateMode === nextMode) {
      this.resetPeriodFilter();
      return;
    }
    this.fromDate = key;
    this.toDate = key;
    this.interactions = [];
    if (metric === 'closed') {
      this.dateMode = 'resolved';
      this.risk = '';
      this.selected.estado = this.stateValues('closed');
    } else if (metric === 'received') {
      this.dateMode = 'created';
      this.selected.estado = this.risk ? this.unresolvedStateValues() : [];
    } else {
      this.dateMode = 'backlog';
      this.selected.estado = this.risk ? this.unresolvedStateValues() : [];
    }
    this.page = 0;
    this.requestRefresh();
  }

  applyTrendSelection(point: any): void {
    const key = point?.key || point?.label || '';
    const text = String(key);
    const metric = this.trendSelectionMetric;
    const dateMode: 'created' | 'resolved' | 'backlog' = metric === 'closed'
      ? 'resolved'
      : metric === 'backlog'
        ? 'backlog'
        : 'created';
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      if (this.fromDate === text && this.toDate === text && this.dateMode === dateMode) {
        this.resetPeriodFilter();
        return;
      }
      this.fromDate = text;
      this.toDate = text;
      this.dateMode = dateMode;
      if (metric === 'closed') {
        this.risk = '';
      }
      this.interactions = metric === 'closed' ? this.interactions : [];
      this.selected.estado = metric === 'closed' ? this.stateValues('closed') : (this.risk ? this.unresolvedStateValues() : []);
      this.page = 0;
      this.requestRefresh();
      return;
    }
    if (/^\d{4}-\d{2}$/.test(text)) {
      const bounds = this.monthBounds(text);
      if (this.fromDate === bounds.from && this.toDate === bounds.to && this.dateMode === dateMode) {
        this.resetPeriodFilter();
        return;
      }
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
      this.dateMode = dateMode;
      if (metric === 'closed') {
        this.risk = '';
      }
      this.interactions = metric === 'closed' ? this.interactions : [];
      this.selected.estado = metric === 'closed' ? this.stateValues('closed') : (this.risk ? this.unresolvedStateValues() : []);
      this.page = 0;
      this.requestRefresh();
      return;
    }
    const monthKey = text.slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(monthKey)) {
      const bounds = this.monthBounds(monthKey);
      if (this.fromDate === bounds.from && this.toDate === bounds.to && this.dateMode === dateMode) {
        this.resetPeriodFilter();
        return;
      }
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
      this.dateMode = dateMode;
      if (metric === 'closed') {
        this.risk = '';
      }
      this.interactions = metric === 'closed' ? this.interactions : [];
      this.selected.estado = metric === 'closed' ? this.stateValues('closed') : (this.risk ? this.unresolvedStateValues() : []);
      this.page = 0;
      this.requestRefresh();
    }
  }

  get trendMax(): number {
    return Math.max(
      1,
      ...this.trendWindowSeries.map((point: any) => Math.max(
        Number(point.received) || 0,
        Number(point.closed) || 0,
        Number(point.backlog) || 0,
      )),
    );
  }

  trendPct(value: number): number {
    return (value / this.trendMax) * 100;
  }

  get periodRecent(): any[] {
    return this.periodCards?.recent_periods || [];
  }

  get periodReceivedMax(): number {
    return Math.max(1, ...this.periodRecent.map((item: any) => item.received || 0));
  }

  get periodUnresolvedMax(): number {
    return Math.max(1, ...this.periodRecent.map((item: any) => item.unresolved || 0));
  }

  get areaQuadrantRows(): SummaryRow[] {
    return this.summaryRowsGroup;
  }

  get areaTreemapItems(): AreaTreemapItem[] {
    const visibleLimit = 11;
    const source = this.areaQuadrantRows.filter((row) => (row.total || 0) > 0);
    const rows = source.length > visibleLimit
      ? [
          ...source.slice(0, visibleLimit),
          source.slice(visibleLimit).reduce<SummaryRow>((acc, row) => ({
            label: 'Otras áreas',
            total: acc.total + (row.total || 0),
            unresolved: acc.unresolved + (row.unresolved || 0),
            open: acc.open + (row.open || 0),
            pending: acc.pending + (row.pending || 0),
            closed: acc.closed + (row.closed || 0),
            within: acc.within + (row.within || 0),
            outside: acc.outside + (row.outside || 0),
            risk_low: (acc.risk_low || 0) + (row.risk_low || 0),
            risk_medium: (acc.risk_medium || 0) + (row.risk_medium || 0),
            risk_high: (acc.risk_high || 0) + (row.risk_high || 0),
            closed_interactions: {
              '1_interaction': (acc.closed_interactions?.['1_interaction'] || 0) + (row.closed_interactions?.['1_interaction'] || 0),
              '2_interactions': (acc.closed_interactions?.['2_interactions'] || 0) + (row.closed_interactions?.['2_interactions'] || 0),
              '3_interactions': (acc.closed_interactions?.['3_interactions'] || 0) + (row.closed_interactions?.['3_interactions'] || 0),
              '4_plus': (acc.closed_interactions?.['4_plus'] || 0) + (row.closed_interactions?.['4_plus'] || 0),
            },
          }), {
            label: 'Otras áreas',
            total: 0,
            unresolved: 0,
            open: 0,
            pending: 0,
            closed: 0,
            within: 0,
            outside: 0,
            risk_low: 0,
            risk_medium: 0,
            risk_high: 0,
            closed_interactions: { '1_interaction': 0, '2_interactions': 0, '3_interactions': 0, '4_plus': 0 },
          }),
        ]
      : source;
    const total = rows.reduce((sum, row) => sum + (row.total || 0), 0);
    if (!rows.length || !total) return [];
    return rows.map((row, index) => {
      const share = ((row.total || 0) / total) * 100;
      const span = row.label === 'Otras áreas' ? 1 : share >= 24 ? 3 : share >= 6 ? 2 : 1;
      return {
        row,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        share,
        color: this.areaTreemapPalette[index % this.areaTreemapPalette.length][0],
        colorSoft: this.areaTreemapPalette[index % this.areaTreemapPalette.length][1],
        compact: span === 1,
        span,
      };
    });
  }

  get areaQuadrantTotal(): number {
    return Math.max(1, this.areaQuadrantRows.reduce((sum, row) => sum + (row.total || 0), 0));
  }

  areaSharePct(row: SummaryRow): number {
    return ((row.total || 0) / this.areaQuadrantTotal) * 100;
  }

  areaTreemapStyle(item: AreaTreemapItem): Record<string, string> {
    return {
      'grid-column': `span ${item.span}`,
      'grid-row': `span ${item.span}`,
      '--area-color': item.color,
      '--area-soft': item.colorSoft,
    };
  }

  private buildTreemap(rows: SummaryRow[], x: number, y: number, w: number, h: number, total: number): AreaTreemapItem[] {
    const nodes = rows
      .map((row) => ({ row, area: ((row.total || 0) / total) * w * h }))
      .sort((a, b) => b.area - a.area);
    const output: AreaTreemapItem[] = [];
    let row: typeof nodes = [];
    let remaining = [...nodes];
    let rx = x;
    let ry = y;
    let rw = w;
    let rh = h;

    const worst = (line: typeof nodes, side: number): number => {
      if (!line.length) return Number.POSITIVE_INFINITY;
      const areas = line.map((item) => item.area);
      const sum = areas.reduce((acc, value) => acc + value, 0);
      const min = Math.max(0.0001, Math.min(...areas));
      const max = Math.max(...areas);
      const sideSq = side * side;
      return Math.max((sideSq * max) / (sum * sum), (sum * sum) / (sideSq * min));
    };

    const layoutRow = (line: typeof nodes): void => {
      const area = line.reduce((sum, item) => sum + item.area, 0);
      if (rw >= rh) {
        const rowH = area / rw;
        let cx = rx;
        for (const item of line) {
          const itemW = item.area / rowH;
          output.push({ row: item.row, x: cx, y: ry, w: itemW, h: rowH, share: 0, color: '', colorSoft: '', compact: false, span: 1 });
          cx += itemW;
        }
        ry += rowH;
        rh -= rowH;
      } else {
        const colW = area / rh;
        let cy = ry;
        for (const item of line) {
          const itemH = item.area / colW;
          output.push({ row: item.row, x: rx, y: cy, w: colW, h: itemH, share: 0, color: '', colorSoft: '', compact: false, span: 1 });
          cy += itemH;
        }
        rx += colW;
        rw -= colW;
      }
    };

    while (remaining.length) {
      const next = remaining[0];
      const side = Math.min(rw, rh);
      if (!row.length || worst([...row, next], side) <= worst(row, side)) {
        row.push(next);
        remaining.shift();
      } else {
        layoutRow(row);
        row = [];
      }
    }
    if (row.length) layoutRow(row);
    return output;
  }

  get periodRisk(): any {
    return this.periodCards?.risk || {};
  }

  get periodAge(): any {
    return this.periodCards?.age || {};
  }

  get periodCreatedRisk(): any {
    return this.periodCards?.created_risk || {};
  }

  get unresolvedTotal(): number {
    return this.periodCards?.unresolved_total || 0;
  }

  get ageRows(): Array<{ key: RiskKey; title: string; range: string; value: number; color: string }> {
    return [
      { key: 'low', title: '0 - 2 días', range: 'Bajo', value: this.periodRisk.low || 0, color: 'green' },
      { key: 'medium', title: '3 - 7 días', range: 'Medio', value: this.periodRisk.medium || 0, color: 'orange' },
      { key: 'high', title: '8+ días', range: 'Riesgo alto', value: this.periodRisk.high || 0, color: 'red' },
    ];
  }

  get createdAgeRows(): Array<{ key: CreatedRiskKey; title: string; range: string; value: number; color: string }> {
    return [
      { key: 'zero_one', title: '0 - 1 días', range: 'Inicio', value: this.periodCreatedRisk.zero_one || 0, color: 'green' },
      { key: 'two', title: '2 días', range: 'Seguimiento', value: this.periodCreatedRisk.two || 0, color: 'teal' },
      { key: 'three', title: '3 días', range: 'Atención', value: this.periodCreatedRisk.three || 0, color: 'violet' },
      { key: 'medium', title: '4 - 7 días', range: 'Medio', value: this.periodCreatedRisk.medium || 0, color: 'orange' },
      { key: 'high', title: '8+ días', range: 'Riesgo alto', value: this.periodCreatedRisk.high || 0, color: 'red' },
    ];
  }

  get legendStates(): Array<{ label: string; value: number; pct: number; tone: string; description: string }> {
    const states = this.summary?.by_state || {};
    const total = Math.max(1, this.summary?.total || 0);
    return Object.entries(states)
      .map(([label, value]) => ({
        label,
        value: Number(value),
        pct: (Number(value) / total) * 100,
        tone: this.stateTone(label),
        description: this.stateDescription(label),
      }))
      .sort((a, b) => b.value - a.value);
  }

  get priorityRows(): Array<{ label: string; value: number; pct: number; share: number; tone: string; description: string }> {
    const entries = Object.entries(this.summary?.by_priority || {})
      .map(([label, value]) => ({ label, value: Number(value) }))
      .sort((a, b) => b.value - a.value);
    const max = Math.max(1, ...entries.map((item) => item.value));
    const total = Math.max(1, entries.reduce((sum, item) => sum + item.value, 0));
    return entries.map((item) => ({
      ...item,
      pct: (item.value / max) * 100,
      share: (item.value / total) * 100,
      tone: this.priorityTone(item.label),
      description: this.priorityDescription(item.label),
    }));
  }

  get interactionSummary(): Array<{ key: SummaryInteraction; label: string; value: number; pct: number }> {
    const totals: Record<SummaryInteraction, number> = {
      '1_interaction': 0,
      '2_interactions': 0,
      '3_interactions': 0,
      '4_plus': 0,
    };
    for (const row of (this.summary?.by_agent_detail || []) as SummaryRow[]) {
      for (const bucket of this.interactionBuckets) {
        totals[bucket] += row.closed_interactions?.[bucket] || 0;
      }
    }
    const total = Math.max(1, Object.values(totals).reduce((sum, value) => sum + value, 0));
    return this.interactionBuckets.map((key) => ({
      key,
      label: key === '1_interaction' ? '1 interacción' : `${this.interactionLabel(key)} interacciones`,
      value: totals[key],
      pct: (totals[key] / total) * 100,
    }));
  }

  get interactionClosedTotal(): number {
    return this.interactionSummary.reduce((sum, item) => sum + item.value, 0);
  }

  interactionInsight(key: SummaryInteraction): string {
    return {
      '1_interaction': 'Resolución directa',
      '2_interactions': 'Requiere una vuelta',
      '3_interactions': 'Seguimiento moderado',
      '4_plus': 'Mayor fricción',
    }[key];
  }

  get topics(): TopicRow[] {
    if (this.topicsSnapshot) return this.topicsSnapshot;
    return this.summaryTopics;
  }

  get summaryTopics(): TopicRow[] {
    return this.mapTopics(this.summary?.top_topics || []);
  }

  get isTopicScoped(): boolean {
    return Boolean(this.topicFilter);
  }

  get trendTopics(): TopicRow[] {
    return this.topics
      .filter((topic) => topic.direction === 'up' && topic.change > 0 && topic.recent >= 4)
      .sort((a, b) => b.change - a.change || b.change_pct - a.change_pct || b.recent - a.recent)
      .slice(0, 10);
  }

  get risingTopics(): TopicRow[] {
    return this.trendTopics;
  }

  get risingPanelTopics(): TopicRow[] {
    return this.trendTopics.length ? this.trendTopics : this.allRecentTopics.slice(0, 10);
  }

  get risingPanelTitle(): string {
    return this.trendTopics.length ? 'Van en aumento' : 'Más llegados';
  }

  get risingPanelCount(): number {
    return this.trendTopics.length || this.risingPanelTopics.length;
  }

  get recentTopics(): TopicRow[] {
    return this.allRecentTopics.slice(0, 12);
  }

  get allRecentTopics(): TopicRow[] {
    return (this.recentTopicsSnapshot || this.mapTopics(this.summary?.recent_topics || []))
      .filter((topic) => topic.recent > 0)
      .sort((a, b) => b.recent - a.recent || b.value - a.value || a.label.localeCompare(b.label));
  }

  get recentTopicsCount(): number {
    return Number(this.summary?.recent_topics_count) || this.allRecentTopics.length;
  }

  get recentTopicsWindowTotal(): number {
    return this.allRecentTopics.reduce((sum, topic) => sum + topic.recent, 0);
  }

  get resolvedTopics(): TopicRow[] {
    return this.allResolvedTopics.slice(0, 12);
  }

  get topResolvedTopics(): TopicRow[] {
    return this.allResolvedTopics.slice(0, 3);
  }

  summaryResolvedTopics(row: SummaryRow): TopicRow[] {
    return this.mapTopics(row.resolved_topics || [])
      .filter((topic) => topic.recent > 0)
      .sort((a, b) => b.recent - a.recent || b.value - a.value || a.label.localeCompare(b.label))
      .slice(0, 3);
  }

  summaryReceivedTopics(row: SummaryRow): TopicRow[] {
    return this.mapTopics(row.top_topics || [])
      .filter((topic) => topic.value > 0)
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
      .slice(0, 3);
  }

  get resolvedRecentTotal(): number {
    return this.allResolvedTopics.reduce((sum, topic) => sum + topic.recent, 0);
  }

  get resolvedPreviousTotal(): number {
    return this.allResolvedTopics.reduce((sum, topic) => sum + topic.previous, 0);
  }

  get resolvedDelta(): number {
    return this.resolvedRecentTotal - this.resolvedPreviousTotal;
  }

  get resolvedDeltaPct(): number {
    if (this.resolvedPreviousTotal > 0) {
      return (this.resolvedDelta / this.resolvedPreviousTotal) * 100;
    }
    return this.resolvedRecentTotal > 0 ? 100 : 0;
  }

  get resolvedTrendDirection(): 'up' | 'down' | 'flat' {
    if (this.resolvedDelta > 0) return 'up';
    if (this.resolvedDelta < 0) return 'down';
    return 'flat';
  }

  get resolvedTrendText(): string {
    const prefix = this.resolvedDelta > 0 ? '+' : '';
    return `${prefix}${this.formatNumber(this.resolvedDelta)} vs período anterior`;
  }

  get allResolvedTopics(): TopicRow[] {
    return (this.resolvedTopicsSnapshot || this.mapTopics(this.summary?.resolved_topics || []))
      .filter((topic) => topic.recent > 0)
      .sort((a, b) => b.recent - a.recent || b.value - a.value || a.label.localeCompare(b.label));
  }

  get resolvedTopicsCount(): number {
    return Number(this.summary?.resolved_topics_count) || this.allResolvedTopics.length;
  }

  get resolvedTopicsSubtitle(): string {
    if (this.topicWindowDays === 0) return 'Temas con más tickets resueltos en el estado actual';
    return this.topicWindowDays === 1
      ? 'Temas con más tickets resueltos hoy'
      : `Temas con más tickets resueltos en los últimos ${this.topicWindowDays} días`;
  }

  get recentTopicsLabel(): string {
    if (this.topicWindowDays === 0) return 'Más llegados · estado actual';
    return this.topicWindowDays === 1 ? 'Llegaron hoy' : `Más llegados · últimos ${this.topicWindowDays} días`;
  }

  get recentTopicsTitle(): string {
    return this.topicWindowDays === 1 ? 'Resumen de actividad reciente' : 'Temas más recibidos';
  }

  get recentTopicsSubtitle(): string {
    if (this.topicWindowDays === 0) return 'Temas de mayor tráfico en el estado actual';
    return this.topicWindowDays === 1
      ? 'Temas de mayor tráfico de hoy'
      : `Temas de mayor tráfico en los últimos ${this.topicWindowDays} días`;
  }

  recentTopicShare(topic: TopicRow): number {
    return (topic.recent / Math.max(1, this.recentTopicsWindowTotal)) * 100;
  }

  resolvedTopicShare(topic: TopicRow): number {
    return (topic.recent / Math.max(1, this.resolvedRecentTotal)) * 100;
  }

  isTrendTopic(topic: TopicRow): boolean {
    return topic.direction === 'up' && topic.change > 0 && topic.recent >= 4;
  }

  risingTopicIcon(topic: TopicRow): string {
    return this.isTrendTopic(topic) ? '↗' : '•';
  }

  risingTopicDescription(topic: TopicRow): string {
    if (this.isTrendTopic(topic)) {
      return `${this.formatNumber(topic.recent)} en ${this.topicWindowLabel} · +${this.formatNumber(topic.change)} vs período anterior`;
    }
    return `${this.formatNumber(topic.recent)} en ${this.topicWindowLabel} · ${this.formatPercent(this.recentTopicShare(topic))} del período`;
  }

  risingTopicDelta(topic: TopicRow): string {
    if (this.isTrendTopic(topic)) {
      return `+${this.formatNumber(topic.change)}`;
    }
    return this.formatNumber(topic.recent);
  }

  topicTrendIcon(topic: TopicRow): string {
    if (topic.direction === 'up') return '↗';
    if (topic.direction === 'down') return '↘';
    return '→';
  }

  topicTrendLabel(topic: TopicRow): string {
    if (topic.direction === 'up') return `+${this.formatNumber(topic.change)} · +${topic.change_pct}%`;
    if (topic.direction === 'down') return `${this.formatNumber(topic.change)} · ${topic.change_pct}%`;
    return `0 · ${topic.change_pct}%`;
  }

  riseDeltaClass(topic: TopicRow): string {
    if (topic.change >= 10) return 'high';
    if (topic.change >= 5) return 'medium';
    return 'low';
  }

  applyTopic(topic: TopicRow): void {
    if (this.normalize(this.topicFilter) === this.normalize(topic.label) && !this.topicScope) {
      this.clearTopicFilter();
      return;
    }
    if (!this.topicFilter) {
      this.topicsSnapshot = [...this.summaryTopics];
      this.recentTopicsSnapshot = this.mapTopics(this.summary?.recent_topics || []);
      this.resolvedTopicsSnapshot = this.mapTopics(this.summary?.resolved_topics || []);
    }
    this.topicFilter = topic.label;
    this.topicScope = '';
    this.page = 0;
    this.requestRefresh();
  }

  applyResolvedTopic(topic: TopicRow): void {
    if (this.normalize(this.topicFilter) === this.normalize(topic.label) && this.topicScope === 'resolved') {
      this.clearTopicFilter();
      return;
    }
    if (!this.topicFilter) {
      this.topicsSnapshot = [...this.summaryTopics];
      this.recentTopicsSnapshot = this.mapTopics(this.summary?.recent_topics || []);
      this.resolvedTopicsSnapshot = this.mapTopics(this.summary?.resolved_topics || []);
    }
    this.topicFilter = topic.label;
    this.topicScope = 'resolved';
    this.risk = '';
    this.page = 0;
    this.requestRefresh();
  }

  isTopicActive(topic: TopicRow): boolean {
    return this.normalize(this.topicFilter) === this.normalize(topic.label);
  }

  clearTopicFilter(): void {
    this.topicFilter = '';
    this.topicScope = '';
    this.topicsSnapshot = null;
    this.recentTopicsSnapshot = null;
    this.resolvedTopicsSnapshot = null;
    this.page = 0;
    this.requestRefresh();
  }

  setTopicWindow(days: TopicWindow): void {
    if (this.topicsLoading) return;
    if (this.topicWindowDays === days && (days !== 1 || this.isTodayTicketWindowActive())) return;
    this.topicWindowDays = days;
    if (days === 1) {
      const bounds = this.todayBounds();
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
    } else {
      this.fromDate = '';
      this.toDate = '';
    }
    this.dateMode = 'created';
    this.trendPanStart = null;
    this.clearTrendHover();
    this.clearWeeklyComparisonHover();
    this.topicsSnapshot = null;
    this.recentTopicsSnapshot = null;
    this.resolvedTopicsSnapshot = null;
    this.page = 0;
    if (this.activeView === 'operations') {
      const satisfactionWindow = this.satisfactionWindowForTopicDays(days);
      if (this.selectedSatisfactionWindow !== satisfactionWindow) {
        this.setSatisfactionWindow(satisfactionWindow);
      }
    }
    this.refreshSelectedOperationsDetail();
    this.requestRefresh();
  }

  setOperationsDetailPeriod(window: OperationsDetailPeriodWindow): void {
    if (this.topicsLoading || this.isOperationsDetailPeriodActive(window)) return;
    this.topicsSnapshot = null;
    this.recentTopicsSnapshot = null;
    this.resolvedTopicsSnapshot = null;
    this.dateMode = 'created';
    if (window === 'previous-month') {
      const bounds = this.previousOperationsMonthBounds();
      if (!bounds) return;
      this.topicWindowDays = 30;
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
    } else if (window === '24h') {
      const bounds = this.todayBounds();
      this.fromDate = bounds.from;
      this.toDate = bounds.to;
      this.topicWindowDays = 1;
    } else {
      this.fromDate = '';
      this.toDate = '';
      this.topicWindowDays = this.topicDaysForOperationsDetailWindow(window);
    }
    this.trendPanStart = null;
    this.clearTrendHover();
    this.clearWeeklyComparisonHover();
    const satisfactionWindow = window === 'previous-month' ? 'previous-month' : this.satisfactionWindowForTopicDays(this.topicWindowDays);
    if (this.activeView === 'operations' && this.selectedSatisfactionWindow !== satisfactionWindow) {
      this.setSatisfactionWindow(satisfactionWindow);
    }
    this.page = 0;
    this.refreshSelectedOperationsDetail();
    this.requestRefresh();
  }

  isOperationsDetailPeriodActive(window: OperationsDetailPeriodWindow): boolean {
    if (window === '24h') {
      return this.topicWindowDays === 1 && this.isTodayTicketWindowActive();
    }
    if (window === 'previous-month') {
      const bounds = this.previousOperationsMonthBounds();
      return !!bounds && this.fromDate === bounds.from && this.toDate === bounds.to && this.dateMode === 'created';
    }
    return !this.fromDate
      && !this.toDate
      && this.dateMode === 'created'
      && this.topicWindowDays === this.topicDaysForOperationsDetailWindow(window);
  }

  private topicDaysForOperationsDetailWindow(window: OperationsDetailPeriodWindow): TopicWindow {
    if (window === '24h') return 1;
    if (window === '3d') return 3;
    if (window === '7d') return 7;
    if (window === '14d') return 14;
    if (window === 'all') return 0;
    return 30;
  }

  private previousOperationsMonthBounds(): { from: string; to: string } | null {
    const reference = this.filtersMeta?.created_range?.to || this.health?.created_range?.to || new Date().toISOString().slice(0, 10);
    const date = new Date(`${reference}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    const previousStart = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    const previousEnd = new Date(date.getFullYear(), date.getMonth(), 0);
    return { from: this.localDateKey(previousStart), to: this.localDateKey(previousEnd) };
  }

  private localDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private satisfactionWindowForTopicDays(days: TopicWindow): SatisfactionPeriodWindow {
    if (days === 0) return 'all';
    if (days === 1) return '24h';
    if (days === 3) return '3d';
    if (days === 7) return '7d';
    if (days === 14) return '14d';
    return 'last-month';
  }

  private async loadTopicsOnly(): Promise<void> {
    const requestId = ++this.topicsRequestId;
    this.topicsLoading = true;
    this.cdr.detectChanges();
    try {
      const data = await this.fetchJson(`${this.apiBase}/api/topics?${this.buildQuery()}`);
      if (requestId !== this.topicsRequestId) return;
      this.summary = {
        ...(this.summary || {}),
        top_topics: data.top_topics || [],
        recent_topics: data.recent_topics || [],
        resolved_topics: data.resolved_topics || [],
        recent_topics_count: Number(data.recent_topics_count) || 0,
        resolved_topics_count: Number(data.resolved_topics_count) || 0,
      };
    } catch {
      if (requestId !== this.topicsRequestId) return;
      await this.loadData();
    } finally {
      if (requestId === this.topicsRequestId) {
        this.topicsLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  get kpiCards(): Array<{ key: 'total' | 'unresolved' | 'unassigned' | 'sla_within' | 'sla_violated' | 'risk_high' | 'avg_first_response'; title: string; value: string; note: string; clickable: boolean }> {
    const summary = this.summary || {};
    const violated = summary?.sla?.violated || 0;
    const within = summary?.sla?.within || 0;
    return [
      {
        key: 'total',
        title: 'Vista actual',
        value: this.formatNumber(summary.total || 0),
        note: 'Tickets del filtro activo',
        clickable: false,
      },
      {
        key: 'unresolved',
        title: 'Sin resolver',
        value: this.formatNumber(summary.unresolved || 0),
        note: `${this.formatNumber(summary.open || 0)} abiertos / ${this.formatNumber(summary.pending || 0)} pendientes`,
        clickable: true,
      },
      {
        key: 'unassigned',
        title: 'Sin asignar',
        value: this.formatNumber(this.unassignedTicketsCount),
        note: 'Abiertos, nuevos o pendientes',
        clickable: true,
      },
      {
        key: 'risk_high',
        title: 'Riesgoso',
        value: this.formatNumber(summary?.risk?.high || 0),
        note: 'Más de 7 días sin actualización',
        clickable: true,
      },
      {
        key: 'sla_within',
        title: 'Dentro de SLA',
        value: this.formatPercent(summary?.sla?.within_pct),
        note: `${this.formatNumber(within)} tickets`,
        clickable: true,
      },
      {
        key: 'sla_violated',
        title: 'Fuera de SLA',
        value: this.formatPercent((violated / Math.max(within + violated, 1)) * 100),
        note: `${this.formatNumber(violated)} tickets`,
        clickable: true,
      },
      {
        key: 'avg_first_response',
        title: '1ra resp. prom.',
        value: this.formatHours(this.avgFirstResponseHours),
        note: 'Promedio en horas',
        clickable: false,
      },
    ];
  }

  get unassignedTicketsCount(): number {
    const backendCount = Number(this.summary?.unassigned);
    if (Number.isFinite(backendCount) && backendCount > 0) {
      return backendCount;
    }
    const detail = ((this.summary?.by_group_detail || []) as SummaryRow[])
      .find((row) => this.normalize(row.label) === 'no agent' || this.normalize(row.label) === 'sin agente' || this.normalize(row.label) === 'unassigned');
    if (detail) {
      return Number(detail.unresolved) || 0;
    }
    const byArea = this.summary?.by_group || {};
    const match = Object.entries(byArea).find(([label]) => this.normalize(label) === 'no agent' || this.normalize(label) === 'sin agente' || this.normalize(label) === 'unassigned');
    return match ? Number(match[1]) || 0 : 0;
  }

  get ticketsPage(): TicketRow[] {
    return this.tickets?.items || [];
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.tickets?.total || 0) / this.pageSize));
  }

  get ticketPageNumbers(): number[] {
    const total = this.totalPages;
    const windowSize = 7;
    if (total <= windowSize) {
      return Array.from({ length: total }, (_, index) => index);
    }
    const half = Math.floor(windowSize / 2);
    const start = Math.max(0, Math.min(this.page - half, total - windowSize));
    return Array.from({ length: windowSize }, (_, index) => start + index);
  }

  get avgFirstResponseHours(): number | null {
    const summaryValue = this.summary?.avg_first_response_hours;
    if (summaryValue !== null && summaryValue !== undefined && summaryValue !== '') {
      const numeric = Number(summaryValue);
      if (!Number.isNaN(numeric)) return numeric;
    }
    const fallback = this.ticketsPage
      .map((ticket) => this.parseHoursValue((ticket as any)['Primer tiempo de respuesta (en horas)']))
      .filter((value): value is number => Number.isFinite(value));
    if (!fallback.length) return null;
    return Math.round((fallback.reduce((sum, value) => sum + value, 0) / fallback.length) * 100) / 100;
  }

  private parseHoursValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const text = String(value).trim();
    if (!text || text === '(vacío)') return null;
    const duration = text.match(/^(\d{1,3}):([0-5]\d):([0-5]\d)$/);
    if (duration) {
      const hours = Number(duration[1]);
      const minutes = Number(duration[2]);
      const seconds = Number(duration[3]);
      return Math.round((hours + (minutes / 60) + (seconds / 3600)) * 100) / 100;
    }
    const normalized = Number(text.replace(',', '.'));
    return Number.isFinite(normalized) ? normalized : null;
  }

  get ticketScope(): string {
    const bits: string[] = [];
    if (this.search) bits.push(`búsqueda: ${this.search}`);
    if (this.topicFilter) bits.push(`tema: ${this.topicFilter} (${this.topicWindowLabel})`);
    if (this.fromDate || this.toDate) bits.push(`rango: ${this.fromDate || 'inicio'} → ${this.toDate || 'fin'}`);
    if (this.risk) bits.push(`riesgo: ${this.displayValue('risk', this.risk)}`);
    if (this.interactions.length) bits.push(`interacciones: ${this.interactions.map((value) => this.displayValue('interactions', value)).join(', ')}`);
    return bits.join(' · ');
  }

  trackBySatisfactionItem(_: number, item: any): any {
    return item?.id_ticket || item?.period || _;
  }

  trackByTicket(_: number, ticket: TicketRow): string {
    return ticket['ID del ticket'] || '';
  }

  trackByScheduleCalendarMonth(_: number, month: any): string {
    return month?.key || String(_);
  }

  trackByScheduleCalendarArea(_: number, area: any): string {
    return area?.key || area?.label || String(_);
  }

  trackByScheduleCalendarGroup(_: number, group: any): string {
    return group?.title || String(_);
  }

  trackByScheduleCalendarEvent(_: number, event: any): string {
    return this.scheduleCalendarEventKey(event) || String(_);
  }

  trackByScheduleCalendarWeek(index: number, week: any[]): string {
    return week?.map((day: any) => day?.date || 'blank').join('|') || String(index);
  }

  trackByScheduleCalendarDay(index: number, day: any): string {
    return day?.date || `blank-${index}`;
  }

  freshdeskTicketUrl(ticketId: unknown): string {
    return `https://rrhhclaropersonas.freshdesk.com/a/tickets/${encodeURIComponent(String(ticketId || '').trim())}`;
  }

  sortTickets(key: TicketSortKey): void {
    if (this.ticketSortBy === key) {
      this.ticketSortDir = this.ticketSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.ticketSortBy = key;
      this.ticketSortDir = 'desc';
    }
    this.page = 0;
    void this.loadTicketsPage();
  }

  sortIcon(key: TicketSortKey): string {
    if (this.ticketSortBy !== key) return '↕';
    return this.ticketSortDir === 'asc' ? '↑' : '↓';
  }

  riskClass(ticket: TicketRow): string {
    const value = this.normalize(ticket['Sin interacción'] || '');
    if (value.includes('bajo') || value.includes('low')) return 'low';
    if (value.includes('medio') || value.includes('medium')) return 'medium';
    if (value.includes('riesgoso') || value.includes('alto') || value.includes('high')) return 'high';
    return 'none';
  }

  staleDaysLabel(ticket: TicketRow): string {
    const value = ticket['Sin interacción'] || '';
    const match = value.match(/\d+/);
    return match ? match[0] : '—';
  }

  createdAgeClass(ticket: TicketRow): string {
    const days = this.createdAgeDays(ticket);
    if (days === null) return 'none';
    if (days <= 2) return 'low';
    if (days <= 7) return 'medium';
    return 'high';
  }

  createdAgeLabel(ticket: TicketRow): string {
    const days = this.createdAgeDays(ticket);
    if (days === null) return '—';
    return String(days);
  }

  private createdAgeDays(ticket: TicketRow): number | null {
    const raw = ticket['Tiempo de creación'];
    if (!raw) return null;
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const createdAt = new Date(normalized);
    if (Number.isNaN(createdAt.getTime())) return null;
    const diffDays = Math.floor((Date.now() - createdAt.getTime()) / 86400000);
    return Math.max(0, diffDays);
  }

  slaClass(ticket: TicketRow): string {
    const value = this.normalize(ticket['Estado de la primera respuesta'] || '');
    if (value.includes('dentro') || value.includes('within')) return 'within';
    if (value.includes('fuera') || value.includes('violated') || value.includes('outside')) return 'outside';
    return 'none';
  }

  prevPage(): void {
    this.goToPage(this.page - 1);
  }

  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  goToPage(page: number): void {
    const nextPage = Math.max(0, Math.min(page, this.totalPages - 1));
    if (nextPage === this.page) return;
    this.page = nextPage;
    void this.loadTicketsPage();
  }

  private async loadTicketsPage(forceRefresh = false): Promise<void> {
    try {
      const params = new URLSearchParams(this.buildTicketsQuery());
      if (forceRefresh) {
        params.set('_ts', String(Date.now()));
      }
      this.tickets = await this.fetchJson(`${this.apiBase}/api/tickets?${params.toString()}`);
      this.cdr.detectChanges();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error cargando tickets';
      this.subtitle = message;
      this.cdr.detectChanges();
    }
  }

  async exportCsv(): Promise<void> {
    const data = await this.fetchJson(`${this.apiBase}/api/tickets?limit=100000&offset=0&${this.buildQuery()}`);
    const items: TicketRow[] = data.items || [];
    const columns: Array<{ header: string; value: (item: TicketRow) => unknown }> = [
      { header: 'ID', value: (item) => item['ID del ticket'] },
      { header: 'Estado', value: (item) => this.ticketValue('estado', item.Estado) },
      { header: 'Prioridad', value: (item) => this.ticketValue('prioridad', item.Prioridad) },
      { header: 'Tipo', value: (item) => this.ticketValue('tipo', item.Tipo) },
      { header: 'Agente', value: (item) => item.Agente },
      { header: 'Grupo', value: (item) => item.Grupo },
      { header: 'Interacciones', value: (item) => this.formatNumber(item.Interacciones || 0) },
      { header: 'Días sin interacción', value: (item) => this.staleDaysLabel(item) },
      { header: 'Días creado', value: (item) => this.createdAgeLabel(item) },
      { header: 'SLA 1ra resp.', value: (item) => this.ticketValue('sla', item['Estado de la primera respuesta']) },
      { header: 'Asunto', value: (item) => item.Asunto },
    ];
    const csvEscape = (value: unknown) => {
      const text = String(value ?? '');
      if (/[;"\r\n]/.test(text)) return `"${text.replaceAll('"', '""')}"`;
      return text;
    };
    const rows = [columns.map((column) => csvEscape(column.header)).join(';')];
    items.forEach((item) => rows.push(columns.map((column) => csvEscape(column.value(item))).join(';')));
    const blob = new Blob([`\ufeff${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BI_Control_CS_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
}
